// ELC Shift — Agents console (the "Agent Control" dashboard).
//
// Bundled by build.js to dist/agents.js (globalName `Agents`). Loaded by
// the standalone dev.html console — NOT by admin.html. This is Matt's
// private control surface, deliberately separate from the managers' app
// so the admin monolith doesn't keep growing.
//
// Public surface (called from dev.html):
//   Agents.render({ workerUrl, token, mountId })  — fetch live data + draw
//   Agents.version()                              — build marker (for tests)
//
// Live data comes from the admin-only Worker endpoints that already exist:
//   GET /api/admin/triage-results    — bug-triage agent activity
//   GET /api/admin/feedback-insights — feedback themes
// Everything else is a registry entry (live feature / planned) until it
// grows its own telemetry.

// v136.77: every "are you sure?" here is Shift's own box, never the browser's confirm(). The browser pane inside
// the Claude app answers a native confirm() with Cancel by itself, so Approve on the pay-record repair card did
// nothing there (29 Oct 2026). The box is bundled in; dev.html maps the colours it uses onto the console's own.
import { uiConfirm } from '../shared/confirm-dialog.js';

// ── tiny helpers ────────────────────────────────────────────
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// RAG → css modifier. 'g' green, 'a' amber, 'r' red, 'x' inert/unknown.
function ragMod(rag) {
  return rag === 'g' ? 'g' : rag === 'r' ? 'r' : rag === 'a' ? 'a' : 'x';
}
// v134.4: a status dot also says its state in words, for screen readers and on hover (never colour alone).
export function ragWord(rag) {
  return rag === 'g' ? 'healthy' : rag === 'r' ? 'problem' : rag === 'a' ? 'watch' : 'not checked';
}
function ragDotAttrs(rag) { return ' role="img" aria-label="' + ragWord(rag) + '" title="' + ragWord(rag) + '"'; }

// RAG → concrete colour (for SVG strokes/fills, which can't read CSS vars
// reliably across the diagram). Matches the dev.html JARVIS palette.
export function ragColor(rag) {
  return rag === 'g' ? '#2ee6a6'
    : rag === 'r' ? '#ff5470'
    : rag === 'a' ? '#f5b13d'
    : '#7e92bd';
}

// ── JACK (the console's voice) — module state ───────────────
// JACK greets Matt and reads the headline aloud via the browser's built-in
// Web Speech API. No backend, no API key, no cost. Guarded so it never
// breaks the bundle/tests in a non-DOM environment.
let _jackHeadline = '';        // last computed spoken line (for the replay button)
let _jackGreeted = false;      // boot greeting started (once per page load)
let _jackHeadlineSpoken = false; // headline read aloud (once per page load)
let _jackBootEnded = false;    // boot utterance finished (or never started)

function _jackMuted() {
  try { return localStorage.getItem('devJackMuted') === '1'; } catch (e) { return false; }
}
// Pick the most JARVIS-ish voice available (prefer a British male), else default.
function _pickVoice(synth) {
  let voices = [];
  try { voices = synth.getVoices() || []; } catch (e) { return null; }
  if (!voices.length) return null;
  const score = (v) => {
    const n = ((v.name || '') + ' ' + (v.lang || '')).toLowerCase();
    let s = 0;
    if (/en-gb|english \(united kingdom\)|uk english/.test(n)) s += 3;
    if (/\bmale\b|daniel|george|arthur|ryan|james/.test(n)) s += 2;
    if (/google|natural|premium|enhanced/.test(n)) s += 1;
    if (/en[-_]?us|english/.test(n)) s += 1;
    return s;
  };
  return voices.slice().sort((a, b) => score(b) - score(a))[0] || voices[0];
}
// Keep utterance references alive while they speak — Chrome garbage-collects
// them mid-sentence otherwise, a classic "speech goes silent" cause.
let _jackUtterQueue = [];
// Cinematic voice (ElevenLabs via the Worker TTS proxy). _jackCtx carries the
// worker URL + admin token (set in render); _ttsOk caches availability so we
// don't re-try a missing key every line; _jackAudio is the playing clip.
let _jackCtx = null;
let _lastRenderOpts = null; // last render() opts, so in-card actions can refresh
// v129.142 — the last compliance payload, so the Nudge action works on the
// SAME rows the card is showing rather than re-fetching a different snapshot.
let _lastCompliance = null;
let _lastCommsSignals = null;   // v129.143 — the Check-in action reads these
// v129.59 — one-screen cockpit state: RAG map from the previous render (change
// narration), alive-loop timers, and where a drilled panel goes home to.
let _lastRagMap = null;
let _lastChangeText = '';
let _aliveTimer = null, _tickTimer = null, _lastCheckedMs = 0;
let _drilledEl = null;
let _ttsOk = null;   // null = untried, true = cinematic works, false = fall back
let _jackAudio = null;
let _audioCtx = null; // Web Audio context for the synthesized boot chime
let _jackConvo = [];  // conversational history [{role:'user'|'assistant', content}]
let _jackState = {};  // compact live console state, refreshed each render
let _jackThinking = false;
// v135.2: the chat's own fixed replies ("not connected", "couldn't reach my brain"). These are labels, so the admin
// app's Arabic may swap them; every other bubble is a real question or answer and is drawn translate="no".
const _jackFixed = new WeakSet();

// Create/resume the Web Audio context (must happen inside a user gesture, so
// jackPrime calls this on the login click). Returns null when unavailable.
function _ensureAudioCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
  } catch (e) { return null; }
  return _audioCtx;
}

// JACK's startup sound — an ORIGINAL ~1.5s "power-up" swell synthesised live
// (rising detuned oscillators through a sweeping low-pass + gain envelope).
// No audio file, no copyrighted material. Respects the mute toggle.
function jackBootChime() {
  if (_jackMuted()) return;
  const ctx = _ensureAudioCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.connect(ctx.destination);
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.22, now + 0.5); // swell in
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.5); // fade out
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(220, now);
    filter.frequency.exponentialRampToValueAtTime(3200, now + 1.0); // open up
    filter.connect(master);
    [110, 165, 220].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = i === 0 ? 'sawtooth' : 'sine';
      o.detune.value = (i - 1) * 7; // slight detune = a richer "powering up"
      o.frequency.setValueAtTime(f, now);
      o.frequency.exponentialRampToValueAtTime(f * 4, now + 0.9); // rise
      o.connect(filter);
      o.start(now);
      o.stop(now + 1.5);
    });
  } catch (e) { /* the chime is a nicety — never let it throw */ }
}

// Dispatch one utterance (voices already loaded). Handles the Chrome
// "starts paused" quirk via resume(), and chains onend/onerror so the
// boot → headline flow continues even if a line is cut off.
function _dispatch(synth, text, onend) {
  const u = new window.SpeechSynthesisUtterance(text);
  const v = _pickVoice(synth);
  if (v) u.voice = v;
  u.rate = 1.08; u.pitch = 0.9; // brisk but low-pitched = a calm, efficient assistant
  let cb = onend;
  const done = () => {
    _jackUtterQueue = _jackUtterQueue.filter(x => x !== u);
    if (cb) { const f = cb; cb = null; f(); }
  };
  u.onend = done;
  u.onerror = done; // not-allowed / interrupted still continues the chain
  _jackUtterQueue.push(u); // anti-GC reference
  try { synth.speak(u); } catch (e) { done(); return; }
  try { synth.resume(); } catch (e) {} // Chrome sometimes starts paused
}

// Low-level: speak one line. Waits for voices to load on the first call
// (Chrome populates getVoices() asynchronously, and speaking before they're
// ready silently no-ops). `onend` fires when the line finishes/errors.
// Guarded for non-DOM (tests/bundle).
function _speak(text, onend) {
  if (!text) return false;
  if (typeof window === 'undefined' || !window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return false;
  const synth = window.speechSynthesis;
  try {
    const haveVoices = (synth.getVoices() || []).length > 0;
    if (haveVoices) {
      _dispatch(synth, text, onend);
    } else {
      // Speak once voices load; a timeout fallback speaks anyway (default
      // voice) if the event never fires.
      let fired = false;
      const go = () => { if (fired) return; fired = true; _dispatch(synth, text, onend); };
      try { synth.addEventListener('voiceschanged', go, { once: true }); }
      catch (e) { try { synth.onvoiceschanged = go; } catch (e2) {} }
      setTimeout(go, 400);
    }
    return true;
  } catch (e) { return false; }
}
// Stop whatever JACK is currently saying (browser utterance OR cinematic clip).
function jackCancel() {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    try { window.speechSynthesis.cancel(); } catch (e) {}
  }
  if (_jackAudio) { try { _jackAudio.pause(); } catch (e) {} _jackAudio = null; }
}

// Cinematic voice via the Worker TTS proxy (ElevenLabs). The API key stays on
// the Worker — the browser only ever receives audio. Falls back to the browser
// voice on any failure / when no key is configured. ALWAYS calls cb exactly
// once so the boot → headline chain never stalls.
function ttsSpeak(text, cb) {
  const fall = () => { if (!_speak(text, cb)) cb(); };
  if (!_jackCtx || typeof fetch === 'undefined' || typeof Audio === 'undefined') { fall(); return; }
  fetch(_jackCtx.workerUrl + '/api/admin/tts', { cache: 'no-store',
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: String(text).slice(0, 600) }),
  }).then((res) => {
    const ct = (res.headers && res.headers.get('content-type')) || '';
    if (!res.ok || !/audio/.test(ct)) { _ttsOk = false; throw new Error('no-audio'); }
    _ttsOk = true;
    return res.blob();
  }).then((blob) => {
    jackCancel();
    const url = URL.createObjectURL(blob);
    const a = new Audio(url);
    _jackAudio = a;
    let ended = false;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch (e) {} if (_jackAudio === a) _jackAudio = null; };
    a.onended = () => { if (ended) return; ended = true; cleanup(); cb(); };
    a.onerror = () => { if (ended) return; ended = true; cleanup(); fall(); };
    const p = a.play();
    if (p && p.catch) p.catch(() => { if (ended) return; ended = true; cleanup(); fall(); });
  }).catch(() => { fall(); });
}

// Speak a line — cinematic if the key's configured, else the browser voice.
// v129.88 (barge-in): while he speaks in hands-free mode, a level monitor
// listens for YOU — interrupting cuts the TTS and opens the mic. The monitor
// always stops when the line ends normally (the cb wrapper), so it never
// outlives the speech it was guarding.
function jackSay(text, onend) {
  const cb0 = onend || function () {};
  let done = false;
  const cb = () => { if (done) return; done = true; _stopBargeMonitor(); cb0(); };
  if (!text) { cb(); return; }
  if (_jackHandsFree) _startBargeMonitor();
  if (_ttsOk !== false && _jackCtx) ttsSpeak(text, cb);
  else if (!_speak(text, cb)) cb();
}

// Replay / one-off: cancel anything in flight, then speak (cinematic-aware).
function speakJack(text) { jackCancel(); jackSay(text); }

// v136.66: the 3D System Map's voice (Matt, 27 Oct 2026: "can it have the jarvis voice over it"). Jack's own voice
// (the cached /api/admin/tts, else the browser's), cutting off whatever he was saying. The console's mute wins:
// muted, nothing is said and false comes back, so the map paces itself without him. onend fires when he finishes.
export function mapSpeak(text, onend) {
  if (!text || _jackMuted()) return false;
  jackCancel();
  jackSay(String(text), onend);
  return true;
}
export function mapHush() { jackCancel(); }

// ── JACK Phase 1b — the SPOKEN LOGIN BRIEFING ────────────────────────────
// (docs/jack-jarvis-plan.md gap #2: summaries were pull, not push.)
// After render() has the console data, we fetch the one-call aggregate
// (/api/admin/console-state) and ask JACK for a single ≤90-word narration —
// ONE model call, no tools (the worker hard-enforces briefing:true →
// noTools). The narration lands in the chat as JACK's first message and,
// if the deterministic headline hasn't been spoken yet, replaces it as the
// spoken line. Any failure = today's behaviour (the deterministic headline
// stands) — never stall, never fake.
let _briefingTried = false;
// Pure: the narration request. Exported for tests.
export function buildBriefingPrompt(state) {
  return 'LOGIN BRIEFING — Matt just opened the console. Narrate the situation in ONE short spoken ' +
    'paragraph, UNDER 90 WORDS: greet him briefly, lead with anything red, failing or trending the ' +
    'wrong way, then the backlog numbers that matter, and if everything is healthy end with a clear ' +
    '"otherwise all quiet". Plain English, no lists, no markdown — it is read aloud. Do not invent ' +
    'numbers; use ONLY this data. IMPORTANT: if db_ok is true, the database is CONNECTED — zeros and ' +
    'empty lists just mean a quiet day (say "quiet", never "not connected" / "no data" / "disconnected"). ' +
    'Only call the database unreachable if db_ok is false.\nDATA:\n' + JSON.stringify(state || {});
}
// ── Phase 2: THE BRIEFING ROOM ───────────────────────────────────────────
// docs/jack-jarvis-plan.md gap #1: "can't see how the agents interact."
// A chat-style feed at the top of the console where every agent REPORTS IN
// with its real, already-computed status, anchored by JACK's synthesis — so
// the team-and-JARVIS collaboration is VISIBLE, not just a grid of cards.
// Pure + exported: every line is a real status handed in by render() (the
// same signals the cards show), never invented. `jackLine` is filled async by
// narrateLoginBriefing once JACK has spoken (id="jackRoomLine").
export function buildBriefingRoom(agents, opts) {
  opts = opts || {};
  const list = Array.isArray(agents) ? agents : [];
  const rows = list.map(a =>
    '<div class="br-row">' +
      '<span class="a-dot ' + ragMod(a.rag) + (a.rag === 'r' ? ' a-pulse' : '') + '"' + ragDotAttrs(a.rag) + '></span>' +
      '<span class="br-agent">' + esc(a.icon || '') + ' ' + esc(a.name) + '</span>' +
      '<span class="br-say">' + esc(a.line || '') + '</span>' +
    '</div>'
  ).join('');
  const jackLine = opts.jackLine || 'Reviewing the overnight reports…';
  const flagged = list.filter(a => a.rag === 'r').length;
  const sub = flagged ? (flagged + ' flagged for you') : 'all reporting in';
  return '<div class="br-wrap">' +
    '<div class="br-head"><span class="br-live"></span> The Briefing Room ' +
      '<span class="br-sub">' + list.length + ' agents · ' + sub + '</span></div>' +
    '<div class="br-jack"><span class="br-jack-i">🤖 JACK</span>' +
      '<span id="jackRoomLine">' + esc(jackLine) + '</span></div>' +
    '<div class="br-feed">' + rows + '</div>' +
  '</div>';
}

// Daily cap: briefings are pennies, but bound them anyway (per Dubai day).
function _briefingCapKey() {
  return 'devJackBrief_' + new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10);
}
async function narrateLoginBriefing() {
  if (_briefingTried || _jackMuted() || !_jackCtx) return;
  _briefingTried = true; // once per load, whatever happens next
  let count = 0;
  try { count = parseInt(localStorage.getItem(_briefingCapKey()) || '0') || 0; } catch (e) {}
  if (count >= 8) return; // daily cap
  try {
    const auth = { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' };
    const state = await fetch(_jackCtx.workerUrl + '/api/admin/console-state', { cache: 'no-store', headers: auth })
      .then(r => r.json());
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack', { cache: 'no-store',
      method: 'POST', headers: auth,
      body: JSON.stringify({
        briefing: true,
        messages: [{ role: 'user', content: buildBriefingPrompt(state) }],
        context: _jackState,
      }),
    });
    const d = await res.json().catch(() => ({}));
    if (!(d && d.ok && d.text)) return; // deterministic headline stands
    try { localStorage.setItem(_briefingCapKey(), String(count + 1)); } catch (e) {}
    // The briefing is JACK's first chat message either way…
    _jackConvo.push({ role: 'assistant', content: d.text });
    _renderJackChat();
    // …and it anchors the Briefing Room — JACK's response to the agents' check-ins.
    if (typeof document !== 'undefined') {
      const roomLine = document.getElementById('jackRoomLine');
      if (roomLine) roomLine.textContent = d.text;
    }
    // …and the spoken line too, if the deterministic one hasn't gone out yet.
    if (!_jackHeadlineSpoken) {
      _jackHeadline = d.text;
      if (_jackBootEnded) _maybeSpeakHeadline();
    }
  } catch (e) { /* graceful: the deterministic headline stands */ }
}
// Read the headline ONCE per load — after the boot line has finished talking.
function _maybeSpeakHeadline() {
  if (_jackHeadlineSpoken || _jackMuted() || !_jackHeadline) return;
  _jackHeadlineSpoken = true;
  speakJack(_jackHeadline);
}
// Warm up the speech engine INSIDE a user gesture (called from dev.html's
// login click). Chrome is far more reliable about speaking later once it's
// been touched during a gesture. Speaks a silent primer + kicks off voice
// loading. Safe to call repeatedly; no-op when muted or non-DOM.
export function jackPrime() {
  if (_jackMuted()) return;
  if (typeof window === 'undefined' || !window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return;
  try {
    const synth = window.speechSynthesis;
    synth.getVoices(); // kick off async voice loading
    const u = new window.SpeechSynthesisUtterance(' ');
    u.volume = 0; // silent — just unlocks the engine within the gesture
    _jackUtterQueue.push(u);
    u.onend = u.onerror = () => { _jackUtterQueue = _jackUtterQueue.filter(x => x !== u); };
    synth.speak(u);
    synth.resume();
  } catch (e) { /* priming is best-effort */ }
  _ensureAudioCtx(); // unlock Web Audio (the boot chime) within the gesture too
}
// JACK's boot greeting — spoken the instant the console starts loading. When
// it finishes it flows straight into the headline brief (set later, once the
// data is in). This runs after the login click, so the browser allows speech.
function jackGreetBoot(name) {
  if (_jackGreeted || _jackMuted()) return;
  _jackGreeted = true;
  jackBootChime(); // a short power-up swell, then JACK speaks
  const line = 'Welcome back, ' + (name || 'Matt') +
    '. Just getting things ready, and checking the status of your app.';
  // jackSay always fires its callback (cinematic or browser or neither), so
  // the boot → headline chain can't stall.
  jackSay(line, () => { _jackBootEnded = true; _maybeSpeakHeadline(); });
}
// Exposed on the Agents global so the welcome banner buttons can call them.
export function jackReplay() { speakJack(_jackHeadline); }
export function jackToggleMute() {
  let muted = _jackMuted();
  try { localStorage.setItem('devJackMuted', muted ? '0' : '1'); } catch (e) {}
  muted = !muted;
  if (muted) jackCancel(); // stop the browser voice AND any cinematic clip
  // Reflect the new state on the toggle button without a full re-render.
  if (typeof document !== 'undefined') {
    const btn = document.getElementById('jackMuteBtn');
    if (btn) btn.textContent = muted ? '🔇 JACK muted' : '🔈 JACK voice on';
  }
  return muted;
}

// v120.5 — clear the errored bug-triage runs from the scorecard. Calls the
// admin-only Worker endpoint (deletes triage_results rows with kind='error'),
// then re-renders the console so the count drops. Exposed on the Agents global
// for the in-card button. Confirms first; nothing else is touched.
// v129.143 — the Comms Watcher's LOOP-CLOSING ACTION (notify group, 2 of 3).
//
// PURE + exported. The quiet stores are taken from the signal's `stores` ARRAY
// (added server-side in the same version) — never from `detail`, which is a
// display string truncated at 8 with "+N more". Acting on `detail` would have
// messaged a store literally named "+3 more" and silently skipped the ninth.
//
// Only the two store-level signals are actionable: a store that stopped
// reading news, or one that has written no journal. task_at_risk /
// survey_at_risk are about a TASK, not a store, so they are not nudgeable here
// and are deliberately excluded rather than half-handled.
export const QUIET_KINDS = ['stopped_reading', 'journal_silent'];
export function quietStores(signals) {
  const seen = {};
  (Array.isArray(signals) ? signals : []).forEach((sig) => {
    if (!sig || QUIET_KINDS.indexOf(sig.kind) === -1) return;
    (Array.isArray(sig.stores) ? sig.stores : []).forEach((st) => {
      if (typeof st !== 'string' || !st.trim()) return;
      const key = st.trim();
      (seen[key] = seen[key] || {})[sig.kind] = true;
    });
  });
  return Object.keys(seen).sort().map((store) => ({ store, kinds: Object.keys(seen[store]).sort() }));
}

// One message, phrased for the reason. A store that is quiet on BOTH counts
// gets ONE message naming both — never two pings in a row.
export function quietMessage(row) {
  const k = (row && row.kinds) || [];
  const bits = [];
  if (k.indexOf('stopped_reading') !== -1) bits.push('recent news posts have not been confirmed as read');
  if (k.indexOf('journal_silent') !== -1) bits.push('no journal entries have come through');
  return '👋 Checking in: ' + bits.join(', and ') + ' for this store. ' +
    'Nothing is in trouble — we would just rather ask than assume. ' +
    'Reply here if something is not working, or if you need a hand.';
}

// v129.142 — the Compliance agent's LOOP-CLOSING ACTION, second of the set.
//
// PURE, and exported, so the store list is testable without a DOM: which
// stores actually have outstanding CORE paperwork, and how many items each.
// Core = the things Matt used to compile by hand (missed checklists / logs).
// Late clock-ins and location noise are deliberately NOT nudged — nudging a
// store about amber noise is how a nudge becomes something people ignore.
export const NUDGE_CATEGORIES = ['checklist_compliance', 'log_compliance'];
export function storesToNudge(issues) {
  const by = {};
  (Array.isArray(issues) ? issues : []).forEach((i) => {
    if (!i || !i.store) return;
    if (NUDGE_CATEGORIES.indexOf(i.category) === -1) return;
    by[i.store] = (by[i.store] || 0) + 1;
  });
  return Object.keys(by).sort().map((s) => ({ store: s, count: by[s] }));
}

// The message a store receives. Templated on purpose — the console composes
// from the agent's own numbers, it is never a free-text box, so this cannot
// become an unlogged broadcast channel.
export function nudgeMessage(row, days) {
  const n = (row && row.count) || 0;
  return '📋 Compliance check: ' + n + ' outstanding paperwork item' + (n === 1 ? '' : 's') +
    ' for this store over the last ' + (days || 7) + ' days (missed checklists or logs). ' +
    'Please catch these up today — reply here if something is blocking you.';
}

// v129.141 — the Security agent's LOOP-CLOSING ACTION (Matt, 14 Aug: "give
// each agent an action that closes the loop"). Nine of the ten agents only
// watched: a red dot stayed a red dot because there was nothing to press.
// This turns the failing security-scan into tracked work — one GitHub issue,
// built SERVER-SIDE from the run the Worker re-reads (this client sends no
// content at all, so the button cannot become a way to post arbitrary text
// into the repo).
//
// Mirrors clearTriageErrors below: disable + restore the button, never leave
// it stuck, and say what happened. The one difference is the scope case — a
// PAT without Issues:write is not an error to apologise for, it is a two-minute
// fix, so it gets its own message naming the exact permission.
export async function openSecurityIssue(btn) {
  if (!_jackCtx) return;
  const restore = () => {
    if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Open an issue'; }
  };
  try {
    if (btn) { btn.dataset.label = btn.textContent; btn.disabled = true; btn.textContent = 'Opening…'; }
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/security/issue', {
      cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: '{}',
    });
    const d = await res.json().catch(() => ({}));
    if (res.status === 403) { restore(); if (typeof alert === 'function') alert('Head Office only.'); return; }
    if (!res.ok) throw new Error('HTTP ' + res.status);
    if (d && d.ok && d.url) {
      if (btn) { btn.textContent = 'Issue #' + (d.number || '?') + ' opened'; }
      if (typeof window !== 'undefined' && window.open) window.open(d.url, '_blank', 'noopener');
      return;                                   // leave the button showing what it did
    }
    restore();
    if (typeof alert === 'function') {
      alert(d && d.needsScope
        ? 'GitHub needs one more permission before this can work:\n\n' + (d.error || 'Issues: read and write') +
          '\n\nAdd it to the GH_PAT and press the button again.'
        : 'Could not open the issue: ' + ((d && d.error) || 'unknown error'));
    }
  } catch (e) {
    restore();
    if (typeof alert === 'function') alert('Could not open the issue: ' + ((e && e.message) || e));
  }
}

// v129.142 — send the nudges. Reuses /api/chat/send exactly as the console
// chat does, which matters for AUTHORISATION: that route already decides who
// may open a conversation with whom (canOpenDm on the verified viewer), so an
// AM cannot nudge a store outside their area and nothing new had to be
// invented. The message lands in the store's normal chat with the sender's own
// identity attached — visible, attributable, repliable.
//
// CONFIRM-GATED and CAPPED. This is the first agent action that reaches STAFF
// rather than a developer surface: the dialog names every store before a
// single message is sent, and NUDGE_MAX stops a bad data day turning one click
// into a broadcast. Failures are counted and reported, never swallowed — a
// half-sent nudge that claims success is worse than one that says "18 of 22".
export const NUDGE_MAX = 28;
export async function nudgeComplianceStores(btn) {
  if (!_jackCtx) return;
  const rows = storesToNudge(_lastCompliance && _lastCompliance.issues).slice(0, NUDGE_MAX);
  if (!rows.length) { if (typeof alert === 'function') alert('No stores have outstanding paperwork right now.'); return; }
  const days = (_lastCompliance && _lastCompliance.period && _lastCompliance.period.days) || 7;
  const names = rows.map((r) => '• ' + r.store + ' (' + r.count + ')').join('\n');
  if (!(await uiConfirm('Send a compliance nudge to ' + rows.length + ' store' + (rows.length === 1 ? '' : 's') +
               '?\n\n' + names + '\n\nEach store gets ONE message in their normal chat, from you.', { confirmLabel: 'Send' }))) {
    return;
  }
  const restore = () => { if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Nudge the stores'; } };
  if (btn) { btn.dataset.label = btn.textContent; btn.disabled = true; btn.textContent = 'Sending…'; }
  let sent = 0;
  const failed = [];
  for (let i = 0; i < rows.length; i++) {
    try {
      const res = await fetch(_jackCtx.workerUrl + '/api/chat/send', {
        cache: 'no-store',
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dm_with: 'store:' + rows[i].store, body: nudgeMessage(rows[i], days) }),
      });
      if (res.ok) sent++; else failed.push(rows[i].store + ' (HTTP ' + res.status + ')');
    } catch (e) {
      failed.push(rows[i].store + ' (' + ((e && e.message) || 'network') + ')');
    }
  }
  restore();
  if (typeof alert === 'function') {
    alert(failed.length
      ? 'Sent ' + sent + ' of ' + rows.length + '.\n\nNot sent:\n' + failed.join('\n')
      : 'Sent ' + sent + ' nudge' + (sent === 1 ? '' : 's') + '.');
  }
}

// v129.143 — send the check-ins. Same guards as the compliance nudge, same
// reused /api/chat/send (so canOpenDm still decides who may message whom).
// The wording is deliberately softer: a quiet store is a QUESTION, not a miss.
export async function checkInQuietStores(btn) {
  if (!_jackCtx) return;
  const rows = quietStores(_lastCommsSignals).slice(0, NUDGE_MAX);
  if (!rows.length) { if (typeof alert === 'function') alert('No stores are quiet right now.'); return; }
  const names = rows.map((r) => '• ' + r.store).join('\n');
  if (!(await uiConfirm('Send a check-in to ' + rows.length + ' quiet store' + (rows.length === 1 ? '' : 's') +
               '?\n\n' + names + '\n\nEach store gets ONE message in their normal chat, from you.', { confirmLabel: 'Send' }))) {
    return;
  }
  const restore = () => { if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Check in'; } };
  if (btn) { btn.dataset.label = btn.textContent; btn.disabled = true; btn.textContent = 'Sending…'; }
  let sent = 0;
  const failed = [];
  for (let i = 0; i < rows.length; i++) {
    try {
      const res = await fetch(_jackCtx.workerUrl + '/api/chat/send', {
        cache: 'no-store',
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dm_with: 'store:' + rows[i].store, body: quietMessage(rows[i]) }),
      });
      if (res.ok) sent++; else failed.push(rows[i].store + ' (HTTP ' + res.status + ')');
    } catch (e) {
      failed.push(rows[i].store + ' (' + ((e && e.message) || 'network') + ')');
    }
  }
  restore();
  if (typeof alert === 'function') {
    alert(failed.length
      ? 'Sent ' + sent + ' of ' + rows.length + '.\n\nNot sent:\n' + failed.join('\n')
      : 'Sent ' + sent + ' check-in' + (sent === 1 ? '' : 's') + '.');
  }
}

export async function clearTriageErrors(btn) {
  if (!_jackCtx) { return; }
  if (!(await uiConfirm('Clear the failed bug-triage runs from the scorecard?\n\nThis removes only errored runs (dead diagnostics) — your reports, fixes and everything else are untouched.', { danger: true, confirmLabel: 'Clear' }))) {
    return;
  }
  const reset = () => { if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Clear errored runs'; } };
  try {
    if (btn) { btn.dataset.label = btn.textContent; btn.disabled = true; btn.textContent = 'Clearing…'; }
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/triage-results/clear-errors', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    await res.json().catch(() => ({}));
    // Refresh the console with fresh data so the scorecard reflects the clear.
    if (_lastRenderOpts) { await render(_lastRenderOpts); }
    else if (typeof location !== 'undefined' && location.reload) { location.reload(); }
  } catch (e) {
    reset();
    if (typeof alert === 'function') alert('Could not clear errored runs: ' + ((e && e.message) || e));
  }
}

// v120.6 — Rung ③ native push alerts (Web Push). enablePushAlerts subscribes
// THIS device via the service worker + the server's VAPID public key; the
// Worker then pushes to it when prod errors spike. testPushAlert fires a test.
function _urlB64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePushAlerts(btn) {
  if (!_jackCtx) return;
  const reset = () => { if (btn) { btn.disabled = false; btn.textContent = '🔔 Enable alerts'; } };
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
      alert('Push notifications are not supported on this browser/device.'); return;
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Enabling…'; }
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') { alert('Notifications were not allowed. Turn them on for this app and try again.'); reset(); return; }
    const cfg = await fetch(_jackCtx.workerUrl + '/api/admin/push-config', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token },
    }).then(r => r.json());
    if (!cfg || !cfg.publicKey) {
      // Tell Matt EXACTLY what's wrong server-side (booleans only, no secrets).
      const miss = [];
      if (cfg && cfg.present) {
        if (!cfg.present.publicKey)  miss.push('VAPID_PUBLIC_KEY');
        if (!cfg.present.privateJwk) miss.push('VAPID_PRIVATE_JWK');
        if (!cfg.present.subject)    miss.push('VAPID_SUBJECT');
      }
      let why;
      if (miss.length) {
        why = 'Missing on the server: ' + miss.join(', ') + '. Add it on the elcapp-shift-staging Worker and Deploy.';
      } else if (cfg && cfg.present && cfg.present.privateJwk && cfg.jwkValid === false) {
        why = 'VAPID_PRIVATE_JWK is set but unreadable — it was likely truncated on paste. Delete it and re-paste the WHOLE { ... } value, then Deploy.';
      } else {
        why = 'VAPID keys not configured on the server yet.';
      }
      alert('Alerts not enabled.\n\n' + why);
      reset(); return;
    }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: _urlB64ToUint8Array(cfg.publicKey),
    });
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/push-subscribe', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    }).then(r => r.json());
    alert(res && res.ok ? 'Alerts enabled on this device.' : 'Could not save the subscription on the server.');
  } catch (e) {
    alert('Could not enable alerts: ' + ((e && e.message) || e));
  } finally {
    reset();
  }
}

export async function testPushAlert(btn) {
  if (!_jackCtx) return;
  try {
    if (btn) btn.disabled = true;
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/push-test', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: '{}',
    }).then(r => r.json());
    if (res && res.ok) alert('Test alert sent to ' + res.sent + ' device(s) — check your phone.');
    else alert('Test alert not sent: ' + ((res && res.error) || 'unknown') + (res && res.pruned ? ' (' + res.pruned + ' dead subscription(s) cleaned up)' : ''));
  } catch (e) {
    alert('Test alert failed: ' + ((e && e.message) || e));
  } finally {
    if (btn) btn.disabled = false;
  }
}

// #4 slice 1B — fire a CONTENT (encrypted payload) test push to this device, so
// Matt can confirm a notification with real text arrives + the tap opens the app.
export async function testPushPayload(btn) {
  if (!_jackCtx) return;
  try {
    if (btn) btn.disabled = true;
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/push-test-payload', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: 'Shift', body: 'Content notification works — this text came through encrypted.', url: '/dev.html' }),
    }).then(r => r.json());
    if (res && res.ok) alert('Content test sent to ' + res.sent + ' device(s) — your phone should show the TEXT, not just a generic alert.');
    else alert('Content test not sent: ' + ((res && res.error) || (res && res.errors && res.errors[0]) || 'unknown') + (res && res.pruned ? ' (' + res.pruned + ' dead subscription(s) cleaned up)' : ''));
  } catch (e) {
    alert('Content test failed: ' + ((e && e.message) || e));
  } finally {
    if (btn) btn.disabled = false;
  }
}

// v120.7 — Rung ③ DB backup. Pulls the read-only /api/admin/db-backup envelope
// (every source-of-truth table as JSON) and saves it to disk as a dated file.
// Read-only: it never writes app data — it's a portable second copy + the
// artifact the staging restore drill replays.
export async function downloadBackup(btn) {
  if (!_jackCtx) return;
  const label = btn ? btn.textContent : '';
  try {
    if (btn) { btn.disabled = true; btn.textContent = 'Backing up…'; }
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/db-backup', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token },
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const envelope = await res.json();
    const stamp = (envelope.created_at || new Date().toISOString()).slice(0, 10);
    const envName = envelope.environment || 'unknown';
    const fname = 'elc-backup-' + envName + '-' + stamp + '.json';
    const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    const errCount = Object.keys(envelope.errors || {}).length;
    alert('Backup saved: ' + fname + '\n\n' + envelope.total_rows + ' rows across '
      + envelope.table_count + ' tables (' + envName + ').'
      + (errCount ? '\n\n⚠ ' + errCount + ' table(s) could not be read: '
          + Object.keys(envelope.errors).join(', ') : ''));
  } catch (e) {
    alert('Backup failed: ' + ((e && e.message) || e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = label || '💾 Backup'; }
  }
}

// v120.8 — Rung ③ restore drill (STAGING ONLY). Lets Matt pick a backup file
// and load it back in, to PROVE the backup is restorable. Triple-guarded:
// (1) the button only renders on staging; (2) this requires a typed RESTORE
// confirmation; (3) the Worker hard-blocks unless APP_ENV==='staging'. It
// upserts the backed-up rows (merge) — it never touches prod.
function _isStagingConsole() {
  return !!(_jackCtx && _jackCtx.workerUrl && _jackCtx.workerUrl.indexOf('staging') !== -1);
}

export async function restoreBackup(btn) {
  if (!_jackCtx) return;
  if (!_isStagingConsole()) {
    alert('Restore is staging-only — it is disabled on production by design.');
    return;
  }
  // Pick a backup file from disk.
  const file = await new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => resolve(input.files && input.files[0]);
    input.click();
  });
  if (!file) return;
  let envelope;
  try {
    envelope = JSON.parse(await file.text());
  } catch (e) {
    alert('That file is not a valid backup (could not parse JSON).');
    return;
  }
  const rows = envelope && envelope.total_rows;
  const when = (envelope && envelope.created_at) || '?';
  const fromEnv = (envelope && envelope.environment) || '?';
  const typed = window.prompt(
    'RESTORE into STAGING from this backup?\n\n' +
    'From: ' + fromEnv + '  ·  taken: ' + when + '  ·  ' + (rows || 0) + ' rows.\n\n' +
    'This MERGES the backed-up rows back into staging (overwriting matching rows).\n' +
    'Type RESTORE to confirm.'
  );
  if (typed !== 'RESTORE') { alert('Restore cancelled.'); return; }
  try {
    if (btn) { btn.disabled = true; btn.textContent = 'Restoring…'; }
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/db-restore', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ backup: envelope, confirm: 'RESTORE' }),
    }).then(r => r.json());
    if (res && res.ok) {
      alert('Restore complete on ' + (res.environment || 'staging') + '.\n\n'
        + res.totalRestored + ' rows written back across ' + (res.results || []).length + ' tables.');
    } else {
      const failed = (res && res.failed && res.failed.length) ? '\n\nTables that failed: ' + res.failed.join(', ') : '';
      alert('Restore did not fully succeed: ' + ((res && res.error) || 'see per-table results') + failed);
    }
  } catch (e) {
    alert('Restore failed: ' + ((e && e.message) || e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '🔁 Restore (staging)'; }
  }
}

// ── JACK conversational chat ────────────────────────────────
// A real, clickable artifact for each WRITE action JACK takes — so a drafted
// PR or a captured roadmap item SHOWS as a card (success or failure), instead
// of being buried in his prose (or worse, narrated but never actually done).

// v129.89 — drafted store emails, kept module-side so the Copy button copies
// the REAL text (never re-parsed out of escaped HTML). Session-lifetime only.
const _jackDrafts = [];
export function jackCopyDraft(n, btn) {
  const d = _jackDrafts[n];
  if (!d) return false;
  const text = 'Subject: ' + d.subject + '\n\n' + d.body;
  const done = () => { if (btn) { btn.textContent = '✓ Copied'; setTimeout(() => { btn.textContent = '📋 Copy'; }, 1500); } };
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
      return true;
    }
  } catch (e) { /* clipboard unavailable — the card still shows the text to select */ }
  return false;
}
function _jackActionCard(a) {
  if (!a || !a.tool) return '';
  const r = a.result || {};
  if (a.tool === 'propose_fix') {
    if (r.ok && r.url) {
      return '<div class="jk-act jk-act-ok">' +
        '<span class="jk-act-i">🔧</span>' +
        '<span class="jk-act-t">Draft PR #' + esc(String(r.number || '?')) + ' opened on staging</span>' +
        '<a class="jk-act-link" href="' + esc(r.url) + '" target="_blank" rel="noopener">Review ↗</a></div>';
    }
    return '<div class="jk-act jk-act-err"><span class="jk-act-i">⚠</span>' +
      '<span class="jk-act-t">Couldn\'t open the PR: ' + esc(r.error || 'unknown error') + '</span></div>';
  }
  if (a.tool === 'get_compliance_trend') {
    // Read tool — JACK pulled the week-on-week trend; render the SAME chart +
    // table as the console card right in the chat, so "pull up a graph" works.
    if (r && Array.isArray(r.weeks) && r.weeks.length) {
      return '<div class="jk-act jk-act-ok" style="display:block">' +
        '<div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">' +
          '<span class="jk-act-i">📈</span><span class="jk-act-t">Compliance, week-on-week by area</span></div>' +
        complianceTrendInner(r) + '</div>';
    }
    return '<div class="jk-act"><span class="jk-act-i">📈</span>' +
      '<span class="jk-act-t">Not enough completed weeks yet to chart a compliance trend.</span></div>';
  }
  if (a.tool === 'draft_store_email') {
    // v129.89 (Phase 3b) — the copy-ready draft. NOTHING was sent: the card
    // IS the deliverable; Matt copies it into the real email himself.
    if (r.ok && r.body) {
      const n = _jackDrafts.push({ store: r.store, subject: r.subject, body: r.body }) - 1;
      return '<div class="jk-act jk-act-ok" style="display:block">' +
        '<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">' +
          '<span class="jk-act-i">✉️</span>' +
          '<span class="jk-act-t">Draft for ' + esc(r.store) + ' — nothing sent</span>' +
          '<button type="button" class="jk-act-link" style="cursor:pointer" ' +
            'onclick="if(window.Agents)Agents.jackCopyDraft(' + n + ', this)">📋 Copy</button></div>' +
        '<div translate="no" style="font-weight:600;margin-bottom:2px">' + esc(r.subject) + '</div>' +
        '<div translate="no" style="white-space:pre-wrap">' + esc(r.body) + '</div></div>';
    }
    return '<div class="jk-act jk-act-err"><span class="jk-act-i">⚠</span>' +
      '<span class="jk-act-t">Couldn\'t compose the draft: ' + esc(r.error || 'unknown error') + '</span></div>';
  }
  if (a.tool === 'append_roadmap') {
    if (r.ok) {
      const link = r.commitUrl
        ? '<a class="jk-act-link" href="' + esc(r.commitUrl) + '" target="_blank" rel="noopener">View commit ↗</a>' : '';
      return '<div class="jk-act jk-act-ok"><span class="jk-act-i">📝</span>' +
        '<span class="jk-act-t">Captured to the roadmap' + (r.item ? ': "<span translate="no">' + esc(r.item) + '</span>"' : '') + '</span>' + link + '</div>';
    }
    return '<div class="jk-act jk-act-err"><span class="jk-act-i">⚠</span>' +
      '<span class="jk-act-t">Couldn\'t save to the roadmap: ' + esc(r.error || 'unknown error') + '</span></div>';
  }
  return '';
}

// Render the conversation into the chat log (hosted in dev.html so it
// survives the dashboard's re-renders). esc() keeps replies safe.
function _renderJackChat() {
  if (typeof document === 'undefined') return;
  const log = document.getElementById('jackChatLog');
  if (!log) return;
  let html = '';
  if (!_jackConvo.length) {
    html = '<div class="jk-empty">Ask Jack anything about your app — <i>"what\'s wrong right now?"</i>, ' +
      '<i>"explain the rota gaps"</i>, <i>"what should I do next?"</i>. ' +
      'He reads your live console state, and can hand a code error to the junior dev to draft a fix (a PR you approve).</div>';
  } else {
    html = _jackConvo.map(m =>
      '<div class="jk-msg jk-' + (m.role === 'user' ? 'you' : 'jack') + '">' +
        '<span class="jk-who">' + (m.role === 'user' ? 'You' : 'JACK') + '</span>' +
        // v135.2: the question and the answer stay as written (translate="no"); only the chat's fixed replies do not.
        // v136.58: Jack's replies are drawn (bold, lists, code); what you typed stays exactly as written
        '<div class="jk-text"' + (_jackFixed.has(m) ? '' : ' translate="no"') + '>' + (m.role === 'user' ? esc(m.content) : jackProse(m.content)) + '</div>' +
        ((m.actions && m.actions.length) ? m.actions.map(_jackActionCard).join('') : '') +
      '</div>').join('');
  }
  if (_jackThinking) {
    html += '<div class="jk-msg jk-jack"><span class="jk-who">JACK</span>' +
      '<div class="jk-text jk-think">…thinking</div></div>';
  }
  log.innerHTML = html;
  log.scrollTop = log.scrollHeight;
}

// Ask JACK a question. Sends the recent conversation + the live console state
// to the Worker (Claude/Opus), shows + speaks the reply. Exposed on Agents.
// v132.70: connect the chat WITHOUT the console boot. The admin app's JACK
// tab mounts the same #jackChatLog / #jackChatInput markup and calls this
// with the admin session; the console state stays empty and JACK reads the
// live data through his tools as usual. Safe to call on every tab open.
export function jackConnect(opts) {
  const o = opts || {};
  if (!o.workerUrl || !o.token) return false;
  _jackCtx = { workerUrl: o.workerUrl, token: o.token };
  _renderJackChat();
  return true;
}
export async function jackAsk(text) {
  text = String(text || '').trim();
  if (!text) return;
  if (!_jackCtx) {
    const notYet = { role: 'assistant', content: 'I\'m not connected yet — reload the console and sign in.' };
    _jackFixed.add(notYet);
    _jackConvo.push(notYet);
    _renderJackChat();
    return;
  }
  _jackConvo.push({ role: 'user', content: text });
  _jackThinking = true;
  _renderJackChat();
  let reply = '';
  let actions = [];
  let answered = false;   // v135.2: false = one of the fixed replies below (a label, see _jackFixed)
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: _jackConvo.slice(-12), context: _jackState }),
    });
    const data = await res.json().catch(() => ({}));
    answered = !!(data && data.ok && data.text);
    reply = (data && data.ok && data.text) ? data.text
      : (data && data.reason === 'no-key') ? 'My brain isn\'t wired up yet — the ANTHROPIC_KEY secret is missing on the Worker.'
      : (data && data.reason === 'rate-limit') ? 'Give me a moment — that was a lot of questions at once.'
      : 'Sorry, I couldn\'t reach my brain just then. Try again in a moment.';
    if (data && Array.isArray(data.actions)) actions = data.actions;
  } catch (e) {
    reply = 'Sorry, I couldn\'t reach my brain just then. Try again in a moment.';
  }
  _jackThinking = false;
  // Stash any WRITE-action results (PR link / roadmap capture) on the message
  // so the chat shows a real artifact, not just Jack's prose. _jackConvo is
  // sliced to {role,content} before it's sent back to the model, so the extra
  // field is purely for the UI.
  const answer = { role: 'assistant', content: reply, actions: actions };
  if (!answered) _jackFixed.add(answer);
  _jackConvo.push(answer);
  _renderJackChat();
  // Speak the reply; in hands-free mode, re-open the mic once Jack finishes so
  // the conversation just keeps flowing.
  jackCancel();
  jackSay(reply, _jackHandsFree ? _reListen : null);
}

// Run Jack's overnight 360 sweep ON DEMAND (the "🌙 Sweep" button). Posts the
// result straight into the chat and speaks it — so you can test the sweep now
// instead of waiting for the nightly cron (works even before the migration;
// storage just no-ops). Exposed on Agents.
export async function jackSweepNow() {
  if (!_jackCtx) return;
  _jackConvo.push({ role: 'user', content: 'Run an overnight sweep now.' });
  _jackThinking = true;
  _renderJackChat();
  let msg = '';
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-sweep-run', { cache: 'no-store',
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
      body: '{}',
    });
    const d = await res.json().catch(() => ({}));
    if (d && d.ok && d.hasFinding) {
      msg = (d.headline || 'I found something.') + (d.detail ? '\n\n' + d.detail : '');
    } else if (d && d.ok) {
      msg = 'Swept everything — all clear. CI, rota coverage and data feeds look healthy right now.';
    } else if (d && d.reason === 'rate-limit') {
      msg = 'Give me a moment before the next sweep.';
    } else {
      msg = 'I couldn\'t complete the sweep just then.';
    }
  } catch (e) {
    msg = 'I couldn\'t complete the sweep just then.';
  }
  _jackThinking = false;
  _jackConvo.push({ role: 'assistant', content: msg });
  _renderJackChat();
  jackCancel();
  jackSay(msg, _jackHandsFree ? _reListen : null);
}

// Voice input. PREFERS ElevenLabs Scribe (record the mic → Worker → accurate
// transcript) because the browser recogniser mangles names/accents. Falls back
// to the browser SpeechRecognition when there's no mic/key. Push-to-talk: click
// to start, click again (or after the cap) to stop, transcribe + send.
let _jackRec = null;          // SpeechRecognition (fallback)
let _jackListening = false;   // fallback recogniser active
let _jackMediaRec = null;     // MediaRecorder (Scribe path)
let _jackChunks = [];
let _jackRecording = false;   // Scribe recording active
let _jackStopTimer = null;
let _jackHandsFree = false;   // continuous conversation loop (JARVIS-style)

function _jackMicBtnEl() { return (typeof document !== 'undefined') ? document.getElementById('jackMicBtn') : null; }
function _jackInputEl() { return (typeof document !== 'undefined') ? document.getElementById('jackChatInput') : null; }
function _jackMicIdle() {
  const btn = _jackMicBtnEl();
  if (btn) { btn.classList.remove('jk-mic-on'); btn.textContent = '🎤'; btn.removeAttribute('data-jk-standby'); }
}
function _jackMicLive() {
  const btn = _jackMicBtnEl();
  if (btn) { btn.classList.add('jk-mic-on'); btn.textContent = '● listening'; btn.removeAttribute('data-jk-standby'); }
}

export function jackListen() {
  if (typeof window === 'undefined') return;
  // If already recording (Scribe) or listening (fallback), this click STOPS it.
  if (_jackRecording) { _stopJackRecording(); return; }
  if (_jackListening) { try { _jackRec && _jackRec.stop(); } catch (e) {} return; }
  // Prefer the accurate server STT when we can record + have a worker context.
  const canRecord = typeof navigator !== 'undefined' && navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia && typeof MediaRecorder !== 'undefined';
  if (_jackCtx && canRecord && _ttsOk !== false) { _startJackRecording(); return; }
  _jackListenBrowser();
}

function _startJackRecording() {
  navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
    let mr;
    try { mr = new MediaRecorder(stream); } catch (e) { stream.getTracks().forEach(t => t.stop()); _jackListenBrowser(); return; }
    _jackChunks = [];
    _jackMediaRec = mr;
    _jackRecording = true;
    _jackMicLive();
    mr.ondataavailable = (e) => { if (e.data && e.data.size) _jackChunks.push(e.data); };
    mr.onstop = () => {
      _jackRecording = false;
      if (_jackStopTimer) { clearTimeout(_jackStopTimer); _jackStopTimer = null; }
      _jackMicIdle();
      try { stream.getTracks().forEach(t => t.stop()); } catch (e) {}
      const blob = new Blob(_jackChunks, { type: 'audio/webm' });
      if (blob.size) _transcribeAndSend(blob);
    };
    mr.start();
    _startVAD(stream); // auto-stop when you stop speaking (hands-free flow)
    // Safety cap so a forgotten recording can't run forever.
    _jackStopTimer = setTimeout(() => { _stopJackRecording(); }, 20000);
  }).catch(() => { _jackMicIdle(); _jackHandsFree = false; _jackListenBrowser(); }); // mic denied → fallback
}
// Voice-activity detection: auto-stop the recording ~1.3s after you stop
// talking, so you never have to tap to send. Pure Web Audio level metering.
function _startVAD(stream) {
  try {
    const ctx = _ensureAudioCtx();
    if (!ctx) return;
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    let speechStarted = false, silenceStart = 0;
    const tick = () => {
      if (!_jackRecording) return; // stopped elsewhere
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const d = buf[i] - 128; sum += d * d; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms > 6) { speechStarted = true; silenceStart = 0; }
      else if (speechStarted) {
        if (!silenceStart) silenceStart = now;
        else if (now - silenceStart > 1300) { _stopJackRecording(); return; }
      }
      (window.requestAnimationFrame || setTimeout)(tick, 16);
    };
    (window.requestAnimationFrame || setTimeout)(tick, 16);
  } catch (e) { /* VAD is best-effort — the 20s cap still applies */ }
}
function _stopJackRecording() {
  try { if (_jackMediaRec && _jackMediaRec.state !== 'inactive') _jackMediaRec.stop(); } catch (e) {}
}
function _transcribeAndSend(blob) {
  const inp = _jackInputEl();
  if (inp) inp.placeholder = 'Transcribing…';
  const fd = new FormData();
  fd.append('file', blob, 'audio.webm');
  fetch(_jackCtx.workerUrl + '/api/admin/stt', { cache: 'no-store',
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + _jackCtx.token }, // no Content-Type — browser sets the multipart boundary
    body: fd,
  }).then((r) => r.json()).then((d) => {
    if (inp) inp.placeholder = 'Ask Jack…  type, or tap 🎤 to talk';
    const text = ((d && d.ok && d.text) || '').trim();
    if (text) jackAsk(text);
    else if (d && d.reason === 'no-key') _jackListenBrowser(); // key missing → fall back next time
    else if (_jackHandsFree) _reListen(); // empty transcript but still conversing → keep listening
  }).catch(() => {
    if (inp) inp.placeholder = 'Ask Jack…  type, or tap 🎤 to talk';
    if (_jackHandsFree) _reListen();
  });
}
// Re-open the mic shortly after (used by the hands-free loop). The small delay
// keeps the mic from catching the tail of Jack's own voice.
// v129.88 (Phase 3a): with the wake word on, the loop returns to STANDBY
// (free on-device listening for "Jack") instead of an always-open paid
// recorder — ambient store noise never reaches the STT bill.
function _reListen() {
  if (!_jackHandsFree) return;
  setTimeout(() => {
    if (!_jackHandsFree || _jackRecording) return;
    if (_jackWakeWord && _wakeStandbySupported()) _wakeStandby();
    else jackListen();
  }, 300);
}
// Toggle the continuous, hands-free conversation loop. When on: you talk → it
// auto-sends on silence → Jack replies aloud → the mic re-opens → repeat.
export function jackHandsFree() {
  if (typeof window === 'undefined') return false;
  _jackHandsFree = !_jackHandsFree;
  const btn = (typeof document !== 'undefined') ? document.getElementById('jackHfBtn') : null;
  if (btn) {
    btn.classList.toggle('jk-hf-on', _jackHandsFree);
    btn.textContent = _jackHandsFree ? '🎙 Hands-free: on' : '🎙 Hands-free: off';
  }
  if (_jackHandsFree) {
    if (_jackWakeWord && _wakeStandbySupported()) _wakeStandby();
    else if (!_jackRecording) jackListen();
  } else { _stopWakeStandby(); _stopJackRecording(); _stopBargeMonitor(); jackCancel(); }
  return _jackHandsFree;
}

// ── JACK Phase 3a (v129.88) — WAKE WORD + BARGE-IN ──────────────────────────
// (docs/jack-jarvis-plan.md Phase 3, un-gated by Matt 8 Aug.)
//
// WAKE WORD: in hands-free mode, a client-side "Jack…" gate BEFORE any audio
// is sent to the paid STT. Standby listening runs on the browser's own
// on-device recogniser (free); only once "Jack" is heard does the accurate
// Scribe recorder open for the actual request. If the wake utterance already
// carries the request ("Jack, what's going on?"), the remainder is sent
// straight to him — no second round of speaking. No SpeechRecognition in this
// browser → the toggle disables itself and hands-free behaves exactly as
// before (fail to today's behaviour, never to silence).
//
// BARGE-IN: while Jack is speaking in hands-free mode, a Web Audio level
// monitor (echoCancellation on, so his own voice through the speakers is
// subtracted by the browser's AEC) watches for YOU starting to talk —
// sustained speech cuts his TTS mid-sentence and opens the mic. Client-only;
// best-effort at every step: any failure means he simply finishes his line.

let _jackWakeWord = (typeof localStorage !== 'undefined' && localStorage.getItem('jack_wake_word') === '1');
let _wakeRec = null;          // browser SR while in standby
let _wakeStandbyOn = false;

const WAKE_RE = /\bjack\b/i;
// Pure: given a standby transcript, decide what the wake word starts.
// Returns null (no wake word), '' (wake only — open the mic for the request),
// or the request itself when it rode in with the wake word.
export function wakeCommand(transcript) {
  const t = String(transcript || '').trim();
  const m = WAKE_RE.exec(t);
  if (!m) return null;
  const rest = t.slice(m.index + m[0].length).replace(/^[\s,.!?:;-]+/, '').trim();
  // A real command needs a couple of words — "Jack?" or "Jack." is a summons.
  return rest.split(/\s+/).filter(Boolean).length >= 2 ? rest : '';
}

function _wakeStandbySupported() {
  return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}
function _wakeChime() {
  // A short soft blip so you know he heard his name. Best-effort.
  try {
    const ctx = _ensureAudioCtx();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.2);
  } catch (e) { /* a nicety — never throw */ }
}
function _wakeStandby() {
  if (_wakeStandbyOn || _jackRecording || !_jackHandsFree) return;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) { jackListen(); return; }
  try {
    const rec = new SR();
    rec.lang = 'en-GB';
    rec.continuous = true;
    rec.interimResults = true;
    _wakeRec = rec;
    _wakeStandbyOn = true;
    const btn = _jackMicBtnEl();
    if (btn) { btn.classList.remove('jk-mic-on'); btn.textContent = '👂'; btn.title = 'Standing by — say "Jack" to talk'; btn.setAttribute('data-jk-standby', '1'); }
    rec.onresult = (e) => {
      let txt = '';
      for (let i = e.resultIndex; i < e.results.length; i++) txt += e.results[i][0].transcript;
      const cmd = wakeCommand(txt);
      if (cmd === null) return;
      _stopWakeStandby();
      _wakeChime();
      if (cmd) jackAsk(cmd);                 // the request rode in with the name
      else if (!_jackRecording) jackListen(); // summons only — open the good mic
    };
    // Browsers time continuous SR out after a while of silence — just restart.
    rec.onend = () => {
      if (_wakeRec !== rec) return;
      _wakeStandbyOn = false;
      if (_jackHandsFree && _jackWakeWord && !_jackRecording) setTimeout(() => _wakeStandby(), 250);
    };
    rec.onerror = () => { /* onend fires next and handles the restart */ };
    rec.start();
  } catch (e) { _wakeStandbyOn = false; _wakeRec = null; jackListen(); }
}
function _stopWakeStandby() {
  const rec = _wakeRec;
  _wakeRec = null;
  _wakeStandbyOn = false;
  if (rec) { try { rec.stop(); } catch (e) {} }
  const btn = _jackMicBtnEl();
  // v135.2: the standby state is read from a data attribute, never from the button's on-screen text.
  if (btn && btn.getAttribute('data-jk-standby') === '1') _jackMicIdle();
}
// Toggle the wake-word gate (persisted; takes effect on the running loop).
export function jackWakeWord() {
  if (typeof window === 'undefined') return false;
  _jackWakeWord = !_jackWakeWord;
  try { localStorage.setItem('jack_wake_word', _jackWakeWord ? '1' : '0'); } catch (e) {}
  const btn = (typeof document !== 'undefined') ? document.getElementById('jackWwBtn') : null;
  if (btn) {
    btn.classList.toggle('jk-hf-on', _jackWakeWord);
    btn.textContent = _jackWakeWord ? '👂 "Jack": on' : '👂 "Jack": off';
  }
  // Re-shape the live loop to the new setting.
  if (_jackHandsFree) {
    if (_jackWakeWord && _wakeStandbySupported()) { _stopJackRecording(); _wakeStandby(); }
    else { _stopWakeStandby(); if (!_jackRecording) jackListen(); }
  }
  return _jackWakeWord;
}

// Barge-in: a level monitor that runs only while Jack speaks in hands-free.
let _bargeStream = null;
let _bargeActive = false;
function _startBargeMonitor() {
  if (_bargeActive || !_jackHandsFree) return;
  if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
  _bargeActive = true;
  navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true } }).then((stream) => {
    if (!_bargeActive) { stream.getTracks().forEach(t => t.stop()); return; }
    _bargeStream = stream;
    const ctx = _ensureAudioCtx();
    if (!ctx) { _stopBargeMonitor(); return; }
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    let loudStart = 0;
    const tick = () => {
      if (!_bargeActive) return;
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const d = buf[i] - 128; sum += d * d; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      // A higher bar than the VAD's 6: the AEC removes Jack's own voice, but
      // barging in on a cough would be worse than missing a quiet interjection.
      if (rms > 10) {
        if (!loudStart) loudStart = now;
        else if (now - loudStart > 300) { _bargeIn(); return; }
      } else loudStart = 0;
      (window.requestAnimationFrame || setTimeout)(tick, 16);
    };
    (window.requestAnimationFrame || setTimeout)(tick, 16);
  }).catch(() => { _bargeActive = false; });
}
function _stopBargeMonitor() {
  _bargeActive = false;
  if (_bargeStream) { try { _bargeStream.getTracks().forEach(t => t.stop()); } catch (e) {} _bargeStream = null; }
}
function _bargeIn() {
  _stopBargeMonitor();
  jackCancel();                 // cut him off mid-sentence
  _stopWakeStandby();
  if (!_jackRecording) _startJackRecording();   // you're already talking — straight to the good mic
}

// The wake-word preference survives reloads — reflect it on the button once
// the DOM is there (the toggle handler keeps it in sync from then on).
if (typeof document !== 'undefined') {
  const _syncWwBtn = () => {
    const b = document.getElementById('jackWwBtn');
    if (b && _jackWakeWord) { b.classList.add('jk-hf-on'); b.textContent = '👂 "Jack": on'; }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', _syncWwBtn);
  else _syncWwBtn();
}

// Fallback: the browser's built-in recogniser (less accurate; Chrome/Edge).
function _jackListenBrowser() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const btn = _jackMicBtnEl();
  if (!SR) { if (btn) btn.title = 'Voice input needs Chrome or Edge'; return; }
  if (_jackListening) { try { _jackRec && _jackRec.stop(); } catch (e) {} return; }
  const inp = _jackInputEl();
  const setIdle = () => { _jackListening = false; _jackMicIdle(); };
  try {
    _jackRec = new SR();
    _jackRec.lang = 'en-GB';
    _jackRec.interimResults = true;
    _jackRec.continuous = false;
    _jackListening = true;
    _jackMicLive();
    _jackRec.onresult = (e) => {
      let txt = '';
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      if (inp) inp.value = txt;
      const last = e.results[e.results.length - 1];
      if (last && last.isFinal) {
        const finalTxt = (inp ? inp.value : txt).trim();
        if (inp) inp.value = '';
        setIdle();
        if (finalTxt) jackAsk(finalTxt);
      }
    };
    _jackRec.onend = setIdle;
    _jackRec.onerror = setIdle;
    _jackRec.start();
  } catch (e) { setIdle(); }
}

async function api(workerUrl, token, path) {
  const res = await fetch(workerUrl + path, { cache: 'no-store',
    headers: { 'Authorization': 'Bearer ' + token },
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

// ── derive the bug-triage card from triage_results rows ─────
function triageStats(rows) {
  rows = Array.isArray(rows) ? rows : [];
  const total = rows.length;
  const errors = rows.filter(r => r.kind === 'error').length;
  const fixes = rows.filter(r => r.kind === 'fix').length;
  const clarify = rows.filter(r => r.kind === 'clarify').length;
  const byDesign = rows.filter(r => r.kind === 'by_design').length;
  // "health" = share of runs that produced a usable answer (not an error).
  const health = total ? Math.round(100 * (total - errors) / total) : 0;
  // RAG: green if healthy + active; amber if shaky or idle; red if mostly errors.
  let rag = 'x';
  if (total === 0) rag = 'a';
  else if (errors / total > 0.4) rag = 'r';
  else if (errors / total > 0.15) rag = 'a';
  else rag = 'g';
  let lastRun = null;
  for (const r of rows) {
    if (r.created_at && (!lastRun || r.created_at > lastRun)) lastRun = r.created_at;
  }
  return { total, errors, fixes, clarify, byDesign, health, rag, lastRun };
}

function ago(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return '—';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.round(h / 24) + 'd ago';
}

// ── watcher-memory trends (sparklines) ──────────────────────
// Render numbers (oldest→newest) as a tiny inline SVG line. < 2 points →
// a "building history" placeholder (the daily cron / manual trigger fills
// it over time).
function sparkline(values) {
  const v = (values || []).filter(n => typeof n === 'number' && isFinite(n));
  if (v.length < 2) return '<span class="spark-wait">building history…</span>';
  const min = Math.min.apply(null, v), max = Math.max.apply(null, v);
  const range = (max - min) || 1;
  const W = 72, H = 20, pad = 2, n = v.length;
  const pts = v.map((val, i) =>
    ((i / (n - 1)) * W).toFixed(1) + ',' +
    (H - pad - ((val - min) / range) * (H - 2 * pad)).toFixed(1)
  ).join(' ');
  return '<svg class="spark" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none">' +
    '<polyline points="' + pts + '" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
}

// A labelled trend row for a card: label + sparkline + the change across the
// window. goodDown=true means a FALLING line is good (e.g. open backlog).
function trendRow(label, values, goodDown) {
  const v = (values || []).filter(n => typeof n === 'number' && isFinite(n));
  let delta = '';
  if (v.length >= 2) {
    const d = v[v.length - 1] - v[0];
    const cls = d === 0 ? 'flat' : ((d < 0) === !!goodDown ? 'good' : 'bad');
    const arrow = d > 0 ? '▲' : d < 0 ? '▼' : '–';
    delta = '<span class="a-trend-d ' + cls + '">' + arrow + ' ' + Math.abs(d) + '</span>';
  }
  return '<div class="a-trend"><span class="a-trend-k">' + esc(label) + '</span>' +
    sparkline(values) + delta + '</div>';
}

// ── Heads-up nudges — make the watchers PROACTIVE ───────────
// Turn the trend history (+ a couple of live signals) into plain-English
// alerts about what's getting WORSE or newly broke — the dimension the
// point-in-time cards don't show.
function _rising(series) {
  const v = (series || []).filter(n => typeof n === 'number' && isFinite(n));
  if (v.length < 3) return null;
  const w = v.slice(-3);
  // non-decreasing across the last 3 points AND a net increase
  if (w[2] >= w[1] && w[1] >= w[0] && w[2] > w[0]) return { from: w[0], to: w[2] };
  return null;
}
function _appeared(series) {
  const v = (series || []).filter(n => typeof n === 'number' && isFinite(n));
  if (v.length < 2) return null;
  const last = v[v.length - 1], prev = v[v.length - 2];
  if (last > 0 && prev === 0) return { to: last };
  return null;
}
export function computeNudges(d) {
  d = d || {};
  const live = d.live || {};
  const n = [];
  const fb = _rising(d.feedbackOpenSeries);
  if (fb) n.push({ sev: 'med', text: 'Open feedback backlog is climbing — up to ' + fb.to + ' (was ' + fb.from + ' a few days ago).' });
  const te = _rising(d.triageErrorsSeries);
  if (te && te.to > 0) n.push({ sev: 'med', text: 'Bug-Triage errors are trending up — ' + te.to + ' lately.' });
  const rg = _appeared(d.rotaGapsSeries) || _rising(d.rotaGapsSeries);
  if (rg && (rg.to || 0) > 0) n.push({ sev: 'high', text: 'Coverage gaps have appeared in this week\'s rota (' + rg.to + ').' });
  const fs = _rising(d.feedStaleSeries);
  if (fs && fs.to > 0) n.push({ sev: 'med', text: 'More data feeds are going stale (' + fs.to + ' need attention).' });
  // Live, no history needed — a failing integrity check is urgent right now.
  if ((live.smokeFail || 0) > 0) {
    n.push({ sev: 'high', text: live.smokeFail + ' data-integrity check' + (live.smokeFail === 1 ? '' : 's') + ' failing right now.' });
  }
  const rank = { high: 3, med: 2, low: 1 };
  n.sort((a, b) => rank[b.sev] - rank[a.sev]);
  return n.slice(0, 3);
}
function buildNudges(nudges) {
  if (!nudges || !nudges.length) return ''; // nothing trending the wrong way → no strip
  const dot = (sev) => sev === 'high' ? 'r' : sev === 'med' ? 'a' : 'x';
  return '<div class="nudge" id="nudgePanel">' +
    '<div class="nudge-h">⚡ Heads-up · things changing</div>' +
    '<ul class="nudge-list">' + nudges.map(x =>
      '<li><span class="a-dot ' + dot(x.sev) + '"></span>' + esc(x.text) + '</li>').join('') +
    '</ul></div>';
}

// v136.58 (Matt, 26 Oct 2026: "look at the formatting in the ui eg ** instead of bold"): Jack writes light
// markdown, and the sweep card and the chat printed it raw (stars, backticks, "---"). This draws it: ESCAPE
// EVERYTHING FIRST, then turn only these markers into our own tags, so the only HTML out is ours (the
// src/richtext rule): **bold**, *italic*, `code`, "# " headings (bold), "- " / "* " / "• " bullets; a line
// of dashes is dropped and blank lines collapse to one break.
export function jackProse(text) {
  const inline = (s) => s
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*\w])\*([^*\s][^*\n]*)\*(?![*\w])/g, '$1<i>$2</i>');
  const out = [];
  let list = null, blank = false;
  const flush = () => { if (list) { out.push('<ul class="jk-ul">' + list.join('') + '</ul>'); list = null; } };
  esc(text).replace(/\r\n?/g, '\n').split('\n').forEach((raw) => {
    const l = raw.trim();
    if (/^[-–—_*=]{2,}$/.test(l)) return;   // a divider line
    const b = /^(?:[-*•])\s+(.*)$/.exec(l);
    if (b) { list = list || []; list.push('<li>' + inline(b[1]) + '</li>'); blank = false; return; }
    flush();
    if (!l) { if (out.length && !blank) { out.push(''); blank = true; } return; }
    blank = false;
    const h = /^#{1,6}\s+(.*)$/.exec(l);
    out.push(h ? '<b>' + inline(h[1]) + '</b>' : inline(l));
  });
  flush();
  while (out.length && out[out.length - 1] === '') out.pop();
  let html = '';
  out.forEach((chunk, i) => {
    if (i > 0 && !/^<ul/.test(chunk) && !/^<ul/.test(out[i - 1])) html += '<br>';
    html += chunk;
  });
  return html;
}

// v136.59 (Matt, 26 Oct 2026: "can we create an agent that fixes this?"): THE REPAIR CARD on Jack's list. The
// Worker (src/worker/routes/clock-repair.js) proposes a fix for each clock-out with no clock-in: the rostered
// start, or removing a duplicate. Nothing changes until Matt taps Approve; each repair is marked ESTIMATED in
// the Clock Log and audited with his name. Rows it cannot propose for are listed for a manager.
let _repairData = null;
export function buildRepairCard(d) {
  const props = (d && Array.isArray(d.proposals)) ? d.proposals : [];
  const manual = (d && Array.isArray(d.manual)) ? d.manual : [];
  if (!props.length && !manual.length) return '';
  const hm = (iso) => esc(String(iso || '').slice(11, 16));
  const who = (r) => '<b translate="no">' + esc(r.staff) + '</b> · <span translate="no">' + esc(r.store) + '</span> · ' + esc(r.date);
  const line = (r) => '<div class="rp-row"><div class="rp-t">' + who(r) + ': clocked out ' + hm(r.clock_out) + ' with no clock-in.<br>' +
      '<span class="rp-p">' + (r.action === 'remove' ? 'Remove it.' : 'Set the clock-in to ' + hm(r.clock_in) + '.') + ' ' + esc(r.why) + '</span></div>' +
      '<button class="dq-go" onclick="Agents.repairApply([' + Number(r.id) + '], this)">Approve</button></div>';
  return '<div class="jsweep" id="repairCard">' +
    '<div class="jsweep-h"><span class="jsweep-i">🛠️</span><span class="jsweep-t">Pay records Jack can repair</span>' +
      (props.length > 1 ? '<button class="dq-go" onclick="Agents.repairApply([' + props.map((r) => Number(r.id)).join(',') + '], this)">Approve all ' + props.length + '</button>' : '') +
    '</div>' +
    props.map(line).join('') +
    (manual.length ? '<div class="rp-man"><b>' + manual.length + ' need a manager</b> (fix in Admin, Clock Log):' +
      manual.map((r) => '<div class="rp-t">' + who(r) + ', clocked out ' + hm(r.clock_out) + '. ' + esc(r.why) + '</div>').join('') + '</div>' : '') +
    '<div class="jsweep-foot">Nothing changes until you approve. Each repair is marked ESTIMATED in the Clock Log and audited with your name.</div>' +
  '</div>';
}
async function _repairPost(body) {
  if (!_jackCtx || typeof fetch === 'undefined') return null;
  const res = await fetch(_jackCtx.workerUrl + '/admin/clock/repair-missing-start', { cache: 'no-store', method: 'POST',
    headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((d && d.error) || ('HTTP ' + res.status));
  return d;
}
export async function repairApply(ids, btn) {
  const was = btn ? btn.textContent : '';
  const n = (ids || []).length;
  // a pay-record write: said plainly and confirmed first, every time
  if (!(await uiConfirm('Repair ' + n + ' pay record' + (n === 1 ? '' : 's') + '?\n\nEach is changed exactly as shown on the card, marked ESTIMATED in the Clock Log and audited with your name.', { confirmLabel: 'Repair' }))) return;
  try {
    if (btn) { btn.disabled = true; btn.textContent = 'Repairing…'; }
    const r = await _repairPost({ apply: true, ids: (ids || []).map(Number) });
    _repairData = await _repairPost({});
    const el = (typeof document !== 'undefined') ? document.getElementById('repairCard') : null;
    const html = buildRepairCard(_repairData);
    if (el) { if (html) el.outerHTML = html; else el.remove(); }
    if (typeof showToast === 'function') showToast((r && r.applied) + ' repaired' + (r && r.failed && r.failed.length ? ', ' + r.failed.length + ' failed' : '') + '.', 'success');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = was || 'Approve'; }
    if (typeof showToast === 'function') showToast('Could not repair: ' + ((e && e.message) || e), 'error');
  }
}

// JACK's overnight 360 sweep — the heads-up he left while you were away.
// `sweep` is the /api/admin/jack-sweep response: { ok, findings:[...] }.
// We show the most recent open finding (the cron supersedes older ones).
function buildJackSweep(sweep) {
  const f = sweep && Array.isArray(sweep.findings) ? sweep.findings[0] : null;
  if (!f || !f.headline) return '';
  const icon = ({ ci: '🔴', rota: '📅', freshness: '🕖', integrity: '🧮', mixed: '🌙', sweep: '🌙' })[f.kind] || '🌙';
  let when = '';
  try { if (f.created_at) when = new Date(f.created_at).toLocaleString(); } catch (e) { /* ignore */ }
  return '<div class="jsweep" id="jackSweep">' +
    '<div class="jsweep-h">' +
      '<span class="jsweep-i">' + icon + '</span>' +
      '<span class="jsweep-t">While you were away, Jack ran a sweep</span>' +
      (when ? '<span class="jsweep-w">' + esc(when) + '</span>' : '') +
      '<button class="jsweep-x" title="Dismiss this heads-up" ' +
        'onclick="if(window.DevConsole&&DevConsole.jackSweepSeen)DevConsole.jackSweepSeen(' + Number(f.id) + ', this)">Dismiss</button>' +
    '</div>' +
    '<div class="jsweep-head">' + esc(f.headline) + '</div>' +
    (f.detail ? '<div class="jsweep-detail">' + jackProse(f.detail) + '</div>' : '') +   // v136.58: drawn, not raw
    '<div class="jsweep-foot">Jack investigated read-only — drafting a fix is your call. ' +
      '<button class="jsweep-ask" onclick="if(window.DevConsole&&DevConsole.showView)DevConsole.showView(\'jack\')">Talk to Jack ↗</button>' +
    '</div>' +
  '</div>';
}

// Dismiss a sweep heads-up (marks it 'seen' server-side, removes the panel).
// Uses _jackCtx (worker URL + admin token) set in render().
export async function jackSweepSeen(id, btn) {
  try {
    if (btn) { btn.disabled = true; btn.textContent = 'Dismissing…'; }
    if (_jackCtx && typeof fetch !== 'undefined') {
      await fetch(_jackCtx.workerUrl + '/api/admin/jack-sweep-seen', { cache: 'no-store',
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + _jackCtx.token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(id) }),
      });
    }
    const el = (typeof document !== 'undefined') ? document.getElementById('jackSweep') : null;
    if (el) el.remove();
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Dismiss'; }
  }
}

// ── Compliance trend chart + table (week-on-week, by area) ──
// Reused by BOTH the always-on console card AND JACK's chat answer, so the
// graph Matt asked for renders identically whether he opens the card or asks
// Jack to "pull it up". Pure string builders (no deps), exported for unit
// tests. SVG strokes use concrete hex (they can't read CSS vars reliably —
// see ragColor); HTML text uses the dev.html dark-theme vars.
const _CT_PALETTE = ['#28e1ff', '#2ee6a6', '#ffc24b', '#ff5c7a', '#b78bff', '#6ee7ff', '#f5a3c7', '#9aa6b2'];

// 'YYYY-MM-DD' → 'd Mon' (e.g. '2026-06-08' → '8 Jun'); falls back to raw.
function _ctWeekLabel(ds) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ds || ''));
  if (!m) return String(ds || '');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return String(Number(m[3])) + ' ' + months[Number(m[2]) - 1];
}

// Round an axis max up to a clean number so gridlines read nicely.
function _ctNiceMax(v) {
  if (!(v > 0)) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return Math.max(4, step * pow);
}

// SVG line chart: x = weeks, one line per area = total compliance issues/week.
export function complianceTrendChartSvg(trend) {
  if (!trend || !Array.isArray(trend.weeks) || !trend.weeks.length) return '';
  const weeks = trend.weeks;
  const areas = (trend.areas || []).filter(a => a && Array.isArray(a.series));
  const n = weeks.length;
  const W = 560, H = 210, padL = 28, padR = 10, padT = 12, padB = 38;
  let maxY = 1;
  areas.forEach(a => a.series.forEach(s => { if ((s.total || 0) > maxY) maxY = s.total; }));
  const yMax = _ctNiceMax(maxY);
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const xAt = i => padL + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const yAt = v => padT + (1 - (v / yMax)) * innerH;
  let grid = '';
  [0, yMax / 2, yMax].forEach(v => {
    const y = yAt(v);
    grid += '<line x1="' + padL + '" y1="' + y.toFixed(1) + '" x2="' + (W - padR) + '" y2="' + y.toFixed(1) +
      '" stroke="#1b2a47" stroke-width="1"/>' +
      '<text x="' + (padL - 4) + '" y="' + (y + 3).toFixed(1) + '" fill="#7e92bd" font-size="9" text-anchor="end">' + Math.round(v) + '</text>';
  });
  let xlab = '';
  weeks.forEach((w, i) => {
    if (n > 8 && i % 2 === 1 && i !== n - 1) return; // thin out crowded axes
    xlab += '<text x="' + xAt(i).toFixed(1) + '" y="' + (H - padB + 16) + '" fill="#7e92bd" font-size="9" text-anchor="middle">' + esc(_ctWeekLabel(w)) + '</text>';
  });
  let lines = '', legend = '';
  areas.forEach((a, idx) => {
    const col = _CT_PALETTE[idx % _CT_PALETTE.length];
    const pts = a.series.map((s, i) => xAt(i).toFixed(1) + ',' + yAt(s.total || 0).toFixed(1)).join(' ');
    lines += '<polyline points="' + pts + '" fill="none" stroke="' + col + '" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>';
    a.series.forEach((s, i) => {
      lines += '<circle cx="' + xAt(i).toFixed(1) + '" cy="' + yAt(s.total || 0).toFixed(1) + '" r="2.4" fill="' + col + '"/>';
    });
    legend += '<span translate="no" style="display:inline-flex;align-items:center;gap:5px;margin-inline-end:12px;font-size:11px;color:var(--sub)">' +
      '<span style="width:11px;height:3px;border-radius:2px;background:' + col + ';display:inline-block"></span>' + esc(a.area_manager) + '</span>';
  });
  const svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet" role="img" ' +
    'aria-label="Compliance issues per week by area">' + grid + lines + xlab + '</svg>';
  return '<div style="margin-top:8px">' + svg + '<div style="margin-top:2px;line-height:1.9">' + legend + '</div></div>';
}

// Compact table: rows = areas, cols = weeks, cell = total; TOTAL row + Δ column
// (last − first; green if down/improving). Reads left-to-right like the chart.
export function complianceTrendTable(trend) {
  if (!trend || !Array.isArray(trend.weeks) || !trend.weeks.length) return '';
  const weeks = trend.weeks;
  const cellR = 'padding:3px 7px;border-bottom:1px solid var(--line);font-size:11px;text-align:end;white-space:nowrap';
  const cellL = cellR.replace('text-align:end', 'text-align:start');
  const headR = 'padding:3px 7px;border-bottom:1px solid var(--line);font-size:10px;color:var(--sub);text-align:end;text-transform:uppercase;letter-spacing:.4px';
  const headL = headR.replace('text-align:end', 'text-align:start');
  const thead = '<th style="' + headL + '">Area</th>' +
    weeks.map(w => '<th style="' + headR + '">' + esc(_ctWeekLabel(w)) + '</th>').join('') +
    '<th style="' + headR + '">&#916;</th>';
  const delta = (first, last) => {
    const d = last - first;
    const col = d < 0 ? 'var(--green)' : d > 0 ? 'var(--red)' : 'var(--sub)';
    return '<td style="' + cellR + ';color:' + col + ';font-weight:700">' + (d > 0 ? '+' : '') + d + '</td>';
  };
  let rows = '';
  (trend.areas || []).forEach(a => {
    const series = a.series || [];
    const first = series.length ? (series[0].total || 0) : 0;
    const last = series.length ? (series[series.length - 1].total || 0) : 0;
    rows += '<tr><td translate="no" style="' + cellL + ';color:var(--txt);font-weight:600">' + esc(a.area_manager) + '</td>' +
      series.map(s => '<td style="' + cellR + ';color:var(--txt)">' + (s.total || 0) + '</td>').join('') +
      delta(first, last) + '</tr>';
  });
  const tot = trend.totals || [];
  const tFirst = tot.length ? (tot[0].total || 0) : 0;
  const tLast = tot.length ? (tot[tot.length - 1].total || 0) : 0;
  const tD = tLast - tFirst;
  const topB = ';border-top:2px solid var(--line)';
  rows += '<tr><td style="' + cellL + ';color:var(--cyan);font-weight:800' + topB + '">All areas</td>' +
    tot.map(t => '<td style="' + cellR + ';color:var(--cyan);font-weight:700' + topB + '">' + (t.total || 0) + '</td>').join('') +
    '<td style="' + cellR + ';font-weight:800' + topB + ';color:' + (tD < 0 ? 'var(--green)' : tD > 0 ? 'var(--red)' : 'var(--sub)') + '">' + (tD > 0 ? '+' : '') + tD + '</td></tr>';
  return '<div style="overflow-x:auto;margin-top:8px"><table style="border-collapse:collapse;width:100%">' +
    '<thead><tr>' + thead + '</tr></thead><tbody>' + rows + '</tbody></table></div>';
}

// One-line direction caption — "Improving · 31 → 18 over 8 weeks".
export function complianceTrendCaption(trend) {
  const t = trend && trend.trend;
  if (!t || !trend.weeks || !trend.weeks.length) return '';
  const n = trend.weeks.length;
  const word = t.improving ? 'Improving' : t.worsening ? 'Worsening' : 'Flat';
  const col = t.improving ? 'var(--green)' : t.worsening ? 'var(--red)' : 'var(--amber)';
  return '<div style="font-size:12px;margin-top:2px">Direction: <b style="color:' + col + '">' + word + '</b> &middot; ' +
    (t.firstTotal || 0) + ' &rarr; ' + (t.lastTotal || 0) + ' total issues over ' + n + ' week' + (n === 1 ? '' : 's') +
    ' <span style="color:var(--sub)">(log + checklist misses, late clock-ins, break issues; completed weeks only)</span></div>';
}

// Full inner block (caption + chart + table) or an honest empty state. Used by
// the console card (fetched data) and JACK's chat action card (tool result).
export function complianceTrendInner(trend) {
  if (!trend) return '<div style="font-size:12px;color:var(--sub);margin-top:6px">Compliance trend unavailable right now.</div>';
  if (!Array.isArray(trend.weeks) || !trend.weeks.length) {
    return '<div style="font-size:12px;color:var(--sub);margin-top:6px">Not enough completed weeks yet to show a trend.</div>';
  }
  return complianceTrendCaption(trend) + complianceTrendChartSvg(trend) + complianceTrendTable(trend);
}

// ── card renderer ───────────────────────────────────────────
// a = { rag, name, type, chip:'LIVE'|'PLANNED'|'NEXT', barLabel, barPct,
//       barTone:'green'|'amber'|'cyan', meta:[[k,v]...], trend:html,
//       done:[], todo:[], planned:false }
function card(a) {
  const planned = a.planned ? ' planned' : '';
  const chipCls = a.chip === 'LIVE' ? 'live' : 'plan';
  const meta = (a.meta || [])
    .map(kv => '<span>' + esc(kv[0]) + ' <b>' + esc(kv[1]) + '</b></span>').join('');
  const bar = (typeof a.barPct === 'number')
    ? '<div class="a-barwrap">' +
        '<div class="a-barhead"><span>' + esc(a.barLabel || '') + '</span><span>' + a.barPct + '%</span></div>' +
        '<div class="a-bar"><div class="a-fill ' + (a.barTone || 'cyan') + '" style="width:' + a.barPct + '%"></div></div>' +
      '</div>'
    : '';
  const cols = (a.done || a.todo)
    ? '<details><summary>DRILL IN</summary><div class="a-cols">' +
        '<div><div class="a-colh">' + (a.planned ? 'PLANNED' : 'DONE') + '</div><ul class="a-b ' + (a.planned ? 'todo' : 'done') + '">' +
          (a.done || []).map(x => '<li>' + esc(x) + '</li>').join('') + '</ul></div>' +
        '<div><div class="a-colh">' + (a.planned ? 'NEEDS FIRST' : 'OUTSTANDING') + '</div><ul class="a-b todo">' +
          (a.todo || []).map(x => '<li>' + esc(x) + '</li>').join('') + '</ul></div>' +
      '</div></details>'
    : '';
  return '<div class="a-card' + planned + '"' + (a.domId ? ' id="' + a.domId + '"' : '') + '>' +
    '<div class="a-crow"><span class="a-dot ' + ragMod(a.rag) + (a.pulse ? ' a-pulse' : '') + '"' + ragDotAttrs(a.rag) + '></span>' +
      '<span class="a-title">' + esc(a.name) + '</span>' +
      '<span class="a-chip ' + chipCls + '">' + esc(a.chip) + '</span></div>' +
    '<div class="a-type">' + esc(a.type) + '</div>' +
    bar +
    (meta ? '<div class="a-meta">' + meta + '</div>' : '') +
    (a.note ? '<div class="a-note">' + esc(a.note) + '</div>' : '') +
    (a.trend || '') +
    cols +
    (a.actionsHtml || '') +
  '</div>';
}

// ── login welcome banner ────────────────────────────────────
// "Welcome Matt — here is your summary" + what the agents did since the
// last visit. Falls back to a steady-state line on a first visit or when
// nothing is new, so the banner never looks broken/empty.
function buildWelcome(name, lastLogin, w) {
  const items = [];
  if (w.triagesSince > 0) {
    let s = '<b>' + w.triagesSince + '</b> bug report' + (w.triagesSince === 1 ? '' : 's') + ' triaged';
    const extra = [];
    if (w.fixSince > 0) extra.push(w.fixSince + ' fix' + (w.fixSince === 1 ? '' : 'es') + ' proposed');
    if (w.clarifySince > 0) extra.push(w.clarifySince + ' needing more info');
    if (extra.length) s += ' <span class="a-welcome-dim">(' + extra.join(', ') + ')</span>';
    items.push(s);
  }
  if (w.feedbackSince > 0) {
    items.push('<b>' + w.feedbackSince + '</b> new feedback report' +
      (w.feedbackSince === 1 ? '' : 's') + ' from the floor');
  }
  const since = lastLogin ? new Date(lastLogin) : null;
  const sinceLabel = since && !isNaN(since.getTime())
    ? since.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) +
      ' ' + since.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;
  const lead = lastLogin
    ? 'Since your last visit · ' + esc(ago(lastLogin)) +
      (sinceLabel ? ' <span class="a-welcome-dim">(' + esc(sinceLabel) + ')</span>' : '')
    : 'Current state of your agents';
  let body;
  if (w.unreachable) {
    // Don't pretend all-is-well with fake zeros when the fetch actually failed.
    body = '<div class="a-welcome-err">⚠ Couldn\'t load your summary — ' + esc(w.unreachable.msg) + '</div>';
  } else if (items.length) {
    body = '<ul class="a-welcome-list">' + items.map(x => '<li>' + x + '</li>').join('') + '</ul>';
  } else {
    body = '<p class="a-welcome-quiet">No new agent activity since you were last here. ' +
      '<b>' + w.totalTriaged + '</b> report' + (w.totalTriaged === 1 ? '' : 's') + ' triaged all-time · ' +
      '<b>' + (w.openFeedback == null ? '—' : w.openFeedback) + '</b> open feedback item' +
      (w.openFeedback === 1 ? '' : 's') + ' · system ' + esc(w.overallLabel) + '.</p>';
  }
  // "Mark as seen" clears the since-window deliberately (only meaningful when
  // there's a baseline to clear). Calls the dev-console host; this module is
  // loaded only by dev.html, so the coupling is safe.
  const seen = lastLogin
    ? '<button class="a-welcome-seen" onclick="if(window.DevConsole&&DevConsole.markSeen)DevConsole.markSeen()">✓ Mark as seen</button>'
    : '';
  // JACK — the console's voice. Replay reads the headline aloud; the toggle
  // mutes/unmutes the auto-greet (persisted in localStorage). Buttons call the
  // Agents global (this module's exports).
  const jackCtl = '<div class="a-jack">' +
    '<span class="a-jack-id"><span class="a-jack-dot"></span>JACK online</span>' +
    '<button class="a-jack-btn" title="Read the summary aloud" onclick="if(window.Agents&&Agents.jackReplay)Agents.jackReplay()">🔊 Replay</button>' +
    '<button class="a-jack-btn" id="jackMuteBtn" title="Mute / unmute JACK\'s greeting" onclick="if(window.Agents&&Agents.jackToggleMute)Agents.jackToggleMute()">' +
      (_jackMuted() ? '🔇 JACK muted' : '🔈 JACK voice on') + '</button>' +
    // v120.6 — Rung ③: enable native push alerts on this device + a test push.
    '<button class="a-jack-btn" id="pushEnableBtn" title="Get a phone notification when prod errors spike — tap once on each device" onclick="if(window.Agents&&Agents.enablePushAlerts)Agents.enablePushAlerts(this)">🔔 Enable alerts</button>' +
    // v127.5 (Matt-approved dev-tab review): the two send-test buttons left the
    // main surface — engineering scaffolding. The FUNCTIONS stay exported
    // (Agents.testPushAlert / Agents.testPushPayload, console-callable): they
    // are the only manual webpush-encryption test rig and tests pin them.
    // v120.7 — Rung ③: download a full JSON backup of the database (read-only).
    '<button class="a-jack-btn" title="Download a full JSON backup of the database to this device" onclick="if(window.Agents&&Agents.downloadBackup)Agents.downloadBackup(this)">💾 Backup</button>' +
    // v120.8 — Rung ③: restore drill. STAGING ONLY — never shown on prod (the
    // Worker also hard-blocks it). Lets Matt prove a backup is restorable.
    (_isStagingConsole()
      ? '<button class="a-jack-btn" title="STAGING ONLY: load a backup file back in to prove it restores" onclick="if(window.Agents&&Agents.restoreBackup)Agents.restoreBackup(this)">🔁 Restore (staging)</button>'
      : '') +
  '</div>';
  return '<div class="a-welcome">' +
    '<div class="a-welcome-hi">Welcome ' + esc(name || 'Matt') + ' — here is your summary</div>' +
    '<div class="a-welcome-sub">' + lead + '</div>' +
    body +
    seen +
    jackCtl +
  '</div>';
}

// ── Senior-Dev Advisor ──────────────────────────────────────
// A REAL, deterministic "state of the app" synthesis. It reads the
// same live signals the dashboard already holds (triage activity +
// feedback themes + each live agent's RAG) and turns them into a
// 0-100 ops-health score (with a "how this is calculated" breakdown), a
// plain-English read, and the single next best step. No AI call, no
// guesswork — every number is computed, so it holds up under "show me".
// Score breakdown — exposed so the panel can show HOW the 0-100 number is
// made (the "How this is calculated" drill-in), not just the final figure.
function advisorBreakdown(d) {
  // Triage cleanliness (share of runs that didn't error). Neutral 70
  // before any runs exist, so a fresh install isn't punished.
  const triageScore = d.triageTotal ? d.triageHealth : 70;
  // Feedback pressure: each open item shaves points, capped so one
  // busy week can't zero the score on its own.
  const openPenalty = Math.min((d.totalOpen || 0) * 6, 60);
  const feedbackScore = 100 - openPenalty;
  // Coverage: share of live agents currently healthy (green).
  const coverageScore = Math.round(100 * d.greenLive / Math.max(d.liveTotal, 1));
  // Weighted contributions, each rounded so the drill-in always adds up to
  // the headline score (no "the parts don't sum" confusion for a sceptic).
  const cTriage = Math.round(triageScore * 0.45);
  const cFeedback = Math.round(feedbackScore * 0.30);
  const cCoverage = Math.round(coverageScore * 0.25);
  const score = Math.max(0, Math.min(100, cTriage + cFeedback + cCoverage));
  return { score, triageScore, feedbackScore, coverageScore, openPenalty, cTriage, cFeedback, cCoverage };
}

function advisorScore(d) { return advisorBreakdown(d).score; }

// Single prioritised recommendation — most-urgent signal wins.
function advisorNextStep(d) {
  if (d.triageTotal && d.triageErrors / d.triageTotal > 0.15) {
    return 'Stabilise the Bug-Triage agent — ' + d.triageErrors + ' of ' + d.triageTotal + ' runs errored.';
  }
  if (d.topTheme && d.topThemeOpen >= 3) {
    return 'Clear the ' + d.topTheme + ' backlog — ' + d.topThemeOpen + ' open reports, your top theme.';
  }
  if ((d.totalOpen || 0) > 0) {
    return 'Work the open feedback — ' + d.totalOpen + ' item' + (d.totalOpen === 1 ? '' : 's') + ' waiting across screens.';
  }
  if (!d.triageTotal) {
    return 'Run the Bug-Triage agent on a real report to start building the activity log.';
  }
  return 'Close remaining test debt and prep the staging → prod promotion.';
}

function buildAdvisor(d) {
  // If the data couldn't be read, an ops-health score would be a fiction.
  if (d.unreachable) {
    return '<div class="a-sect">Senior-Dev Advisor</div>' +
      '<div class="adv" id="advPanel">' +
        '<div class="adv-top">' +
          '<div class="adv-scorebox"><div class="adv-num red">—</div>' +
            '<div class="adv-grade">data unreachable</div></div>' +
          '<div class="adv-body">' +
            '<div class="adv-read">' + esc(d.unreachable.msg) + '</div>' +
            '<div class="adv-next"><span class="adv-next-k">NEXT BEST STEP</span>' +
              (d.unreachable.auth ? 'Sign out and back in to refresh your session.' : 'Check the Worker is up, then reload.') +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }
  const b = advisorBreakdown(d);
  const score = b.score;
  // Tone now matches the grade bands (green ≥70, amber ≥50, red below).
  const tone = score >= 70 ? 'green' : score >= 50 ? 'amber' : 'red';
  const grade = score >= 85 ? 'Strong' : score >= 70 ? 'Healthy' : score >= 50 ? 'Watch' : 'Needs work';
  const open = d.totalOpen || 0;
  const openWord = open + ' open feedback item' + (open === 1 ? '' : 's');
  const narrative = d.triageTotal
    ? d.greenLive + ' of ' + d.liveTotal + ' live agents healthy · Bug-Triage ' +
        d.triageHealth + '% clean over ' + d.triageTotal + ' run' + (d.triageTotal === 1 ? '' : 's') +
        ' · ' + openWord + '.'
    : d.greenLive + ' of ' + d.liveTotal + ' live agents healthy · Bug-Triage idle · ' + openWord + '.';

  const obs = [];
  if (d.triageTotal) {
    obs.push('Bug-Triage: ' + d.triageFixes + ' fix' + (d.triageFixes === 1 ? '' : 'es') +
      ' proposed, ' + d.triageClarify + ' awaiting info · ' + d.triageHealth + '% clean.');
  } else {
    obs.push('Bug-Triage: idle — no runs recorded yet.');
  }
  if (d.insightsLive) {
    obs.push('Feedback Insights: ' + open + ' open across ' + d.liveScreens +
      ' screen' + (d.liveScreens === 1 ? '' : 's') + (d.topTheme ? ', top is ' + d.topTheme : '') + '.');
  }
  obs.push(d.greenLive + ' of ' + d.liveTotal + ' live agents healthy.');
  const obsHtml = obs.slice(0, 3).map(x => '<li>' + esc(x) + '</li>').join('');

  // "How this is calculated" — makes the score auditable. Each row: the
  // component's 0-100 score, its weight, and the points it contributed.
  const calcRow = (label, raw, weight, contrib, detail) =>
    '<li><div class="adv-calc-h"><span class="adv-calc-k">' + esc(label) + '</span>' +
      '<span class="adv-calc-v">' + raw + ' × ' + weight + '% = <b>' + contrib + '</b></span></div>' +
      '<div class="adv-calc-d">' + esc(detail) + '</div></li>';
  const triageDetail = d.triageTotal
    ? d.triageHealth + '% of ' + d.triageTotal + ' triage run' + (d.triageTotal === 1 ? '' : 's') + ' ran clean'
    : 'no triage runs yet — neutral 70';
  const feedbackDetail = open + ' open report' + (open === 1 ? '' : 's') + ' → −' + b.openPenalty + ' points';
  const coverageDetail = d.greenLive + ' of ' + d.liveTotal + ' live agents green';
  const drill =
    '<details class="adv-drill"><summary>How this is calculated</summary>' +
      '<ul class="adv-calc">' +
        calcRow('Triage cleanliness', b.triageScore, 45, b.cTriage, triageDetail) +
        calcRow('Feedback pressure', b.feedbackScore, 30, b.cFeedback, feedbackDetail) +
        calcRow('Agent coverage', b.coverageScore, 25, b.cCoverage, coverageDetail) +
      '</ul>' +
      '<div class="adv-calc-total">' + b.cTriage + ' + ' + b.cFeedback + ' + ' + b.cCoverage +
        ' = <b>' + score + '</b> ops health</div>' +
    '</details>';

  return '<div class="a-sect">Senior-Dev Advisor</div>' +
    '<div class="adv" id="advPanel">' +
      '<div class="adv-top">' +
        '<div class="adv-scorebox">' +
          '<div class="adv-num ' + tone + '">' + score + '</div>' +
          '<div class="adv-grade">' + esc(grade) + ' · ops health</div>' +
        '</div>' +
        '<div class="adv-body">' +
          '<div class="adv-read">' + esc(narrative) + '</div>' +
          '<div class="adv-bar"><div class="adv-fill ' + tone + '" style="width:' + score + '%"></div></div>' +
          '<div class="adv-next"><span class="adv-next-k">NEXT BEST STEP</span>' + esc(advisorNextStep(d)) + '</div>' +
        '</div>' +
      '</div>' +
      '<ul class="adv-obs">' + obsHtml + '</ul>' +
      drill +
      '<div class="adv-foot">Computed live from triage + feedback signals — no AI guesswork. ' +
        'Next: a release panel &amp; cron heartbeats on this screen.</div>' +
    '</div>';
}

// ── Morning Brief card RETIRED (v127.6, K2 of the Matt-approved dev-tab
// review): it duplicated the admin Dashboard/Brief sales-vs-target view.
// JACK's SPOKEN morning brief (the get_morning_brief tool) and the
// /api/admin/morning-brief endpoint are untouched — admin + JACK use them.
// ── Dev Agent ───────────────────────────────────────────────
// The bridge from SENSE → ACT. Reads every watcher's findings and turns
// them into ONE prioritised to-do list, each item with a concrete next
// step + a button. It RECOMMENDS; it does NOT act — code issues link to
// the run, operational issues are advisory only (it never touches stores
// or pay). Deterministic, no AI.
function buildDevAgent(d) {
  const actions = [];
  // — CODE: the junior dev can DRAFT a fix on your click (→ PR you review) —
  if (d.topBug) {
    actions.push({ sev: 'med', issue: 'Newest open report: "' + d.topBug.title + '"',
      todo: 'Hand it to the junior dev — drafts a fix via the triage agent, opens a PR for you.',
      btn: { label: '⚒ Fix this', fix: { source: 'feedback', id: d.topBug.id } } });
  }
  // — CODE (link to the run; a human acts) —
  if (d.qaFailing) {
    actions.push({ sev: 'high',
      issue: d.qaSummary ? 'CI is failing — ' + d.qaSummary : 'CI is failing — the latest test run did not pass.',
      todo: 'Fix it before the next staging → prod promotion.',
      btn: d.ciUrl ? { label: 'View run', href: d.ciUrl } : { label: 'Open QA', drill: '#card-qa' } });
  }
  if (d.secFailing) {
    actions.push({ sev: 'high', issue: 'The Security Agent flagged a risk in a recent PR.',
      todo: 'Review the finding; rotate anything exposed.',
      btn: d.secUrl ? { label: 'View scan', href: d.secUrl } : { label: 'Open Security', drill: '#card-security' } });
  }
  if (d.triageErrRate) {
    actions.push({ sev: 'med', issue: 'Bug-Triage errored on ' + d.triageErrors + ' of ' + d.triageTotal + ' runs.',
      todo: 'Stabilise the triage agent (check Worker logs / model).',
      btn: { label: 'Open Bug-Triage', drill: '#card-bug-triage' } });
  }
  // — OPS (recommend only — never auto-acts on stores/pay) —
  if (d.compCore > d.compDays) {
    actions.push({ sev: 'med',
      issue: d.compCore + ' checklist/log/never-signed-in misses in 7 days' +
        (d.compWorstArea && d.compWorstArea !== '—' ? ' (worst: ' + d.compWorstArea + ')' : '') + '.',
      todo: 'Nudge the worst area manager to clear the backlog.',
      btn: { label: 'Open Compliance', drill: '#card-compliance' } });
  }
  if ((d.attLate + d.attBreak) > d.compDays) {
    actions.push({ sev: 'med', issue: d.attLate + ' late clock-ins + ' + d.attBreak + ' break issues in 7 days.',
      todo: 'Review pay-affecting anomalies before payroll close.',
      btn: { label: 'Open Compliance', drill: '#card-compliance' } });
  }
  if ((d.rotaGaps || 0) > 0 || (d.rotaDoubles || 0) > 0) {
    const bits = [];
    if (d.rotaGaps) bits.push(d.rotaGaps + ' store-day' + (d.rotaGaps === 1 ? '' : 's') + ' open with no one scheduled');
    if (d.rotaDoubles) bits.push(d.rotaDoubles + ' double-booking' + (d.rotaDoubles === 1 ? '' : 's'));
    actions.push({ sev: d.rotaGaps ? 'high' : 'med',
      issue: 'This week\'s rota: ' + bits.join(' + ') + '.',
      todo: 'Review coverage before the week starts (advisory — fix in the rota tool).',
      btn: { label: 'Open Rota Check', drill: '#card-rota-check' } });
  }
  if ((d.dfStale || 0) > 0) {
    actions.push({ sev: d.dfErr ? 'med' : 'low',
      issue: d.dfStale + ' data feed' + (d.dfStale === 1 ? '' : 's') + ' stale or missing' +
        (d.dfFirstStale ? ' (oldest: ' + d.dfFirstStale + ')' : '') + '.',
      todo: 'Upload the missing feed before reports/rota depend on it (advisory).',
      btn: { label: 'Open Data Freshness', drill: '#card-data-freshness' } });
  }
  if ((d.smokeFail || 0) > 0) {
    actions.push({ sev: 'high',
      issue: d.smokeFail + ' data integrity check' + (d.smokeFail === 1 ? '' : 's') + ' failing' +
        (d.smokeFirstBad ? ' (' + d.smokeFirstBad + ')' : '') + '.',
      todo: 'Open the Smoke panel and fix the data before it hits pay or rota.',
      btn: { label: 'Open Smoke', view: 'smoke' } });
  }
  if ((d.totalOpen || 0) > 0) {
    actions.push({ sev: 'low',
      issue: d.totalOpen + ' open feedback item' + (d.totalOpen === 1 ? '' : 's') + (d.topTheme ? ', top: ' + d.topTheme : '') + '.',
      todo: d.topTheme ? 'Work the ' + d.topTheme + ' backlog first.' : 'Work the open feedback.',
      btn: { label: 'Open Feedback', view: 'feedback' } });
  }
  const rank = { high: 3, med: 2, low: 1 };
  actions.sort((a, b) => rank[b.sev] - rank[a.sev]);
  const top = actions.slice(0, 5);
  const sevDot = (sev) => sev === 'high' ? 'r' : sev === 'med' ? 'a' : 'x';
  const btnHtml = (b) => {
    if (!b) return '';
    if (b.href)  return '<a class="dq-go" href="' + esc(b.href) + '" target="_blank" rel="noopener">' + esc(b.label) + ' ↗</a>';
    if (b.drill) return '<button class="dq-go" onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'' + esc(b.drill) + '\')">' + esc(b.label) + '</button>';
    if (b.view)  return '<button class="dq-go" onclick="if(window.DevConsole&&DevConsole.showView)DevConsole.showView(\'' + esc(b.view) + '\')">' + esc(b.label) + '</button>';
    // The one ACTION button: hand a specific bug to the junior dev to fix.
    if (b.fix)   return '<button class="dq-go dq-fix" onclick="if(window.DevConsole&&DevConsole.fixBug)DevConsole.fixBug(\'' + esc(b.fix.source) + '\',' + (String(b.fix.id).replace(/[^0-9]/g, '') || '0') + ')">' + esc(b.label) + '</button>';
    return '';
  };
  const body = top.length
    ? '<ul class="dq-list">' + top.map(a =>
        '<li class="dq-item">' +
          '<span class="a-dot ' + sevDot(a.sev) + '"></span>' +
          '<div class="dq-text"><div class="dq-issue">' + esc(a.issue) + '</div>' +
            '<div class="dq-todo">' + esc(a.todo) + '</div></div>' +
          btnHtml(a.btn) +
        '</li>').join('') + '</ul>'
    : '<div class="dq-clear">✓ Nothing needs your attention right now. Next: ' + esc(d.nextStep || 'keep shipping.') + '</div>';
  // EVERY raised issue is fixable — not just the newest. Each ⚒ Fix hands that
  // specific report to the junior dev (triage agent) → drafts a fix → PR.
  const fixId = (id) => String(id).replace(/[^0-9]/g, '') || '0';
  const issuesHtml = (d.openBugs && d.openBugs.length)
    ? '<div class="dq-sub">Raised issues — hand any to the junior dev</div>' +
      '<ul class="dq-issues">' + d.openBugs.map(b =>
        '<li class="dq-issue-row">' +
          '<div class="dq-issue-t">' + esc(b.title) +
            (b.screen ? ' <span class="dq-issue-s">' + esc(b.screen) + '</span>' : '') + '</div>' +
          '<button class="dq-go dq-fix" onclick="if(window.DevConsole&&DevConsole.fixBug)DevConsole.fixBug(\'feedback\',' + fixId(b.id) + ')">⚒ Fix</button>' +
        '</li>').join('') + '</ul>'
    : '';
  return '<div class="a-sect">Dev Agent</div>' +
    '<div class="dq" id="devAgentPanel">' +
      '<div class="dq-lead">Turns every watcher\'s findings into a prioritised to-do. It <b>recommends</b> — you decide. ' +
        'On any raised issue, <b>⚒ Fix</b> hands it to the junior dev to draft a fix → PR for your review. ' +
        'Operational issues stay advisory — it never acts on stores or pay.</div>' +
      body +
      issuesHtml +
    '</div>';
}

// ── JACK headline — the one spoken line ─────────────────────
// Pure: turns the same numbers the banner shows into a short sentence JACK
// reads aloud. Kept tight so it sounds like a brief, not a paragraph.
export function jackHeadline(d) {
  d = d || {};
  const name = d.name || 'Matt';
  // Honesty first: if the data couldn't be read, say so — don't read a brief
  // built from blanks.
  if (d.unreachable) {
    return 'Welcome back, ' + name + '. I could not reach your agent data. ' +
      String(d.unreachable.msg || '').replace(/\s+/g, ' ').trim();
  }
  const bits = ['Welcome back, ' + name + '.'];
  const since = [];
  if (d.triagesSince > 0) since.push(d.triagesSince + ' report' + (d.triagesSince === 1 ? '' : 's') + ' triaged');
  if (d.feedbackSince > 0) since.push(d.feedbackSince + ' new feedback item' + (d.feedbackSince === 1 ? '' : 's'));
  if (since.length) bits.push('Since your last visit, ' + since.join(' and ') + '.');
  const healthWord = d.overallLabel === 'GOOD' ? 'good'
    : d.overallLabel === 'WATCH' ? 'worth watching' : 'needs attention';
  bits.push(typeof d.score === 'number'
    ? 'System health is ' + healthWord + ', scoring ' + d.score + ' out of 100.'
    : 'System health is ' + healthWord + '.');
  if (d.integrityFail > 0) {
    bits.push('Heads up. ' + d.integrityFail + ' data integrity check' + (d.integrityFail === 1 ? '' : 's') +
      ' ' + (d.integrityFail === 1 ? 'is' : 'are') + ' failing.');
  }
  if (d.nudge) bits.push('One thing trending. ' + String(d.nudge).replace(/\s+/g, ' ').trim());
  if (d.nextStep) bits.push('Your next best step. ' + String(d.nextStep).replace(/\s+/g, ' ').trim());
  return bits.join(' ');
}

// ── System map — the "hybrid charging system" view ──────────
// A live SVG of the real architecture. Every node's colour is COMPUTED from a
// real signal (no decorative health), so it holds up under "show me".
// v134.6: the detailed map (people, Netlify, Cloudflare, Supabase, the CS
// worker and every outside service); see buildSystemMap below for its input.
// v133.123: the staging Worker's public version, read by the browser (the prod Worker cannot fetch it).
export const STAGING_VERSION_URL = 'https://elcapp-shift-staging.matt-kiernan86.workers.dev/api/version';
// Pure: the release verdict once both versions are known.
export function releaseVerdict(prodVersion, stagingVersion) {
  if (!prodVersion || !stagingVersion) return { key: 'unknown', text: 'cannot compare — one end unread' };
  return prodVersion === stagingVersion ? { key: 'sync', text: 'in sync ✓' } : { key: 'differs', text: 'differs — staging awaits promotion' };
}
function fillStagingFromBrowser(prodVersion, tries) {
  if (typeof document === 'undefined' || typeof fetch === 'undefined') return;
  const el = document.getElementById('relStaging');
  if (!el) { if (tries < 3) setTimeout(function () { fillStagingFromBrowser(prodVersion, tries + 1); }, 400); return; }
  fetch(STAGING_VERSION_URL + '?t=' + Date.now(), { cache: 'no-store' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (d) {
      if (!d || !d.version) {
        // v134.6: the browser could not read it either, so this is a real "watch", not the platform limit.
        _patchMapNode('workerStaging', 'a', 'unreadable', 'Neither the live Worker nor this browser could read the staging Worker\'s version.');
        return;
      }
      _patchMapNode('workerStaging', 'g', d.version + (prodVersion && prodVersion !== d.version ? ' · awaits promo' : prodVersion ? ' · in sync' : ''),
        'Staging Worker answers with ' + d.version + '. Read by this browser: the live Worker cannot call the staging Worker directly.');
      el.textContent = d.version;
      el.title = 'Read by this browser: the live Worker cannot call the staging Worker directly';
      const v = releaseVerdict(prodVersion, d.version);
      const vEl = document.getElementById('relVerdict');
      if (vEl) {
        vEl.textContent = v.text;
        vEl.style.fontWeight = '700';
        vEl.style.color = v.key === 'sync' ? 'var(--green)' : v.key === 'differs' ? 'var(--amber)' : 'var(--sub)';
      }
    })
    .catch(function () {
      /* the release line stays "unreachable", with its reason on hover */
      _patchMapNode('workerStaging', 'a', 'unreadable', 'Neither the live Worker nor this browser could reach the staging Worker.');
    });
}

// v133.123: a check name short enough for a map node (about 24 characters).
export function shortCheck(name) {
  const n = String(name || '').replace(/\s*\(.*?\)\s*/g, ' ').trim();
  return n.length > 24 ? n.slice(0, 23).trim() + '…' : n;
}

// v134.6: the APP_VERSION inside the site's own /version.js (self.APP_VERSION = 'v134.2';), or null.
export function parseFrontVersion(text) {
  const m = String(text || '').match(/APP_VERSION\s*=\s*['"]([^'"]+)['"]/);
  return m ? m[1] : null;
}

// v134.6: the DETAILED live system map (Matt liked the static hosting diagram; this is that diagram, live).
// The rule is unchanged: every node's colour is computed live from the stack, not decorative. A node the
// console has no live signal for is grey "not checked", NEVER a guessed green. Input:
//   s = { <node id>: { rag, sub, tip, go } }   rag 'g' | 'a' | 'r'; anything else, or a missing node, is grey.
// tip is the hover <title>; go is a card to open on click (DevConsole.drillTo).
const SM_W = 148, SM_H = 44;
export const SYSMAP_GROUPS = [
  { label: 'PEOPLE', x: 8, y: 70, w: 160, h: 490 },
  { label: 'NETLIFY', x: 186, y: 70, w: 160, h: 400 },
  { label: 'OTHER ACCOUNT', x: 186, y: 482, w: 160, h: 78 },
  { label: 'CLOUDFLARE', x: 364, y: 70, w: 340, h: 400 },
  { label: 'SUPABASE', x: 718, y: 70, w: 176, h: 400 },
  { label: 'OUTSIDE SERVICES', x: 922, y: 70, w: 176, h: 490 },
];
// why = what the console would need to read it; shown on hover whenever the node is grey.
export const SYSMAP_NODES = [
  { id: 'staff', label: 'Store staff', x: 88, y: 150, why: 'Read from the "Recent clock activity" integrity check, which did not run this load.' },
  { id: 'managers', label: 'Managers + HO', x: 88, y: 210, why: 'Read from the "Area manager coverage" integrity check, which did not run this load.' },
  { id: 'players', label: 'Virtual Store', x: 88, y: 270, why: 'The console has no live read of Virtual Store play.' },
  { id: 'customers', label: 'Customers', x: 88, y: 400, why: 'The console has no live read of customer traffic.' },
  { id: 'site', label: 'Site · live', x: 266, y: 210, why: 'elcapp-shift on Netlify. Read only when this console is served from it (the browser reads /version.js).' },
  { id: 'feedbackSite', label: 'elctoysfeedback.com', x: 266, y: 310, why: 'The customer feedback platform on Netlify. The console has no live read of it.' },
  { id: 'siteStaging', label: 'Site · staging', x: 266, y: 420, why: 'elcapp-shift-staging on Netlify. Read only when this console is served from it.' },
  { id: 'cs', label: 'WhatsApp CS', x: 266, y: 528, why: 'elcapp-cs lives on a separate Cloudflare account. Only JACK reads it (get_cs_status), on request; the console does not.' },
  { id: 'worker', label: 'Worker · live', x: 448, y: 210, why: 'The live Worker (elcapp-shift) was not read this load.' },
  { id: 'workerStaging', label: 'Worker · staging', x: 448, y: 420, why: 'The staging Worker (elcapp-shift-staging) was not read this load.' },
  { id: 'nightly', label: '3am train', x: 620, y: 120, why: 'No cron heartbeat was read this load (release-state unreachable).' },
  { id: 'hourly', label: 'Version watchdog', x: 620, y: 176, why: 'The hourly prod-watch run stamps no heartbeat; only its failure memory is readable, and it holds no failures.' },
  { id: 'r2', label: 'R2 media + mail', x: 620, y: 266, why: 'The console has no live read of the R2 buckets (media, mail and the weekly backup copy).' },
  { id: 'jack', label: 'Jack worker', x: 620, y: 322, why: 'Reached through the JACK service binding; the console does not call it.' },
  { id: 'db', label: 'Database · live', x: 806, y: 210, why: 'The live Supabase database was not read this load.' },
  { id: 'dbStaging', label: 'Database · staging', x: 806, y: 420, why: 'The staging Supabase database is read only from the staging console.' },
  { id: 'anthropic', label: 'Anthropic Claude', x: 1012, y: 114, why: 'Read from the bug-triage runs; there were none to read this load.' },
  { id: 'resend', label: 'Resend email', x: 1012, y: 161, why: 'Read from the email-delivery integrity check and the weekly email record; neither said anything this load.' },
  { id: 'push', label: 'Web Push', x: 1012, y: 208, why: 'Read from the push integrity checks, which did not run this load.' },
  // v136.65 (Matt, 27 Oct 2026: "cubes and amanah are 2 seperate data sources right?"): two nodes, each read from
  // the ingest health of its own push feeds (feedHealthStatus, a late read), no longer the Data Freshness agent.
  { id: 'cubes', label: 'Cubes sales', x: 1012, y: 255, why: 'The ingest health of the Cubes sales and Top 50 pushes was not read this load.' },
  { id: 'amanah', label: 'Amanah HR', x: 1012, y: 302, why: 'The ingest health of the Amanah staff push was not read this load.' },
  { id: 'github', label: 'GitHub CI + triage', x: 1012, y: 349, why: 'Read from the QA agent\'s CI history, which was unreachable.' },
  { id: 'elevenlabs', label: 'ElevenLabs voice', x: 1012, y: 396, why: 'The console has no live read of ElevenLabs. JACK falls back to the browser voice when it fails.' },
  { id: 'places', label: 'Google Places', x: 1012, y: 443, why: 'The console has no live read of Google Places (the Google reviews reporting).' },
  { id: 'jitsi', label: 'Jitsi video', x: 1012, y: 490, why: 'Calls run on meet.jit.si between browsers; the console has no live read of it.' },
  { id: 'quickchart', label: 'quickchart', x: 1012, y: 537, why: 'Email charts are drawn by quickchart.io; the console has no live read of it.' },
];
// [from, to, dep]: each line takes the colour of the node whose health it depends on.
export const SYSMAP_FLOWS = [
  ['staff', 'site', 'site'], ['managers', 'site', 'site'], ['players', 'site', 'site'],
  ['customers', 'feedbackSite', 'feedbackSite'], ['customers', 'cs', 'cs'],
  ['site', 'worker', 'worker'], ['feedbackSite', 'worker', 'worker'], ['cs', 'worker', 'cs'],
  ['siteStaging', 'workerStaging', 'workerStaging'],
  ['worker', 'db', 'db'], ['workerStaging', 'dbStaging', 'dbStaging'],
  ['worker', 'nightly', 'nightly'], ['worker', 'hourly', 'hourly'], ['worker', 'r2', 'r2'], ['worker', 'jack', 'jack'],
];
// Everything in the outside column hangs off one bus from the live Worker.
const SM_OUTSIDE = ['anthropic', 'resend', 'push', 'cubes', 'amanah', 'github', 'elevenlabs', 'places', 'jitsi', 'quickchart'];
const SM_BUS_X = 908, SM_BUS_Y = 44;

function _smFit(t) {
  const v = String(t == null ? '' : t);
  return v.length > 24 ? v.slice(0, 23).trim() + '…' : v;
}
// Pure: normalise the inputs into one { rag, sub, tip, go } per node; anything without a real rag is grey.
function _smSignals(s) {
  s = s || {};
  const out = {};
  SYSMAP_NODES.forEach((n) => {
    const v = s[n.id] || null;
    const rag = v && (v.rag === 'g' || v.rag === 'a' || v.rag === 'r') ? v.rag : 'x';
    const sub = (v && v.sub) || (rag === 'x' ? 'not checked' : '');
    out[n.id] = {
      rag, sub,
      tip: (v && v.tip) || (rag === 'x' ? 'Not checked. ' + n.why : n.label + ': ' + sub),
      go: (v && v.go) || null,
    };
  });
  return out;
}
// Pure: how many nodes sit in each state (the cockpit's one-line summary).
export function systemMapTally(s) {
  const sig = _smSignals(s);
  const t = { g: 0, a: 0, r: 0, x: 0 };
  Object.keys(sig).forEach((k) => { t[sig[k].rag]++; });
  return t;
}
// Pure: one integrity (smoke) check -> a node signal. A failing check is named on the node (the v133.123 rule).
export function checkNode(c, okSub, watchSub) {
  if (!c || !c.status) return null;
  const st = c.status;
  const rag = st === 'pass' ? 'g' : st === 'fail' ? 'r' : (st === 'info' || st === 'error') ? 'a' : null;
  if (!rag) return null;
  return {
    rag,
    sub: rag === 'g' ? okSub : rag === 'r' ? shortCheck(c.name) : st === 'error' ? 'check errored' : (watchSub || shortCheck(c.name)),
    tip: c.name + ': ' + (c.detail || st) + '.',
    go: '#card-smoke',
  };
}
// Pure: the worse of two RAGs (null/'x' never wins over a real colour).
export function worseRag(a, b) {
  const rank = { r: 3, a: 2, g: 1 };
  return (rank[b] || 0) > (rank[a] || 0) ? b : a;
}

// v136.60: the 3D view (src/map3d, dist/map3d.js) draws the SAME nodes and the SAME live signals as this map, so
// the two can never disagree. _lastMapSignals is what the flat map was last drawn with, kept current by
// _patchMapNode; mapData() hands it to the 3D view.
let _lastMapSignals = {};
let _lastMapExtras = {};   // v136.65: { checks, checksOn, agents }, set by render()
export function mapData() {
  const sig = _smSignals(_lastMapSignals);
  return {
    nodes: SYSMAP_NODES.map((n) => ({ id: n.id, label: n.label, rag: sig[n.id].rag, sub: sig[n.id].sub })),
    flows: SYSMAP_FLOWS.map((f) => f.slice()),
    checks: _lastMapExtras.checks || null,
    checksOn: _lastMapExtras.checksOn || 'db',
    agents: (_lastMapExtras.agents || []).map((a) => Object.assign({}, a)),
  };
}

// v136.65: the Cubes and Amanah nodes, from the ingest health of their own pushes (GET /api/admin/ingest-health).
// Cubes = the sales feed (the Top 50 counts only when it fails); Amanah = the staff feed. A feed that sent nothing
// in the window is amber, never a guessed green; one that only sent rejected pushes is red.
export function feedNodes(h) {
  if (!h || !h.ok || !h.feeds) return null;
  const days = h.windowDays || 14;
  const one = (main, extra, word) => {
    const f = h.feeds[main];
    if (!f) return null;
    const seen = (x) => x && ((x.validatedCount || 0) + (x.rejectedCount || 0)) > 0;
    const bad = [f, h.feeds[extra]].filter((x) => seen(x) && !x.hasRecentSuccess).map((x) => x.feed);
    if (bad.length) return { rag: 'r', sub: bad.join(' and ') + ' rejected', tip: word + ': the ' + bad.join(' and ') + ' push only sent rejected data in ' + days + ' days. See the ingest card in admin.' };
    if (!seen(f)) return { rag: 'a', sub: 'nothing in ' + days + ' days', tip: word + ': no ' + main + ' push arrived in ' + days + ' days.' };
    return { rag: 'g', sub: 'last ' + ago(f.lastReceivedAt), tip: word + ': the ' + main + ' push arrives and is accepted; the last one ' + ago(f.lastReceivedAt) + '.' };
  };
  return { cubes: one('sales', 'top50', 'Cubes'), amanah: one('staff', null, 'Amanah') };
}
async function feedHealthStatus() {
  if (!_jackCtx || typeof fetch === 'undefined') return;
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/ingest-health', { cache: 'no-store', headers: _wemHeaders() });
    const n = feedNodes(await res.json());
    if (!n) return;
    ['cubes', 'amanah'].forEach((id) => { if (n[id]) _patchMapNode(id, n[id].rag, n[id].sub, n[id].tip); });
  } catch (e) { /* the nodes stay grey: not checked */ }
}

export function buildSystemMap(s) {
  _lastMapSignals = Object.assign({}, s || {});
  const sig = _smSignals(s);
  const by = {};
  SYSMAP_NODES.forEach((n) => { by[n.id] = n; });
  const flowEl = (d, dep) => {
    const rag = sig[dep].rag;
    return '<path d="' + d + '" fill="none" class="sys-flow' + (rag === 'x' ? ' sys-flow-nc' : '') + '" data-dep="' + dep + '" stroke="' + ragColor(rag) + '"/>';
  };
  // A gentle S-curve from the right edge of one node to the left edge of the next column.
  const curve = (a, b) => {
    const x1 = a.x + SM_W / 2, x2 = b.x - SM_W / 2, mx = Math.round((x1 + x2) / 2);
    return 'M' + x1 + ',' + a.y + ' C' + mx + ',' + a.y + ' ' + mx + ',' + b.y + ' ' + x2 + ',' + b.y;
  };
  const groupEl = (g) =>
    '<rect x="' + g.x + '" y="' + g.y + '" width="' + g.w + '" height="' + g.h + '" rx="14" class="sys-grp"/>' +
    '<text x="' + (g.x + 10) + '" y="' + (g.y + 16) + '" class="sys-g">' + esc(g.label) + '</text>';
  const nodeEl = (n) => {
    const v = sig[n.id], col = ragColor(v.rag), x = n.x - SM_W / 2, y = n.y - SM_H / 2;
    const click = v.go
      ? ' style="cursor:pointer" tabindex="0" role="link" onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'' + esc(v.go) + '\')"' +
        ' onkeydown="if(event.key===\'Enter\'&&window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'' + esc(v.go) + '\')"'
      : '';
    // v134.4's rule holds on the map too: the state is said in words, never colour alone.
    return '<g id="sm-' + n.id + '" data-rag="' + v.rag + '" aria-label="' + esc(n.label + ': ' + ragWord(v.rag) + ', ' + v.sub) + '"' + click + '>' +
      '<title>' + esc(v.tip) + '</title>' +
      '<rect x="' + x + '" y="' + y + '" width="' + SM_W + '" height="' + SM_H + '" rx="10" class="sys-node' + (v.rag === 'x' ? ' sys-nc' : '') + '" stroke="' + col + '"/>' +
      '<circle cx="' + (x + 11) + '" cy="' + (y + 13) + '" r="4" fill="' + col + '">' +
        (v.rag === 'x' ? '' : '<animate attributeName="opacity" values="1;.35;1" dur="2.2s" repeatCount="indefinite"/>') + '</circle>' +
      '<text x="' + (x + 21) + '" y="' + (y + 18) + '" class="sys-t">' + esc(n.label) + '</text>' +
      '<text x="' + (x + 21) + '" y="' + (y + 34) + '" class="sys-s">' + esc(_smFit(v.sub)) + '</text>' +
    '</g>';
  };
  const w = by.worker, lastOut = by[SM_OUTSIDE[SM_OUTSIDE.length - 1]];
  const svg =
    '<svg class="sysmap" viewBox="0 0 1104 568" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Live system map: every service and its health">' +
      SYSMAP_GROUPS.map(groupEl).join('') +
      // flows sit behind the nodes; each is coloured by the health it depends on
      SYSMAP_FLOWS.map((f) => flowEl(curve(by[f[0]], by[f[1]]), f[2])).join('') +
      // the outside-services bus: a trunk from the live Worker, then one stub per service
      flowEl('M500,' + (w.y - SM_H / 2) + ' V' + SM_BUS_Y + ' H' + SM_BUS_X + ' V' + lastOut.y, 'worker') +
      SM_OUTSIDE.map((id) => flowEl('M' + SM_BUS_X + ',' + by[id].y + ' H' + (by[id].x - SM_W / 2), id)).join('') +
      SYSMAP_NODES.map(nodeEl).join('') +
    '</svg>';
  const t = systemMapTally(s);
  const v3 = mapView() === '3d';
  return '<div class="a-sect">System map · live' +
      ' <span class="smv" role="group" aria-label="Map view">' +
        '<button type="button" class="dq-go" data-mv="3d" aria-pressed="' + v3 + '" onclick="if(window.Agents&&Agents.setMapView)Agents.setMapView(\'3d\')" title="The live map as a globe: the features, how each works and its flow of data">3D</button>' +
        '<button type="button" class="dq-go" data-mv="flat" aria-pressed="' + !v3 + '" onclick="if(window.Agents&&Agents.setMapView)Agents.setMapView(\'flat\')" title="The live map, flat: every service and its health">Flat map</button>' +
      '</span></div>' +
    '<div class="sysmap-wrap" id="sysMap"><div id="sysMap3d" class="sysmap-3d"' + (v3 ? '' : ' hidden') + '>Loading the 3D map…</div>' +
      '<div class="sysmap-flat"' + (v3 ? ' hidden' : '') + '><div class="sysmap-scroll">' + svg + '</div>' +
      '<div class="sysmap-legend">Every node\'s colour is computed live from the stack, not decorative. Grey means the console has no live signal for it. ' +
        '<span class="sm-k"><i class="a-dot g"></i>healthy</span>' +
        '<span class="sm-k"><i class="a-dot a"></i>watch</span>' +
        '<span class="sm-k"><i class="a-dot r"></i>problem</span>' +
        '<span class="sm-k"><i class="a-dot x"></i>not checked</span>' +
        '<span class="sm-k" id="sysMapTally">' + t.g + ' healthy · ' + t.a + ' watch · ' + t.r + ' problem · ' + t.x + ' not checked</span></div>' +
    '</div></div>';
}

// v136.64 (Matt, 27 Oct 2026: "I also want this over the 2D model - 3D default"). The System Map opens as the globe;
// "Flat map" switches back, remembered on this device. A device that cannot draw 3D gets the flat map.
const MAP_VIEW_KEY = 'devMapView';
export function mapView() {
  try { return (typeof localStorage !== 'undefined' && localStorage.getItem(MAP_VIEW_KEY) === 'flat') ? 'flat' : '3d'; } catch (e) { return '3d'; }
}
function _showMapView(v) {
  const g = document.getElementById('sysMap3d'), f = document.querySelector('#sysMap .sysmap-flat');
  if (g) g.hidden = v !== '3d';
  if (f) f.hidden = v === '3d';
  document.querySelectorAll('.smv [data-mv]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mv') === v)));
}
export function setMapView(v) {
  if (typeof document === 'undefined') return;
  v = v === 'flat' ? 'flat' : '3d';
  try { localStorage.setItem(MAP_VIEW_KEY, v); } catch (e) { /* the choice just is not remembered */ }
  _showMapView(v);
  if (v === '3d') _mount3d();
}
// Mount (or, after the console's refresh, move) the globe into the panel. dev.html loads the bundle on demand.
function _mount3d() {
  const host = typeof document !== 'undefined' && document.getElementById('sysMap3d');
  if (!host || !(window.DevConsole && DevConsole.mount3DMap)) return;
  DevConsole.mount3DMap(host, (ok) => {
    if (ok) return;
    host.textContent = 'This device cannot draw the 3D map, so the flat map is shown.';
    _showMapView('flat');
  });
}

// v134.6: a late signal (the browser's own staging read, the email key) recolours its node and its lines in place.
function _patchMapNode(id, rag, sub, tip) {
  if (typeof document === 'undefined') return;
  _lastMapSignals[id] = { rag, sub: sub != null ? sub : ((_lastMapSignals[id] || {}).sub), tip };   // v136.60: the 3D view follows
  if (window.Map3D && Map3D.isMounted && Map3D.isMounted()) Map3D.update(mapData());   // v136.64: it is on screen now
  const g = document.getElementById('sm-' + id);
  if (!g) return;
  const col = ragColor(rag);
  const rect = g.querySelector('rect');
  if (rect) { rect.setAttribute('stroke', col); rect.setAttribute('class', 'sys-node' + (rag === 'x' ? ' sys-nc' : '')); }
  const dot = g.querySelector('circle');
  if (dot) dot.setAttribute('fill', col);
  const subEl = g.querySelector('.sys-s');
  if (subEl && sub != null) subEl.textContent = _smFit(sub);
  const tEl = g.querySelector('title');
  if (tEl && tip) tEl.textContent = tip;
  g.setAttribute('data-rag', rag);
  const labEl = g.querySelector('.sys-t');
  g.setAttribute('aria-label', (labEl ? labEl.textContent : id) + ': ' + ragWord(rag) + ', ' + (subEl ? subEl.textContent : ''));
  document.querySelectorAll('[data-dep="' + id + '"]').forEach((p) => {
    p.setAttribute('stroke', col);
    p.setAttribute('class', 'sys-flow' + (rag === 'x' ? ' sys-flow-nc' : ''));
  });
  const tally = document.getElementById('sysMapTally');
  if (tally) {
    const c = { g: 0, a: 0, r: 0, x: 0 };
    document.querySelectorAll('#sysMap [data-rag]').forEach((n) => { const k = n.getAttribute('data-rag'); if (c[k] != null) c[k]++; });
    tally.textContent = c.g + ' healthy · ' + c.a + ' watch · ' + c.r + ' problem · ' + c.x + ' not checked';
    const ck = document.getElementById('ckMapTally');
    if (ck) ck.innerHTML = mapTallyHtml(c);
  }
}
// The cockpit's one-line tally, coloured like the legend.
export function mapTallyHtml(t) {
  t = t || {};
  return '<b style="color:var(--green)">' + (t.g || 0) + ' healthy</b> · <b style="color:var(--amber)">' + (t.a || 0) + ' watch</b> · ' +
    '<b style="color:var(--red)">' + (t.r || 0) + ' problem</b> · ' + (t.x || 0) + ' not checked';
}

// ── Roadmap agent ───────────────────────────────────────────
// Reads the LIVING roadmap doc (docs/agent-roadmap.md, served same-origin by
// Netlify) so the console is clear on what's next — single source of truth, no
// drift. Pure parser: pulls the live/planned counts (🟢/🟠) and the "Next up"
// numbered list out of the markdown.
export function parseRoadmap(md) {
  // Normalise line endings first: the doc is read straight off disk, and a
  // CRLF checkout leaves a trailing \r on every split('\n') line that breaks
  // the `(.*)$` capture in the Next-up regex. Be robust to CRLF/CR regardless
  // of who calls us.
  md = String(md || '').replace(/\r\n?/g, '\n');
  const live = (md.match(/🟢/g) || []).length;
  const planned = (md.match(/🟠/g) || []).length;
  const nextUp = [];
  let inNext = false;
  // Items JACK captured from chats — their own section (newest first).
  const captured = [];
  let inCap = false;
  for (const raw of md.split('\n')) {
    if (/^##\s+Next up/i.test(raw)) { inNext = true; inCap = false; continue; }
    if (/^##\s+Captured from JACK/i.test(raw)) { inCap = true; inNext = false; continue; }
    if ((inNext || inCap) && /^##\s+/.test(raw)) { inNext = false; inCap = false; continue; }
    if (inNext) {
      const m = raw.match(/^\s*\d+\.\s+\*\*(.+?)\*\*\s*[—-]\s*(.*)$/);
      if (m) {
        const title = m[1].replace(/[*`]/g, '').trim();
        let desc = m[2].replace(/[_*`]/g, '').replace(/\s*_?effort[^.]*\.?$/i, '').trim();
        nextUp.push({ title, desc: desc.slice(0, 150), done: /✅/.test(raw) });
      }
    } else if (inCap) {
      const c = raw.match(/^\s*-\s+(\d{4}-\d{2}-\d{2}):\s+(.*)$/);
      if (c) captured.push({ date: c[1], text: c[2].replace(/[*`]/g, '').trim() });
    }
  }
  return { live, planned, nextUp, captured, hasContent: nextUp.length > 0 };
}

function buildRoadmap(r) {
  const docUrl = 'https://github.com/mattkiernan86-byte/elc-shift/blob/' + (_isStagingConsole() ? 'staging' : 'main') + '/docs/agent-roadmap.md';
  if (!r || !r.hasContent) {
    return '<div class="a-sect">Roadmap · what\'s next</div>' +
      '<div class="rm" id="roadmapPanel"><div class="rm-lead">Roadmap unavailable right now. ' +
      '<a class="rm-link" href="' + docUrl + '" target="_blank" rel="noopener">Open it on GitHub ↗</a></div></div>';
  }
  const next = r.nextUp[0];
  const rest = r.nextUp.slice(1, 6);
  const nextHtml = next
    ? '<div class="rm-next"><span class="rm-next-k">NEXT BUILD</span>' +
        '<div class="rm-next-t">' + esc(next.title) + '</div>' +
        (next.desc ? '<div class="rm-next-d">' + esc(next.desc) + '</div>' : '') + '</div>'
    : '';
  const restHtml = rest.length
    ? '<ol class="rm-list" start="2">' + rest.map(it =>
        '<li><b>' + esc(it.title) + '</b>' + (it.desc ? ' — ' + esc(it.desc) : '') + '</li>').join('') + '</ol>'
    : '';
  // Items JACK captured from your conversations (gated on your "yes").
  const cap = (r.captured || []).slice(0, 5);
  const capHtml = cap.length
    ? '<div class="rm-cap"><div class="rm-cap-h">📝 Captured from your chats with Jack</div>' +
        '<ul class="rm-cap-list">' + cap.map(c =>
          '<li><span class="rm-cap-d">' + esc(c.date) + '</span> ' + esc(c.text) + '</li>').join('') +
      '</ul></div>'
    : '';
  return '<div class="a-sect">Roadmap · what\'s next</div>' +
    '<div class="rm" id="roadmapPanel">' +
      '<div class="rm-lead">Read live from <code>docs/agent-roadmap.md</code> — the single source of truth. ' +
        '<b>' + r.live + '</b> live · <b>' + r.planned + '</b> planned.</div>' +
      nextHtml + restHtml + capHtml +
      '<a class="rm-link" href="' + docUrl + '" target="_blank" rel="noopener">View the full roadmap ↗</a>' +
    '</div>';
}

// Compact relative time for the drafts board ("2h ago", "3d ago").
function _ago(iso) {
  const t = Date.parse(iso || '');
  if (isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 90) return 'just now';
  const m = s / 60; if (m < 60) return Math.round(m) + 'm ago';
  const h = m / 60; if (h < 24) return Math.round(h) + 'h ago';
  const d = h / 24; if (d < 14) return Math.round(d) + 'd ago';
  return new Date(t).toLocaleDateString();
}

// ── Jack's drafts board ─────────────────────────────────────
// A persistent, JARVIS-style list of the pull requests Jack has drafted
// (across sessions, not just the current chat). Open/draft = amber, merged =
// green, closed = grey. `data` is the /api/admin/jack-prs response.
function buildJackDrafts(data) {
  const prs = (data && Array.isArray(data.prs)) ? data.prs : [];
  const head = (sub) => '<div class="a-sect">Jack\'s drafts · <span class="a-sect-sub">' + sub + '</span></div>';
  if (!prs.length) {
    return head('nothing yet') +
      '<div class="jd" id="jackDrafts"><div class="jd-empty">No drafts yet. Ask Jack to fix something — ' +
      '<i>"draft a fix for that"</i> — and the pull requests he opens for your review land here, ' +
      'so you can approve them on your own time.</div></div>';
  }
  const openCount = prs.filter(p => p.state === 'open').length;
  const rows = prs.map(p => {
    const st = p.state === 'merged' ? 'merged'
      : p.state === 'open' ? (p.draft ? 'draft' : 'open')
      : 'closed';
    const dotCls = st === 'merged' ? 'g' : (st === 'closed' ? 'x' : 'a');
    const icon   = st === 'merged' ? '✓' : (st === 'closed' ? '✕' : '●');
    const go     = st === 'merged' ? 'View ↗' : (st === 'closed' ? 'View ↗' : 'Review ↗');
    const title  = (p.title || '').replace(/^\[jack\]\s*/i, '');
    return '<a class="jd-row jd-' + st + '" href="' + esc(p.url) + '" target="_blank" rel="noopener">' +
      '<span class="jd-dot ' + dotCls + '">' + icon + '</span>' +
      '<span class="jd-num">#' + esc(String(p.number)) + '</span>' +
      '<span class="jd-title">' + esc(title) + '</span>' +
      '<span class="jd-meta">' + st + ' · ' + esc(_ago(p.created_at)) + '</span>' +
      '<span class="jd-go">' + go + '</span></a>';
  }).join('');
  const sub = openCount ? openCount + ' awaiting your review' : 'all reviewed';
  return head(sub) + '<div class="jd" id="jackDrafts">' + rows + '</div>';
}

// ── "Who to talk to" card RETIRED here (v127.8, dev-tab review K3):
// moved to the admin AM Planner tab (AmPlanner.renderBriefing) with a
// Monday email — buildAmBriefing now lives worker-side in
// src/worker/am-briefing.js so the card + email share one compute.

// ── main render ─────────────────────────────────────────────
export async function render(opts) {
  opts = opts || {};
  const mount = document.getElementById(opts.mountId || 'agentRoot');
  if (!mount) return;
  if (!opts.workerUrl || !opts.token) {
    mount.innerHTML = '<div class="a-load">Not authenticated.</div>';
    return;
  }
  // v120.5 — remember the opts so an in-card action (e.g. "clear errored runs")
  // can re-render the console with fresh data without a full page reload.
  _lastRenderOpts = opts;
  // ── JARVIS-style boot: a live progress bar that tracks REAL fetch stages,
  // with JACK talking you through it. No fake timer — the bar advances as each
  // agent's data actually lands. A QUIET refresh (the alive loop) re-fetches
  // everything without repainting the boot screen or re-greeting.
  if (!opts.quiet) {
    mount.innerHTML =
      '<div class="boot">' +
        '<div class="boot-head"><span class="boot-dot"></span>JACK · bringing the console online</div>' +
        '<div class="boot-bar"><div class="boot-fill" style="width:5%"></div></div>' +
        '<div class="boot-status">Establishing secure link…</div>' +
      '</div>';
  }
  // Give JACK the context it needs to call the cinematic-voice proxy.
  _jackCtx = { workerUrl: opts.workerUrl, token: opts.token };
  if (!opts.quiet) jackGreetBoot(opts.name);
  const BOOT_TOTAL = 15; // one per bootTick() below — keep in step or the bar never fills
  let _bootStep = 0;
  const bootTick = (label) => {
    _bootStep++;
    const pct = Math.min(100, Math.round((_bootStep / BOOT_TOTAL) * 100));
    const fill = mount.querySelector('.boot-fill');
    const stat = mount.querySelector('.boot-status');
    if (fill) fill.style.width = pct + '%';
    if (stat) stat.textContent = label;
  };

  let triageRows = [];
  let insights = null;
  let triageErr = null;
  try {
    const t = await api(opts.workerUrl, opts.token, '/api/admin/triage-results?limit=200');
    triageRows = (t && t.rows) || [];
  } catch (e) { triageErr = (e && e.message) || String(e); }
  bootTick('Reading bug-triage…');
  try {
    insights = await api(opts.workerUrl, opts.token, '/api/admin/feedback-insights?limit=500');
  } catch (e) { /* non-fatal — insights card shows amber */ }
  bootTick('Clustering feedback themes…');

  // Raw feedback list (for submitted_at timestamps) → the login welcome.
  let feedbackRows = [];
  try {
    const fb = await api(opts.workerUrl, opts.token, '/api/feedback?limit=500');
    feedbackRows = Array.isArray(fb) ? fb : ((fb && fb.rows) || []);
  } catch (e) { /* non-fatal — welcome falls back to triage-only */ }
  bootTick('Loading feedback from the floor…');

  // Compliance Watcher — a deterministic 7-day scan via the existing
  // /admin/compliance-summary endpoint (the "what Matt compiles every
  // Monday" cross-reference). No new backend; reuse what's there.
  let compliance = null;
  try {
    const _cTo = new Date();
    const _cFrom = new Date(_cTo.getTime() - 6 * 86400000);
    compliance = await api(opts.workerUrl, opts.token,
      '/admin/compliance-summary?from=' + _cFrom.toISOString().slice(0, 10) +
      '&to=' + _cTo.toISOString().slice(0, 10));
    _lastCompliance = compliance;   // v129.142 — the Nudge action reads these rows
  } catch (e) { /* non-fatal — Compliance Watcher card shows amber */ }
  bootTick('Scanning compliance + attendance…');

  // Compliance Trends — the WEEK-ON-WEEK history (by area) the snapshot can't
  // give. Reuses the same compliance engine over the last 8 completed weeks.
  let complianceTrend = null;
  try {
    complianceTrend = await api(opts.workerUrl, opts.token, '/api/admin/compliance-trend?weeks=8');
  } catch (e) { /* non-fatal — Compliance Trends card shows an honest empty state */ }

  // QA Agent — CI-health watcher. Reads recent GitHub Actions runs via the
  // Worker. Degrades honestly to a "CI access needed" state when the GH_PAT
  // lacks Actions:read (ciHealth.needsScope).
  let ciHealth = null;
  try {
    ciHealth = await api(opts.workerUrl, opts.token, '/api/admin/ci-health?limit=20');
  } catch (e) { /* non-fatal — QA card shows amber/unreachable */ }
  bootTick('Checking CI / QA…');

  // Security Agent — reads the security-scan workflow's recent runs (same
  // CI-health endpoint, scoped to one workflow). A failed run = a secret was
  // caught in a PR. Same honest "needs scope" degradation as QA.
  let secHealth = null;
  try {
    secHealth = await api(opts.workerUrl, opts.token,
      '/api/admin/ci-health?limit=20&workflow=security-scan.yml');
  } catch (e) { /* non-fatal — Security card shows amber/unreachable */ }
  bootTick('Scanning security…');

  // QA "what broke" — only when the latest CI run FAILED (so the AI summary
  // cost is rare). Gives a one-sentence plain-English read of the failure.
  let ciFail = null;
  if (ciHealth && ciHealth.ok && ciHealth.last && ciHealth.last.conclusion === 'failure') {
    try { ciFail = await api(opts.workerUrl, opts.token, '/api/admin/ci-whatbroke'); }
    catch (e) { /* non-fatal — QA card just shows it failed */ }
  }

  // Rota Check — read-only advisory pre-flight on this week's saved rota.
  // Flags coverage gaps / double-bookings / bad balances for a manager to
  // eyeball. Advisory only — it never writes a rota or touches pay.
  let rota = null;
  try {
    rota = await api(opts.workerUrl, opts.token, '/api/admin/rota-check');
  } catch (e) { /* non-fatal — Rota Check card shows amber/unreachable */ }
  bootTick('Pre-flighting the rota…');

  // Data Freshness — read-only watcher over the periodic data feeds (Top-50
  // sales, targets, next week's rota, holidays, balances). Flags stale/missing
  // uploads. Advisory only — it never uploads or edits anything.
  let dataFresh = null;
  try {
    dataFresh = await api(opts.workerUrl, opts.token, '/api/admin/data-freshness');
  } catch (e) { /* non-fatal — Data Freshness card shows amber/unreachable */ }
  bootTick('Checking data feeds…');

  // Comms Watcher (reporting B1, v125.27) — adoption ANOMALIES across the v125
  // comms platform: stores that stopped reading news, imminent tasks/surveys
  // with low completion, journal silence. Read-only; same watcher family as
  // Rota Check / Data Freshness (the numbers live on admin 📊 Comms Report).
  let commsWatch = null;
  try {
    commsWatch = await api(opts.workerUrl, opts.token, '/api/admin/comms-watch');
  } catch (e) { /* non-fatal — Comms Watcher card shows amber/unreachable */ }

  // Cost Watcher (v125.73, reliability plan phase 1) — GitHub Actions minutes
  // via the billing API. A silent mid-month exhaustion freezes CI and every
  // merge (the Cloudflare/Netlify free-tier lesson, third platform in line).
  let ghMinutes = null;
  try {
    ghMinutes = await api(opts.workerUrl, opts.token, '/api/admin/gh-minutes');
  } catch (e) { /* non-fatal — Cost Watcher card shows amber/unreachable */ }
  // Phase 2 (v128.7): Cloudflare Workers requests/day + Netlify caps —
  // dark (not-configured guidance) until the CF/Netlify secrets exist.
  let platformUsage = null;
  try {
    platformUsage = await api(opts.workerUrl, opts.token, '/api/admin/platform-usage');
  } catch (e) { /* non-fatal — the card's phase-2 rows show unreachable */ }

  // Smoke tests — read-only DB integrity invariants ("should never be true").
  // Auto-run on launch so the console reports integrity status without a click.
  let smoke = null;
  try {
    smoke = await api(opts.workerUrl, opts.token, '/api/admin/smoke');
  } catch (e) { /* non-fatal — Data Integrity card shows amber/unreachable */ }
  bootTick('Running data-integrity checks…');

  // Roadmap agent — reads the living roadmap doc (served same-origin by
  // Netlify, no auth needed) so the console is clear on the next builds.
  let roadmap = null;
  try {
    const r = await fetch('/docs/agent-roadmap.md', { cache: 'no-cache' });
    if (r.ok) roadmap = parseRoadmap(await r.text());
  } catch (e) { /* non-fatal — Roadmap panel shows a GitHub link instead */ }
  // v134.6: the version the browser actually loads from Netlify (same origin, static), so the
  // system map's site node reports the deployed page, not a claim. Unread stays unread.
  let frontVersion = null;
  try {
    const fv = await fetch('/version.js', { cache: 'no-store' });
    if (fv.ok) frontVersion = parseFrontVersion(await fv.text());
  } catch { /* non-fatal: the site node says "version unread" */ }
  bootTick('Reading the roadmap…');

  // Watcher-memory history → sparkline trends on the cards. Empty until the
  // daily cron (or the manual POST trigger) has captured ≥2 snapshots.
  let snapshots = [];
  try {
    const sn = await api(opts.workerUrl, opts.token, '/api/admin/agent-snapshots?limit=30');
    snapshots = (sn && sn.rows) || [];
  } catch (e) { /* non-fatal — cards show "building history…" */ }
  bootTick('Loading trend history…');
  const _snapsOldFirst = snapshots.slice().reverse(); // API returns newest-first

  // JACK's overnight 360 sweep — any heads-up he left while you were away.
  // Empty until the nightly cron has run with the jack_findings table in place.
  let jackSweep = null;
  try {
    jackSweep = await api(opts.workerUrl, opts.token, '/api/admin/jack-sweep');
  } catch (e) { /* non-fatal — no heads-up shown */ }
  bootTick('Reading the overnight sweep…');

  // v136.59: the pay records Jack can repair (a preview: nothing is written by reading it)
  try { _repairData = await _repairPost({}); } catch (e) { _repairData = null; /* non-fatal — no repair card */ }

  // Jack's drafts board — the PRs he has opened for your review, across sessions.
  let jackPRs = null;
  try {
    jackPRs = await api(opts.workerUrl, opts.token, '/api/admin/jack-prs');
  } catch (e) { /* non-fatal — drafts board shows its empty state */ }
  bootTick('Loading Jack\'s drafts…');

  // v129.60 — the release panel + cron heartbeat board (one tolerant read).
  let releaseState = null;
  try { releaseState = await api(opts.workerUrl, opts.token, '/api/admin/release-state'); } catch (e) { /* panel shows unreachable */ }
  bootTick('Checking releases + cron heartbeats…');

  const seriesOf = (pick) => _snapsOldFirst
    .map(s => (s && s.metrics ? pick(s.metrics) : null))
    .filter(n => typeof n === 'number' && isFinite(n));
  const feedbackOpenSeries = seriesOf(m => m.feedback && m.feedback.open);
  const triageTotalSeries  = seriesOf(m => m.triage && m.triage.total);
  // v2 watcher-memory series (rota + feeds + triage errors) → trends + nudges.
  const triageErrorsSeries = seriesOf(m => m.triage && m.triage.errors);
  const rotaGapsSeries     = seriesOf(m => m.rota && m.rota.coverageGaps);
  const feedStaleSeries    = seriesOf(m => m.dataFreshness && m.dataFreshness.stale);
  // B3 — comms adoption series (per-feature company %, from the nightly snapshot).
  const commsNewsSeries    = seriesOf(m => m.comms && m.comms.news);
  const commsTasksSeries   = seriesOf(m => m.comms && m.comms.tasks);
  const commsSurveysSeries = seriesOf(m => m.comms && m.comms.surveys);
  const commsSignalsSeries = seriesOf(m => m.comms && m.comms.signals);

  const ts = triageStats(triageRows);
  const totalOpen = insights && Array.isArray(insights.byScreen)
    ? insights.byScreen.reduce((n, g) => n + (g.open || 0), 0) : null;
  const topTheme = insights && Array.isArray(insights.byScreen) && insights.byScreen.length
    ? insights.byScreen[0].screen : null;

  // ── "since your last visit" deltas (for the welcome banner) ──
  const sinceMs = opts.lastLogin ? new Date(opts.lastLogin).getTime() : 0;
  const afterSince = (iso) => {
    if (!sinceMs || !iso) return false;
    const t = new Date(iso).getTime();
    return !isNaN(t) && t > sinceMs;
  };
  const triagesSince = triageRows.filter(r => afterSince(r.created_at)).length;
  const fixSince = triageRows.filter(r => r.kind === 'fix' && afterSince(r.created_at)).length;
  const clarifySince = triageRows.filter(r => r.kind === 'clarify' && afterSince(r.created_at)).length;
  const feedbackSince = feedbackRows.filter(r => afterSince(r.submitted_at)).length;

  // Data-reachability: the triage fetch is the canonical first admin call. If
  // it failed (esp. 401/403), the whole console is degraded — say so honestly
  // instead of rendering a confident summary built from blanks.
  const authErr = !!(triageErr && /\b(401|403)\b/.test(triageErr));
  const unreachable = triageErr ? {
    auth: authErr,
    msg: authErr
      ? 'your login looks expired (' + triageErr + ') — sign out and back in to refresh it.'
      : 'couldn\'t reach the Worker (' + triageErr + ') — try again shortly.',
  } : null;

  // Overall system RAG: worst of the live agents (RED when data is unreachable).
  const liveRags = [ts.rag, insights ? 'g' : 'a'];
  const overall = unreachable ? 'r'
    : liveRags.indexOf('r') >= 0 ? 'r'
    : liveRags.indexOf('a') >= 0 ? 'a' : 'g';
  const overallLabel = unreachable ? 'ATTENTION'
    : overall === 'g' ? 'GOOD' : overall === 'a' ? 'WATCH' : 'ATTENTION';

  const welcomeHtml = buildWelcome(opts.name, opts.lastLogin, {
    triagesSince, fixSince, clarifySince, feedbackSince,
    totalTriaged: ts.total, openFeedback: totalOpen, overallLabel, unreachable,
  });

  // ── Compliance Watcher inputs (deterministic 7-day scan) ──
  const compStats = (compliance && compliance.stats) || null;
  const compTotal = compStats ? (compStats.total || 0) : null;
  const compCat = (compStats && compStats.by_category) || {};
  const compArea = (compStats && compStats.by_area_manager) || {};
  const compDays = (compliance && compliance.period && compliance.period.days) || 7;
  // "Core" misses = the things Matt compiles by hand (missed checklists / logs
  // / staff who never signed in). Info-level noise (late clock-ins, location)
  // stays amber; core misses above ~1/day push it red.
  const compCore = (compCat.checklist_compliance || 0) + (compCat.log_compliance || 0) + (compCat.never_signed_in || 0);
  let compWorstArea = '—', compWorstN = 0;
  for (const k in compArea) { if (compArea[k] > compWorstN) { compWorstN = compArea[k]; compWorstArea = k; } }
  const compRag = compliance == null ? 'a'
    : compTotal === 0 ? 'g'
    : compCore > compDays ? 'r' : 'a';

  // ── Attendance Watcher inputs (clock / break / pay accuracy) ──
  // Same 7-day scan, a different lens: the clock-side anomalies that hit
  // pay — late clock-ins, break violations, location-off clock-ins.
  const attLate = compCat.late_clockin || 0;
  // Break violations are reported under ONE category ('break_issue') by the
  // compliance engine — the no_break / break_short / break_long flavours are a
  // SUBTYPE on each issue, not separate by_category keys. Summing the subtype
  // names read undefined every time, so this card always showed 0 break issues
  // even when there were real ones (a pay/labour signal). Read the real key.
  const attBreak = compCat.break_issue || 0;
  const attLoc = compCat.location_off || 0;
  const attTotal = attLate + attBreak + attLoc;
  // Pay-critical = late clock-ins + break violations; red above ~1/day.
  const attRag = compliance == null ? 'a'
    : attTotal === 0 ? 'g'
    : (attLate + attBreak) > compDays ? 'r' : 'a';

  // ── QA Agent inputs (CI-health watcher) ──
  const qaOk = !!(ciHealth && ciHealth.ok);
  const qaScope = !!(ciHealth && ciHealth.needsScope);
  const qaPass = qaOk && ciHealth.passRate != null ? ciHealth.passRate : null;
  const qaLastConcl = qaOk && ciHealth.last ? (ciHealth.last.conclusion || ciHealth.last.status) : null;
  // Green when CI is healthy; red when the latest run failed; amber when the
  // pass rate is shaky OR we can't read CI yet (access/unreachable).
  const qaRag = !qaOk ? 'a'
    : (ciHealth.last && ciHealth.last.conclusion === 'failure') ? 'r'
    : (qaPass != null && qaPass < 80) ? 'a' : 'g';
  const qaMeta = qaOk
    ? [['Pass rate', qaPass == null ? '—' : qaPass + '%'],
       ['Last run', qaLastConcl || '—'],
       ['Runs', String(ciHealth.completed || ciHealth.total || 0)]]
    : [['Status', qaScope ? 'CI access needed' : 'CI unreachable'],
       ['Next', qaScope ? 'add Actions:read to GH_PAT' : 'check Worker']];

  // ── Security Agent inputs (security-scan workflow runs) ──
  const secOk = !!(secHealth && secHealth.ok);
  const secScope = !!(secHealth && secHealth.needsScope);
  const secLast = secOk && secHealth.last ? secHealth.last : null;
  // A FAILED security-scan run = a secret was caught in that PR → red. A clean
  // latest run → green. No scans yet / can't read CI → amber.
  const secRag = !secOk ? 'a' : !secLast ? 'a' : (secLast.conclusion === 'failure' ? 'r' : 'g');
  const secMeta = secOk
    ? (secLast
        ? [['Last scan', secLast.conclusion || secLast.status || '—'],
           ['Scans', String(secHealth.completed || secHealth.total || 0)],
           ['Secrets caught', String(secHealth.failure || 0)]]
        : [['Status', 'no PRs scanned yet'], ['Scans', '0']])
    : [['Status', secScope ? 'CI access needed' : 'CI unreachable'],
       ['Next', secScope ? 'add Actions:read to GH_PAT' : 'check Worker']];

  // ── Rota Check inputs (read-only advisory pre-flight) ──
  const rotaOk = !!(rota && rota.summary);
  const rotaSum = rotaOk ? rota.summary : null;
  const rotaGaps = rotaSum ? (rotaSum.coverage_gaps || 0) : 0;
  const rotaDoubles = rotaSum ? (rotaSum.double_booked || 0) : 0;
  const rotaWarn = rotaSum ? (rotaSum.warn || 0) : 0;
  // Green when nothing flagged; RED on a real operational problem (a store
  // open with no one scheduled, or someone booked at two stores at once);
  // amber for softer flags (config / unscheduled / balances) or unreachable.
  const rotaRag = !rotaOk ? 'a'
    : (rotaGaps > 0 || rotaDoubles > 0) ? 'r'
    : rotaWarn > 0 ? 'a' : 'g';
  const rotaWeekLabel = rotaOk && rota.week_start ? rota.week_start : '—';
  const rotaMeta = rotaOk
    ? [['Coverage gaps', String(rotaGaps)],
       ['Double-booked', String(rotaDoubles)],
       ['Week', rotaWeekLabel]]
    : [['Status', 'unreachable'], ['Next', 'check Worker / admin token']];

  // ── Data Freshness inputs (read-only feed watcher) ──
  const dfOk = !!(dataFresh && dataFresh.summary);
  const dfSum = dfOk ? dataFresh.summary : null;
  const dfStale = dfSum ? (dfSum.stale || 0) : 0;
  const dfErr = dfSum ? (dfSum.error || 0) : 0;
  // The single most useful detail: the first feed that needs attention.
  const dfFirstStale = dfOk && Array.isArray(dataFresh.checks)
    ? (dataFresh.checks.find(c => c.status !== 'ok') || null) : null;
  // Green when every feed is fresh; amber when one is stale/missing; red only
  // when a feed couldn't be read at all (a real wiring problem) or unreachable.
  const dfRag = !dfOk ? 'a'
    : dfErr > 0 ? 'r'
    : dfStale > 0 ? 'a' : 'g';
  const dfMeta = dfOk
    ? [['Feeds checked', String(dfSum.total || 0)],
       ['Need attention', String(dfStale)],
       ['Oldest issue', dfFirstStale ? dfFirstStale.name : '—']]
    : [['Status', 'unreachable'], ['Next', 'check Worker / admin token']];

  // ── Comms Watcher inputs (adoption anomalies over the comms platform) ──
  const cwOk = !!(commsWatch && commsWatch.ok && commsWatch.summary);
  const cwSignals = cwOk && Array.isArray(commsWatch.signals) ? commsWatch.signals : [];
  _lastCommsSignals = cwSignals;   // v129.143 — act on the rows the card shows
  const cwFirst = cwSignals.length ? cwSignals[0] : null;
  // Green = no anomalies; amber = warnings (or unreachable); red = a critical
  // (a zero-response survey near deadline / a due-today task under 50% / 3+
  // stores gone silent on news).
  const cwRag = !cwOk ? 'a'
    : commsWatch.rag === 'red' ? 'r'
    : commsWatch.rag === 'amber' ? 'a' : 'g';
  const cwMeta = cwOk
    ? [['Signals', String(commsWatch.summary.total || 0)],
       ['Critical', String(commsWatch.summary.crit || 0)],
       ['Top signal', cwFirst ? cwFirst.label : 'all clear']]
    : [['Status', 'unreachable'], ['Next', 'check Worker / admin token']];

  // ── Cost Watcher inputs (GitHub Actions minutes — v125.73) ──
  const gmOk = !!(ghMinutes && ghMinutes.ok);
  // Green = comfortably inside the included quota; amber = ≥80% used, or the
  // check itself is unavailable (missing PAT / scope / unreachable); RED the
  // moment paid minutes appear or the quota is exhausted.
  const gmRag = !gmOk ? 'a'
    : ghMinutes.level === 'over' ? 'r'
    : ghMinutes.level === 'warn' ? 'a' : 'g';
  const gmMeta = !gmOk
    ? [['Status', (ghMinutes && ghMinutes.needsScope) ? 'PAT lacks billing scope'
         : (ghMinutes && ghMinutes.configured === false) ? 'GH_PAT not set' : 'unreachable'],
       ['Next', (ghMinutes && ghMinutes.needsScope) ? 'add "Plan: read" (fine-grained) or `user` scope (classic) to GH_PAT'
         : 'check Worker / admin token']]
    // Budget mode (v125.74): Matt runs a deliberate $-budget with Stop-usage
    // on, so paid minutes are NORMAL — spend-vs-budget is the real signal
    // (GitHub hard-freezes CI when the budget is hit).
    : (ghMinutes.budgetPct != null
      ? [['Spend', '$' + Number(ghMinutes.spentUsd || 0).toFixed(2) + ' / $' + ghMinutes.budgetUsd],
         ['Budget used', ghMinutes.budgetPct + '%'],
         ['Minutes', String(ghMinutes.used) + (ghMinutes.included ? ' (' + ghMinutes.included + ' incl.)' : '')]]
      : [['Minutes used', String(ghMinutes.used) + (ghMinutes.included ? ' / ' + ghMinutes.included : '')],
         ['Usage', ghMinutes.pct != null ? ghMinutes.pct + '%' : 'n/a'],
         ['Paid minutes', String(ghMinutes.paid || 0)]]);
  // Phase 2 rows (v128.7) — one line per platform. Not configured reads as
  // "add the secret", never as green; a warn/over on either pulls the card's
  // RAG up alongside the GH-minutes level.
  {
    const cf = platformUsage && platformUsage.cf;
    const nf = platformUsage && platformUsage.netlify;
    gmMeta.push(['CF Workers', !cf ? 'unreachable'
      : cf.configured === false ? 'add CF_API_TOKEN + CF_ACCOUNT_ID'
      : cf.needsScope ? 'token lacks Account Analytics: Read'
      : cf.pct != null ? (cf.used + ' / ' + cf.included + ' today (' + cf.pct + '%)')
      : (cf.error || 'unreachable')]);
    // v136.8: which Worker the requests went to (live, staging, Jack, mission-control),
    // shown only when Cloudflare returned the per-Worker split.
    if (cf && Array.isArray(cf.byScript) && cf.byScript.length) {
      gmMeta.push(['CF by Worker', cf.byScript.slice(0, 6).map(function (s) { return String(s.script) + ' ' + Number(s.requests || 0).toLocaleString('en-GB'); }).join(' · ')]);
    }
    const nfEntries = (nf && nf.entries) || [];
    gmMeta.push(['Netlify', !nf ? 'unreachable'
      : nf.configured === false ? 'add NETLIFY_TOKEN'
      : nf.needsScope ? 'token rejected — mint a fresh one'
      : nfEntries.length ? nfEntries.map(function (e) { return e.label.replace(/^Netlify /, '') + ' ' + (e.pct != null ? e.pct + '%' : '?'); }).join(' · ')
      : 'no capped capabilities reported']);
  }
  const p2Levels = [];
  if (platformUsage && platformUsage.cf && platformUsage.cf.level) p2Levels.push(platformUsage.cf.level);
  if (platformUsage && platformUsage.netlify && platformUsage.netlify.level) p2Levels.push(platformUsage.netlify.level);
  const gmRag2 = p2Levels.indexOf('over') !== -1 ? 'r'
    : (p2Levels.indexOf('warn') !== -1 && gmRag === 'g') ? 'a' : gmRag;

  // ── Data Integrity (smoke) inputs — auto-run DB invariant checks ──
  const smokeOk = !!(smoke && smoke.summary);
  const smokeSum = smokeOk ? smoke.summary : null;
  const smokeFail = smokeSum ? (smokeSum.fail || 0) : 0;
  const smokeErr = smokeSum ? (smokeSum.error || 0) : 0;
  const smokeFirstBad = smokeOk && Array.isArray(smoke.checks)
    ? (smoke.checks.find(c => c.status === 'fail') || smoke.checks.find(c => c.status === 'error') || null) : null;
  // Green when every invariant holds; RED on a real failure (an invariant
  // broke); amber when a check errored or we can't reach the endpoint.
  const smokeRag = !smokeOk ? 'a'
    : smokeFail > 0 ? 'r'
    : smokeErr > 0 ? 'a' : 'g';
  const smokeMeta = smokeOk
    ? [['Pass', String(smokeSum.pass || 0)],
       ['Fail', String(smokeFail)],
       ['Checked', String(smokeSum.total || 0)]]
    : [['Status', 'unreachable'], ['Next', 'check Worker / admin token']];

  // ── Senior-Dev Advisor inputs (counted over the real agents: Bug-Triage,
  // Feedback Insights, Compliance, Attendance, QA, Security, Rota Check,
  // Data Freshness, Comms Watcher (since B2), Data Integrity, the Advisor) ──
  const liveScreens = insights && Array.isArray(insights.byScreen) ? insights.byScreen.length : 0;
  const topThemeOpen = insights && Array.isArray(insights.byScreen) && insights.byScreen.length
    ? (insights.byScreen[0].open || 0) : 0;
  // v129.59: ONE denominator. Must equal the stat strip's
  // `stability.length + data.length + 1` (3 + 8 + 1 = 12) — the old list
  // double-counted Attendance (merged into Compliance) and skipped Cost
  // Watcher + AI Rota, so the strip said 12 while the advisor divided by 11.
  const greenLive =
    (ts.rag === 'g' ? 1 : 0) +        // Bug-Triage
    (insights ? 1 : 0) +              // Feedback Insights
    (compliance != null ? 1 : 0) +    // Compliance (incl. attendance, one card)
    (qaOk ? 1 : 0) +                  // QA Agent (green = CI readable)
    (secOk ? 1 : 0) +                 // Security Agent (green = scans readable)
    (rotaOk ? 1 : 0) +                // Rota Check (green = it ran)
    (dfOk ? 1 : 0) +                  // Data Freshness (green = it ran)
    (cwOk ? 1 : 0) +                  // Comms Watcher (green = it ran)
    (gmOk ? 1 : 0) +                  // Cost Watcher (green = billing readable)
    (smokeOk ? 1 : 0) +               // Data Integrity / smoke (green = it ran)
    1 +                               // AI Rota Generator (registry entry, manual)
    1;                                // the Advisor (operational by definition)
  const liveTotal = 12;
  const advisorInputs = {
    triageTotal: ts.total, triageHealth: ts.health, triageErrors: ts.errors,
    triageFixes: ts.fixes, triageClarify: ts.clarify,
    totalOpen, topTheme, topThemeOpen, insightsLive: !!insights, liveScreens,
    greenLive, liveTotal, overallLabel, unreachable,
  };
  const advisorHtml = buildAdvisor(advisorInputs);
  const opsScore = advisorScore(advisorInputs);
  const nextStepText = advisorNextStep({
    triageTotal: ts.total, triageErrors: ts.errors, topTheme, topThemeOpen, totalOpen,
  });

  // ── System map inputs — every node colour is a REAL signal ──
  // Worker reachable = at least one admin API call returned. DB reachable =
  // a data query COMPLETED through the Worker (we never hit Supabase direct).
  const workerReachable = (triageErr == null) || !!insights || feedbackRows.length > 0;
  // v124.39 — a SUCCESSFUL query proves the Worker→DB round-trip, even with
  // zero rows: empty tables are "quiet", NOT "disconnected". (Was: required
  // actual rows, so an empty/quiet DB — e.g. staging, or a dead-quiet prod
  // day — falsely read as "no data returned" ≈ database down.) A query that
  // ERRORS still fails reachability, so a real outage still shows red.
  const dbReachable = workerReachable;
  const dbHasData = triageRows.length > 0 || feedbackRows.length > 0 ||
    !!(insights && Array.isArray(insights.byScreen) && insights.byScreen.length);
  // Auth-rejected (401/403) means the Worker IS up — it just refused the token.
  // Show that as amber "auth rejected", not red "unreachable".
  const workerRag = workerReachable ? 'g' : (authErr ? 'a' : 'r');
  const workerSub = workerReachable ? 'API reachable' : (authErr ? 'auth rejected — re-login' : 'unreachable');
  const dbRag = !workerReachable ? 'x' : dbReachable ? 'g' : 'a';
  // The Database node reflects DATA INTEGRITY when the smoke checks ran: a
  // broken invariant turns it amber/red even while data is flowing.
  const dbNodeRag = smokeOk
    ? (smokeFail > 0 ? 'r' : smokeErr > 0 ? 'a' : (dbReachable ? 'g' : 'a'))
    : dbRag;
  const dbNodeSub = smokeOk
    // v133.123: name the failing check ("1 integrity fail" read as "the database is down", 15 Oct 2026)
    ? (smokeFail > 0 ? (smokeFail === 1 && smokeFirstBad ? shortCheck(smokeFirstBad.name) : smokeFail + ' checks failing')
        : (smokeSum.total || 0) + ' checks ok')
    : (!workerReachable ? 'unreachable' : dbHasData ? 'serving data' : 'reachable · quiet');
  // v134.6: the detailed map. The console talks to ONE Worker (live or staging, by its address).
  // That Worker's reachability, its database's integrity checks and the site this page came from
  // are the "self" end; the other end is only what release-state (or this browser) could read, and
  // anything nobody read stays grey "not checked".
  const onStaging = String(opts.workerUrl || '').indexOf('staging') !== -1;
  const smCheck = (name) => (smokeOk && Array.isArray(smoke.checks)) ? (smoke.checks.find((c) => c && c.name === name) || null) : null;
  const relS = (releaseState && releaseState.release) || {};
  const selfRel = onStaging ? relS.staging : relS.prod;
  const otherRel = onStaging ? relS.prod : relS.staging;
  const selfVer = selfRel && selfRel.ok ? selfRel.version : null;
  const usageRag = (u) => (!u || u.configured === false) ? null : u.level === 'over' ? 'r' : u.level === 'warn' ? 'a' : null;
  const cfUse = usageRag(platformUsage && platformUsage.cf);
  const nfUse = usageRag(platformUsage && platformUsage.netlify);
  const selfNodes = {
    worker: { rag: worseRag(workerRag, cfUse), sub: workerSub + (workerReachable && selfVer ? ' · ' + selfVer : ''),
              tip: 'The Worker this console talks to (' + String(opts.workerUrl || '').replace(/^https?:\/\//, '') + '): ' + workerSub +
                (cfUse ? '. Cloudflare Workers usage is ' + (cfUse === 'r' ? 'over' : 'near') + ' the daily cap (Cost Watcher).' : '.'),
              go: cfUse ? '#card-gh-minutes' : null },
    db:     { rag: dbNodeRag, sub: dbNodeSub,
              tip: smokeFirstBad ? 'Failing check: ' + smokeFirstBad.name + (smokeFirstBad.detail ? '. ' + smokeFirstBad.detail : '') + '. The database itself is answering.'
                : smokeOk ? 'All ' + (smokeSum.total || 0) + ' integrity checks hold. Supabase answers through the Worker.' : null,
              go: smokeOk ? '#card-smoke' : null },
    site:   frontVersion == null
      ? { rag: worseRag('a', nfUse), sub: 'version unread', tip: 'This page loaded from Netlify, but its /version.js could not be read.' }
      : (selfVer && selfVer !== frontVersion)
        ? { rag: worseRag('a', nfUse), sub: 'page ' + frontVersion + ' · API ' + selfVer,
            tip: 'The site serves ' + frontVersion + ' but its Worker runs ' + selfVer + '. A promotion deploys the two separately: check both finished.' }
        : { rag: worseRag('g', nfUse), sub: 'serving ' + frontVersion,
            tip: 'This page came from here: /version.js reads ' + frontVersion + (selfVer ? ', the same as its Worker.' : '.') +
              (nfUse ? ' Netlify usage is ' + (nfUse === 'r' ? 'over' : 'near') + ' a cap (Cost Watcher).' : ''),
            go: nfUse ? '#card-gh-minutes' : null },
  };
  // The other end: green only when its version was actually read. On the live console the staging
  // Worker is usually read by this browser a moment later (fillStagingFromBrowser patches the node).
  const otherWorker = otherRel && otherRel.ok
    ? { rag: 'g', sub: otherRel.version + (relS.comparable ? (relS.in_sync ? ' · in sync' : (onStaging ? ' · behind staging' : ' · awaits promo')) : ''),
        tip: (onStaging ? 'The live' : 'The staging') + ' Worker answers with ' + otherRel.version + ' (read by release-state).' }
    : (releaseState && !onStaging)
      ? { rag: 'x', sub: 'reading from browser', tip: 'Not checked yet. The live Worker cannot fetch the staging Worker (a Cloudflare limit), so this browser asks it directly.' }
      : null;
  // The 3am train stamps cron_last_complete as its LAST step, so a stale stamp means it died mid-run.
  const smCrons = (releaseState && releaseState.crons) || null;
  const smHrs = smCrons && smCrons.nightly ? smCrons.nightly.hours_since : null;
  const nightlyNode = !smCrons ? null
    : smHrs == null ? { rag: 'a', sub: 'no heartbeat', tip: 'cron_last_complete has no readable stamp: no finished 3am train is on record.' }
    : smHrs > 26 ? { rag: 'r', sub: 'last ran ' + smHrs + 'h ago', tip: 'The 3am train last finished ' + smHrs + ' hours ago. Over 26 hours means it died or stopped.' }
    : { rag: 'g', sub: 'ran ' + smHrs + 'h ago', tip: 'The 3am train finished its last run ' + smHrs + ' hours ago (cron_last_complete).' };
  // The hourly cron runs prod-watch, which keeps a failure memory but stamps no heartbeat: it can
  // show a problem, never prove health, so with no failures it stays grey.
  let smWatch = null;
  try {
    const st = smCrons && smCrons.prod_watch ? smCrons.prod_watch.state : null;
    const o = typeof st === 'string' ? JSON.parse(st) : st;
    if (o && typeof o === 'object') smWatch = o;
  } catch { smWatch = null; }
  // v136.6 (Matt, 16 Oct 2026): named "Version watchdog" (it was "Hourly cron", which read as the cron failing). It
  // ignores the live Worker checking ITS OWN address: Cloudflare refuses a Worker calling itself, so that check fails
  // every hour for ever (the known false alarm, docs/field-lessons.md).
  let smSelfHost = '';
  try { smSelfHost = (typeof DB !== 'undefined' && DB.workerUrl) ? new URL(DB.workerUrl()).host : ''; } catch { smSelfHost = ''; }
  const smWatchKeys = smWatch ? Object.keys(smWatch).filter((k) => Number(smWatch[k]) > 0 && !(smSelfHost && k.indexOf('down:') === 0 && k.indexOf(smSelfHost) !== -1)) : [];
  const smWatchMax = smWatchKeys.reduce((m, k) => Math.max(m, Number(smWatch[k]) || 0), 0);
  const hourlyNode = smWatchKeys.length
    ? { rag: smWatchMax >= 2 ? 'r' : 'a', sub: smWatchKeys.length + ' watch failure' + (smWatchKeys.length === 1 ? '' : 's'),
        tip: 'The hourly version watchdog (is the live site up, and do the site and the Worker run the same version?) is failing: ' + smWatchKeys.join('; ') + '. A second failed run in a row turns it red. A mismatch during a deploy clears on the next run.' }
    : null;
  // Resend: a failed weekly send first, then the bounce check over every send, then the last good send.
  const smMail = checkNode(smCheck('Email delivery (bounces, 30d)'), 'no bounces in 30d', 'bounces in 30d');
  const smWl = smCrons && smCrons.weekly_email;
  const resendNode = (smWl && smWl.outcome === 'failed')
    ? { rag: 'r', sub: 'weekly send failed', tip: 'The weekly email\'s last send FAILED' + (smWl.detail ? ': ' + smWl.detail : '') + '.', go: '#wemPanel' }
    : smMail ? smMail
    : (smWl && smWl.outcome === 'sent') ? { rag: 'g', sub: 'last send ok', tip: 'The weekly email last sent successfully (' + String(smWl.ts || '').slice(0, 16).replace('T', ' ') + ').', go: '#wemPanel' }
    : null;
  // Web Push: subscriptions exist at all, then every store can be reached; the worse of the two shows.
  const smPushSubs = checkNode(smCheck('Push subscriptions registered'), 'devices registered');
  const smPushStores = checkNode(smCheck('Store notifications reachable'), 'stores reachable', 'some stores unreachable');
  const pushNode = (smPushSubs && smPushStores)
    ? (worseRag(smPushSubs.rag, smPushStores.rag) === smPushSubs.rag ? smPushSubs : smPushStores)
    : (smPushSubs || smPushStores);
  // Anthropic: the bug-triage runs are the console's only read of Claude. No runs = no signal.
  const anthropicNode = (triageErr || !ts.total) ? null
    : { rag: ts.rag, sub: ts.total + ' run' + (ts.total === 1 ? '' : 's') + ' · last ' + ago(ts.lastRun),
        tip: 'Bug-triage runs on Claude: ' + ts.total + ' runs, ' + ts.errors + ' errored, the last ' + ago(ts.lastRun) + '.', go: '#card-bug-triage' };
  const githubNode = qaOk
    ? { rag: qaRag, sub: qaPass == null ? 'CI readable' : qaPass + '% pass · ' + (qaLastConcl || 'last run'),
        tip: 'GitHub Actions: ' + (qaPass == null ? 'pass rate unknown' : qaPass + '% pass') + ', the latest run ' + (qaLastConcl || 'unknown') + '.', go: '#card-qa' }
    : qaScope ? { rag: 'a', sub: 'access needed', tip: 'GitHub refused the CI read: add Actions:read to GH_PAT.', go: '#card-qa' }
    : null;
  const mapIn = {
    staff: checkNode(smCheck('Recent clock activity'), 'clocking in'),
    managers: checkNode(smCheck('Area manager coverage'), 'every area covered', 'area without a manager'),
    nightly: nightlyNode, hourly: hourlyNode,
    anthropic: anthropicNode, resend: resendNode, push: pushNode, github: githubNode,
  };
  // Self nodes land on whichever end this console is; the other end's site and database are never read.
  mapIn[onStaging ? 'workerStaging' : 'worker'] = selfNodes.worker;
  mapIn[onStaging ? 'dbStaging' : 'db'] = selfNodes.db;
  mapIn[onStaging ? 'siteStaging' : 'site'] = selfNodes.site;
  mapIn[onStaging ? 'worker' : 'workerStaging'] = otherWorker;
  const systemMapHtml = buildSystemMap(mapIn);
  const mapTally = systemMapTally(mapIn);

  // ── Heads-up nudges — what's trending the wrong way / newly broke ──
  const nudges = computeNudges({
    feedbackOpenSeries, triageErrorsSeries, rotaGapsSeries, feedStaleSeries,
    live: { smokeFail },
  });
  // v129.59: nudges feed Jack's list (the ⚡ rows) — buildNudges stays exported for tests/reuse.
  const jackSweepHtml = buildJackSweep(jackSweep);

  // ── JACK's spoken headline (read aloud after login; replayable) ──
  const jackText = jackHeadline({
    name: opts.name, score: opsScore, overallLabel,
    triagesSince, feedbackSince, totalOpen, nextStep: nextStepText,
    integrityFail: smokeFail, unreachable,
    nudge: nudges.length ? nudges[0].text : null,
  });

  // ── Dev Agent (the SENSE→ACT bridge) ──
  // The newest still-open feedback report → the junior dev's "Fix this"
  // target (reuses feedbackRows already fetched for the welcome). Clicking
  // Fix this hands it to the existing bug-triage agent → drafts a fix → PR.
  const _openBugs = feedbackRows
    .filter(r => ((r.status || 'new') === 'new'))
    .sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0));
  const topBug = _openBugs.length
    ? { id: _openBugs[0].id, title: String(_openBugs[0].doing || '(no description)').slice(0, 80) }
    : null;
  // EVERY raised issue gets a ⚒ Fix button (not just the newest). Each hands
  // that specific report to the junior dev → drafts a fix → PR for review.
  const openBugs = _openBugs.slice(0, 6).map(r => ({
    id: r.id,
    title: String(r.doing || r.screen || '(no description)').slice(0, 70),
    screen: r.screen || '',
  }));

  // Compact live state for the JACK chat — so when Matt asks "what's wrong?"
  // Jack answers from the real numbers, not guesses. Refreshed every render.
  _jackState = {
    overall: overallLabel, opsScore, nextStep: nextStepText,
    unreachable: unreachable ? unreachable.msg : null,
    agents: {
      bugTriage: ts.rag, feedbackInsights: insights ? 'g' : 'a',
      compliance: compRag, attendance: attRag, qa: qaRag, security: secRag,
      rotaCheck: rotaRag, dataFreshness: dfRag, dataIntegrity: smokeRag,
    },
    triage: { total: ts.total, errors: ts.errors, fixes: ts.fixes, clarify: ts.clarify },
    feedback: { open: totalOpen, topTheme },
    compliance7d: { coreMisses: compCore, worstArea: compWorstArea, lateClockIns: attLate, breakIssues: attBreak },
    rotaThisWeek: rotaOk ? { coverageGaps: rotaGaps, doubleBooked: rotaDoubles } : null,
    dataFeeds: dfOk ? { needAttention: dfStale, oldest: dfFirstStale ? dfFirstStale.name : null } : null,
    integrity: smokeOk ? { pass: smokeSum.pass, fail: smokeFail, firstBad: smokeFirstBad ? smokeFirstBad.name : null } : null,
    topOpenReports: openBugs.map(b => ({ id: b.id, title: b.title, screen: b.screen })),
  };
  if (typeof document !== 'undefined') _renderJackChat();
  const devAgentHtml = buildDevAgent({
    topBug, openBugs,
    qaFailing: !!(qaOk && ciHealth.last && ciHealth.last.conclusion === 'failure'),
    qaSummary: (ciFail && ciFail.summary) ? ciFail.summary : null,
    ciUrl: (qaOk && ciHealth.last) ? ciHealth.last.url : null,
    secFailing: !!(secOk && secLast && secLast.conclusion === 'failure'),
    secUrl: secLast ? secLast.url : null,
    triageErrRate: !!(ts.total && ts.errors / ts.total > 0.15),
    triageErrors: ts.errors, triageTotal: ts.total,
    compCore, compDays, compWorstArea, attLate, attBreak,
    rotaGaps, rotaDoubles,
    dfStale, dfErr, dfFirstStale: dfFirstStale ? dfFirstStale.name : null,
    smokeFail, smokeFirstBad: smokeFirstBad ? smokeFirstBad.name : null,
    totalOpen, topTheme,
    nextStep: nextStepText,
  });

  // ── Roadmap agent (what's next) ──
  const roadmapHtml = buildRoadmap(roadmap);
  const jackDraftsHtml = buildJackDrafts(jackPRs);

  // ── live agent cards, split into two plain-English groups ──
  // "Keep the app stable" = the software/engineering health agents.
  // "Keep the data & operations correct" = the live workforce-data agents.
  const stability = [];
  const data = [];
  stability.push(card({
    rag: ts.rag, pulse: true, name: 'Bug-Triage Agent', chip: 'LIVE',
    domId: 'card-bug-triage',
    type: 'Product agent · first-pass triage',
    barLabel: 'HEALTH', barPct: ts.health, barTone: 'green',
    meta: [
      ['Last run', ago(ts.lastRun)],
      ['Runs', String(ts.total)],
      ['Errors', String(ts.errors)],
    ],
    trend: trendRow('Triage runs', triageTotalSeries, false),
    done: [
      'Reads ESL reports, routes by screen',
      'Plain-English clarify emails',
      'Anti-ghost + duplicate detection',
      'Scorecard + live progress',
    ],
    todo: [
      'Behavioural eval set (measure accuracy)',
      'Consolidate the two engines',
      'Confidence trend on the scorecard',
    ],
    // v120.5 — when failed runs are cluttering the scorecard, give Matt a way to
    // clear them (they're dead diagnostics with nothing to act on). Only shown
    // when there are errored runs to clear.
    actionsHtml: ts.errors > 0
      ? '<div class="a-cardact"><button class="a-cardbtn" ' +
          'title="Remove failed bug-triage runs from the scorecard" ' +
          'onclick="if(window.Agents&&Agents.clearTriageErrors)Agents.clearTriageErrors(this)">' +
          'Clear ' + ts.errors + ' errored run' + (ts.errors === 1 ? '' : 's') +
        '</button></div>'
      : '',
  }));
  data.push(card({
    rag: insights ? 'g' : 'a', pulse: true, name: 'Feedback Insights', chip: 'LIVE', domId: 'card-feedback-insights',
    type: 'Product agent · theme clustering',
    barLabel: 'COVERAGE', barPct: insights ? 100 : 0, barTone: 'green',
    meta: [
      ['Top theme', topTheme || '—'],
      ['Open', totalOpen == null ? '—' : String(totalOpen)],
      ['Screens', insights && insights.byScreen ? String(insights.byScreen.length) : '—'],
    ],
    // v136.69: the weekly plain-English summary (Haiku, Mondays; src/worker/feedback-summary.js), when there is one
    note: insights && insights.weekly && insights.weekly.text ? 'This week (' + insights.weekly.week + '): ' + insights.weekly.text : '',
    trend: trendRow('Open backlog', feedbackOpenSeries, true),
    done: ['Groups all feedback by screen', 'Ranks by open count', 'Plain-English weekly summary (Haiku, Mondays)'],
    todo: ['Thumbs up/down verdict capture'],
  }));
  // Compliance — ONE card (merged from the old Compliance Watcher + Attendance
  // Watcher + Compliance Trends). Top: this-week snapshot (paperwork + clock/
  // break). Below: the week-on-week trend by area. The underlying signals
  // (compRag / attRag / the trend) still feed JACK + the advisor unchanged —
  // this only consolidates three cards into one. RAG = the worst of: this
  // week's paperwork, this week's clock/break, and the trend direction.
  const _ctDir = (complianceTrend && complianceTrend.trend) || null;
  const _ctRag = _ctDir ? (_ctDir.improving ? 'g' : _ctDir.worsening ? 'r' : 'a') : 'x';
  const _ctDirWord = !_ctDir ? 'n/a' : _ctDir.improving ? 'improving' : _ctDir.worsening ? 'worsening' : 'flat';
  const _ragRank = { r: 3, a: 2, g: 1, x: 0 };
  const complianceRag = [compRag, attRag, _ctRag].reduce((w, r) => ((_ragRank[r] || 0) > (_ragRank[w] || 0) ? r : w), 'g');
  data.push(card({
    rag: complianceRag, pulse: true, name: 'Compliance', chip: 'LIVE',
    domId: 'card-compliance',
    type: 'Product agent · paperwork + clocks this week, and the week-on-week trend by area',
    meta: [
      ['Checklist/log misses', String((compCat.checklist_compliance || 0) + (compCat.log_compliance || 0))],
      ['Late clock-ins', String(attLate)],
      ['Break issues', String(attBreak)],
      ['Worst area', compWorstArea],
      ['Trend', _ctDirWord],
    ],
    trend: complianceTrendInner(complianceTrend),
    done: [
      'This-week scan: checklist/log misses, late clock-ins, break + location issues, never-signed-in',
      'Buckets every miss by store & area manager',
      'Week-on-week trend by area over the last 8 completed weeks, with the direction called',
    ],
    todo: [
      'Daily auto-run + alert on the worst stores',
      'Drill from a trend line into the stores within an area',
    ],
    // v129.142 — the action. Only when CORE paperwork is actually outstanding
    // (checklists/logs); amber noise like late clock-ins is deliberately not
    // nudge-worthy. The count in the label is the number of STORES, not issues,
    // because that is how many messages the button will send.
    actionsHtml: (function () {
      const rows = storesToNudge(compliance && compliance.issues);
      if (!rows.length) return '';
      return '<div class="a-cardact"><button class="a-cardbtn" ' +
        'title="Send each of these stores one message in their normal chat" ' +
        'onclick="if(window.Agents&&Agents.nudgeComplianceStores)Agents.nudgeComplianceStores(this)">' +
        'Nudge ' + rows.length + ' store' + (rows.length === 1 ? '' : 's') +
      '</button></div>';
    })(),
  }));
  data.push(card({
    rag: rotaRag, pulse: true, name: 'Rota Check', chip: 'LIVE',
    domId: 'card-rota-check',
    type: 'Product agent · read-only rota pre-flight (this week)',
    meta: rotaMeta,
    trend: trendRow('Coverage gaps', rotaGapsSeries, true),
    done: [
      'Flags stores open (per active-season hours) with no one scheduled',
      'Catches the same person booked at two stores on one day',
      'Surfaces negative holiday/sick balances + orphan shifts',
    ],
    todo: [
      'Propose fixes for a manager to approve (write step — gated)',
      'Contracted-hours under/over once shift durations are stored',
    ],
  }));
  data.push(card({
    rag: dfRag, pulse: true, name: 'Data Freshness', chip: 'LIVE',
    domId: 'card-data-freshness',
    type: 'Product agent · watches the periodic data feeds are current',
    meta: dfMeta,
    trend: trendRow('Feeds stale', feedStaleSeries, true),
    done: [
      'Checks Top-50 sales, targets, next week\'s rota, holidays, balances',
      'Flags a feed that\'s stale or missing before it bites',
      'Read-only — never uploads or edits anything',
    ],
    todo: [
      'Per-feed cadence config + cover KPI / delivery feeds',
      'Daily auto-run + a nudge when a feed goes stale',
    ],
  }));
  data.push(card({
    rag: cwRag, pulse: true, name: 'Comms Watcher', chip: 'LIVE',
    domId: 'card-comms-watch',
    type: 'Product agent · watches comms adoption for anomalies',
    meta: cwMeta,
    // B3 — the adoption mini-trend: one sparkline per feature (company % from
    // the nightly snapshot; fills in as snapshots accrue) + the anomaly count.
    trend: trendRow('News read %', commsNewsSeries, false) +
           trendRow('Tasks done %', commsTasksSeries, false) +
           trendRow('Surveys %', commsSurveysSeries, false) +
           trendRow('Signals', commsSignalsSeries, true),
    done: [
      'Flags a store that STOPPED reading news (was confirming, now silent)',
      'Flags imminent tasks / surveys with low or zero completion',
      'Flags stores writing no store journal for days',
      'Read-only — the per-store numbers live on admin 📊 Comms Report',
      'Feeds JACK (get_comms_health), the login briefing + the nightly sweep (B2)',
      'Adoption mini-trend per feature from the nightly snapshot (B3)',
    ],
    todo: [
      'Nudge when an adoption trend turns down for 3+ days',
    ],
    // v129.143 — the action. Only the STORE-level signals are actionable
    // (stopped reading / journal silent); a task or survey at risk is about the
    // task, not a store, so it is not offered here.
    actionsHtml: (function () {
      const rows = quietStores(cwSignals);
      if (!rows.length) return '';
      return '<div class="a-cardact"><button class="a-cardbtn" ' +
        'title="Send each quiet store one check-in message in their normal chat" ' +
        'onclick="if(window.Agents&&Agents.checkInQuietStores)Agents.checkInQuietStores(this)">' +
        'Check in with ' + rows.length + ' store' + (rows.length === 1 ? '' : 's') +
      '</button></div>';
    })(),
  }));
  data.push(card({
    rag: gmRag2, pulse: true, name: 'Cost Watcher', chip: 'LIVE',
    domId: 'card-gh-minutes',
    type: 'Infra agent · GH Actions + Cloudflare + Netlify usage (nightly cron + on launch)',
    meta: gmMeta,
    done: [
      'Reads the Actions billing API with the existing GH_PAT — no new tokens',
      'Warns at ≥80% of included minutes; RED the moment paid minutes appear',
      'Phase 2 (v128.7): Cloudflare Workers requests/day (100k free-plan cap) + Netlify caps — warn 80% / red 95%',
      'Nightly cron alert on warn/over: error_log + the watchdog email channel',
      'Honest degrade — a missing token/scope says exactly what to add, never fake green',
    ],
    todo: [
      'Goes fully live when CF_API_TOKEN + CF_ACCOUNT_ID and NETLIFY_TOKEN land as Worker secrets',
    ],
  }));
  data.push(card({
    rag: smokeRag, pulse: true, name: 'Data Integrity', chip: 'LIVE',
    domId: 'card-smoke',
    type: 'Product agent · DB invariant checks (auto-run on launch)',
    meta: smokeMeta,
    done: [
      'Runs on launch — no button needed; reports pass / fail / errored',
      'Checks staff.store matches a real store (the load-bearing FK)',
      'Clock-out-without-clock-in, negative durations, dupes, bad balances, negative targets',
    ],
    todo: [
      'Cover more surfaces as invariants (Top-50, opening hours)',
      'Trend the pass rate + alert on a newly-broken check',
    ],
  }));
  stability.push(card({
    rag: qaRag, pulse: true, name: 'QA Agent', chip: 'LIVE',
    domId: 'card-qa',
    type: 'Build agent · watches CI test runs',
    meta: qaMeta,
    note: (ciFail && ciFail.summary) ? '⚠ What broke: ' + ciFail.summary : null,
    done: [
      'Reads your GitHub Actions run history',
      "Reports pass rate + the latest run's result",
      'On a red run, says what broke in one plain sentence',
    ],
    todo: [
      'Trend the pass rate over time',
    ],
  }));
  stability.push(card({
    rag: secRag, pulse: true, name: 'Security Agent', chip: 'LIVE',
    domId: 'card-security',
    type: 'Build agent · scans every PR diff for risk',
    meta: secMeta,
    done: [
      'Scans each PR\'s added lines for committed secrets',
      'Flags eval / new Function and key/token shapes',
      'Fails the check when a real secret is caught',
    ],
    todo: [
      'AI review pass for logic-level risks',
      'Trend findings + per-rule tuning',
    ],
    // v129.141 — the action. Shown ONLY when the latest scan actually failed:
    // a button offering to open an issue about a green scan is noise, and the
    // Worker would refuse it anyway ("nothing to open"). This is the first of
    // the ten agents to close its own loop.
    actionsHtml: (secOk && secLast && secLast.conclusion === 'failure')
      ? '<div class="a-cardact"><button class="a-cardbtn" ' +
          'title="Open a GitHub issue for this failed security scan" ' +
          'onclick="if(window.Agents&&Agents.openSecurityIssue)Agents.openSecurityIssue(this)">' +
          'Open an issue' +
        '</button></div>'
      : '',
  }));
  data.push(card({
    rag: 'a', name: 'AI Rota Generator', chip: 'FEATURE', domId: 'card-rota-gen',
    type: 'AI feature · one-shot (not yet an agent)',
    barLabel: 'MATURITY → AGENT', barPct: 40, barTone: 'amber',
    meta: [['Type', 'feature'], ['Model', 'Haiku'], ['Trigger', 'manual']],
    done: ['One-shot draft rota from constraints'],
    todo: ['Graduate to a scheduled Rota Assistant', 'Check TOIL / coverage / contracted hours', 'Propose-for-approval flow'],
  }));

  // ── planned agents ──
  // Every agent we planned is now live — the list is empty. Keep the array so
  // the count stays honest and a future planned agent slots straight back in.
  const plannedAgents = [];




  bootTick('Composing your brief…');
  // Weekly Sales Email controls (beta): live status + preview + on/off + send-now.
  // Dark JARVIS-console theme (matches the other cards): panel bg + cyan-line borders.
  const wemBtn = 'padding:8px 12px;border:1px solid var(--line);background:#0a1326;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;color:var(--txt)';
  const wemDanger = 'padding:8px 12px;border:1px solid var(--red);background:rgba(255,92,122,.14);color:var(--red);border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit';
  const weeklyEmailHtml =
    '<div class="a-group" style="margin-top:18px"><div class="a-group-h"><span class="a-group-i">📧</span> Weekly Sales Email' +
      '<span class="a-group-s">Wednesday recap to the UK team (beta)</span></div>' +
      '<div class="a-card" style="padding:14px">' +
        '<div id="wemStatus" style="font-size:13px;color:var(--sub);margin-bottom:12px">Checking status…</div>' +
        '<div style="display:flex;flex-wrap:wrap;gap:8px">' +
          '<button onclick="Agents.weeklyEmailPreview(this)" style="' + wemBtn + '">✉ Preview to me</button>' +
          '<button onclick="Agents.weeklyEmailToggle(true,this)" style="' + wemBtn + '">▶ Wednesday ON</button>' +
          '<button onclick="Agents.weeklyEmailToggle(false,this)" style="' + wemBtn + '">⏸ Wednesday OFF</button>' +
          '<button onclick="Agents.weeklyEmailSendNow(this)" style="' + wemDanger + '">🚀 Send to UK team NOW</button>' +
        '</div>' +
        '<div style="font-size:11px;color:var(--sub);margin-top:8px">"Send now" emails the real recap immediately to the live recipient list shown above.</div>' +
      '</div></div>';
  // Jack Weekly Search Digest: what the estate asked Jack, Monday morning.
  // Same controls and the same dark-console theme as the weekly sales email —
  // deliberately, so both agents are operated the same way. The audience is
  // editable here because it lives in app_config, not in a deploy.
  const jackDigestHtml =
    '<div class="a-group" style="margin-top:18px"><div class="a-group-h"><span class="a-group-i">🔎</span> Jack Weekly Search Digest' +
      '<span class="a-group-s">Monday: what every store asked Jack, with recommended actions</span></div>' +
      '<div class="a-card" style="padding:14px">' +
        '<div id="jdgStatus" style="font-size:13px;color:var(--sub);margin-bottom:12px">Checking status…</div>' +
        '<div style="display:flex;flex-wrap:wrap;gap:8px">' +
          '<button onclick="Agents.jackDigestTest(this)" style="' + wemBtn + '">✉ Test to me</button>' +
          '<button onclick="Agents.jackDigestRecipients(this)" style="' + wemBtn + '">👥 Edit audience</button>' +
          '<button onclick="Agents.jackDigestToggle(true,this)" style="' + wemBtn + '">▶ Monday ON</button>' +
          '<button onclick="Agents.jackDigestToggle(false,this)" style="' + wemBtn + '">⏸ Monday OFF</button>' +
          '<button onclick="Agents.jackDigestSendNow(this)" style="' + wemDanger + '">🚀 Send digest NOW</button>' +
        '</div>' +
        '<div style="font-size:11px;color:var(--sub);margin-top:8px">"Send now" emails the real digest immediately to the audience shown above, whatever the Monday toggle says.</div>' +
      '</div></div>';
  // ── Phase 2: assemble the Briefing Room feed from REAL agent statuses ──
  // Every line is the same signal the agent's card shows — just summarised as
  // a one-line "check-in". _say3 picks green / amber / red wording honestly.
  const _say3 = (rag, g, a, r) => rag === 'g' ? g : rag === 'r' ? r : a;
  const _compMiss = (compCat.checklist_compliance || 0) + (compCat.log_compliance || 0);
  const briefingRoomAgents = [
    { icon: '🐞', name: 'Bug-Triage', rag: ts.rag,
      line: ts.errors > 0 ? (ts.errors + ' errored run' + (ts.errors === 1 ? '' : 's') + ' to clear')
                          : (ts.total + ' run' + (ts.total === 1 ? '' : 's') + ' · healthy') },
    { icon: '💬', name: 'Feedback Insights', rag: insights ? 'g' : 'a',
      line: totalOpen == null ? 'reachable' : (totalOpen + ' open' + (topTheme ? ' · top: ' + topTheme : '')) },
    { icon: '📋', name: 'Compliance', rag: complianceRag,
      line: _say3(complianceRag, 'clean this week',
        (_compMiss + ' paperwork miss' + (_compMiss === 1 ? '' : 'es') + (compWorstArea && compWorstArea !== '—' ? ' · worst: ' + compWorstArea : '')),
        (_compMiss + ' misses · worst: ' + (compWorstArea || 'n/a'))) },
    { icon: '📅', name: 'Rota Check', rag: rotaRag,
      line: _say3(rotaRag, 'coverage clean', 'check unavailable', 'coverage gaps this week') },
    { icon: '🔄', name: 'Data Freshness', rag: dfRag,
      line: _say3(dfRag, 'all feeds current', 'check unavailable', 'a feed looks stale') },
    { icon: '📣', name: 'Comms Watcher', rag: cwRag,
      line: _say3(cwRag, 'comms adoption healthy',
        (cwOk && cwFirst ? cwFirst.label : 'check unavailable'),
        (cwFirst ? cwFirst.label : 'critical comms signal')) },
    { icon: '🗄', name: 'Data Integrity', rag: smokeRag,
      line: _say3(smokeRag, ((smokeSum.total || 0) + ' checks passing'), 'not run this load',
        ((smokeFail || 0) + ' integrity fail' + (smokeFail === 1 ? '' : 's'))) },
    { icon: '🧪', name: 'QA', rag: qaRag,
      line: _say3(qaRag, (qaPass == null ? 'CI readable' : qaPass + '% pass'), 'CI access needed', 'latest CI run failed') },
    { icon: '🛡', name: 'Security', rag: secRag,
      line: _say3(secRag, 'scans clean', 'scans unreadable', 'last scan failed') },
    // v129.59: gmRag2 (the phase-2 CF/Netlify-aware level) — the card already
    // used it; the briefing line lagged on plain gmRag and could disagree.
    { icon: '⛽', name: 'Cost Watcher', rag: gmRag2,
      line: _say3(gmRag2,
        (gmOk && ghMinutes.pct != null ? 'CI minutes at ' + ghMinutes.pct + '%' : 'CI minutes healthy'),
        (gmOk ? 'CI minutes at ' + (ghMinutes.pct != null ? ghMinutes.pct + '%' : '?') + ' — running low' : 'billing check unavailable'),
        'CI minutes exhausted — merges at risk') },
  ];
  // Drill target per agent — the chip grid + Jack's list both use these.
  const AGENT_SEL = { 'Bug-Triage': '#card-bug-triage', 'Feedback Insights': '#card-feedback-insights',
    'Compliance': '#card-compliance', 'Rota Check': '#card-rota-check', 'Data Freshness': '#card-data-freshness',
    'Comms Watcher': '#card-comms-watch', 'Data Integrity': '#card-smoke', 'QA': '#card-qa',
    'Security': '#card-security', 'Cost Watcher': '#card-gh-minutes' };
  const briefingRoomHtml = buildBriefingRoom(briefingRoomAgents);

  // The alive loop's change detection: diff each agent's RAG against the last
  // render — JACK narrates only what MOVED ("CI just went red"), never the
  // steady state. First render seeds the map silently.
  const newRagMap = {};
  briefingRoomAgents.forEach((a) => { newRagMap[a.name] = a.rag; });
  // v136.65: the globe colours a feature by the integrity checks ABOUT it (not every failing check on every
  // feature that touches the database), and draws these same agents, with these same colours, as satellites.
  _lastMapExtras = {
    checks: smokeOk && Array.isArray(smoke.checks) ? smoke.checks.filter(Boolean).map((c) => ({ name: c.name, status: c.status, detail: c.detail || '' })) : null,
    checksOn: onStaging ? 'dbStaging' : 'db',
    agents: briefingRoomAgents.map((a) => ({ name: a.name, rag: a.rag, line: a.line })),
  };
  const ragChanges = [];
  if (_lastRagMap) {
    const RAGWORD = { g: 'green', a: 'amber', r: 'red' };
    briefingRoomAgents.forEach((a) => {
      const was = _lastRagMap[a.name];
      if (was && was !== a.rag) ragChanges.push(a.name + ' just went ' + (RAGWORD[a.rag] || a.rag) + ' — ' + a.line);
    });
  }
  _lastRagMap = newRagMap;
  if (opts.quiet && ragChanges.length) {
    _lastChangeText = ragChanges[0];
    if (typeof window !== 'undefined' && window.DevConsole && DevConsole.showToast) DevConsole.showToast(ragChanges[0]);
    if (!_jackMuted()) _speak(ragChanges[0]);
  }

  // ── v129.59 ONE-SCREEN COCKPIT ─────────────────────────────────────────────
  // Everything on one desktop screen; deep panels move to #deepPanels and open
  // in the drill overlay. Every builder above still renders — only WHERE moved.

  // Jack's list — THE one merged priority queue (replaces the four competing
  // "what should I do" surfaces: nudges, advisor next-step, dev-agent list and
  // the briefing room's red lines all fed from the same signals).
  const _rankRag = { r: 0, a: 1 };
  const listRows = briefingRoomAgents
    .filter((a) => a.rag !== 'g')
    .sort((a, b) => (_rankRag[a.rag] ?? 2) - (_rankRag[b.rag] ?? 2))
    .slice(0, 6)
    .map((a) => '<div class="ck-row"><span>' + a.icon + '</span><span class="t"><b>' + esc(a.name) + '</b> — ' + esc(a.line) + '</span>' +
      (a.name === 'Feedback Insights'
        ? '<button class="dq-go" onclick="if(window.DevConsole)DevConsole.showView(\'feedback\')">Open</button>'
        : '<button class="dq-go" onclick="Agents.openDrill(\'' + (AGENT_SEL[a.name] || '#advPanel') + '\')">Open</button>') +
    '</div>');
  nudges.slice(0, Math.max(0, 6 - listRows.length)).forEach((n) => {
    listRows.push('<div class="ck-row"><span>⚡</span><span class="t">' + esc(n.text || n) + '</span></div>');
  });
  const jackListHtml =
    '<div class="ck-panel"><div class="ck-h">🤖 Jack\'s list <span style="text-transform:none;letter-spacing:0;font-weight:500">— what needs you, worst first</span></div>' +
      buildRepairCard(_repairData) +   // v136.59
      jackSweepHtml +
      (listRows.length ? listRows.join('') :
        '<div class="ck-quiet">All quiet — every agent is reporting green. Ask me anything below, or drill into a chip.</div>') +
      '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px">' +
        '<button class="dq-go" onclick="Agents.openDrill(\'#devAgentPanel\')">Full dev to-do</button>' +
        '<button class="dq-go" onclick="Agents.openDrill(\'#jackDrafts\')">Jack\'s drafts</button>' +
        '<button class="dq-go" onclick="Agents.openDrill(\'#roadmapPanel\')">Roadmap</button>' +
        '<button class="dq-go" onclick="Agents.openDrill(\'#briefingRoomPanel\')">Briefing room</button>' +
        '<button class="dq-go" onclick="Agents.openDrill(\'#wemPanel\')">📧 Weekly email</button>' +
      '</div>' +
    '</div>';

  // Chip grid — the whole fleet at a glance; number = the load-bearing figure.
  const chipsHtml = '<div class="ck-chips">' + briefingRoomAgents.map((a) =>
    '<button class="ck-chip" onclick="' +
      (a.name === 'Feedback Insights' && totalOpen ? 'if(window.DevConsole)DevConsole.showView(\'feedback\')'
        : 'Agents.openDrill(\'' + (AGENT_SEL[a.name] || '') + '\')') + '" title="' + esc(a.line) + '">' +
      '<span class="a-dot ' + ragMod(a.rag) + '"' + ragDotAttrs(a.rag) + '></span>' + a.icon + ' ' + esc(a.name) +
    '</button>').join('') +
    '<button class="ck-chip" onclick="Agents.openDrill(\'#card-rota-gen\')" title="one-shot AI feature — not yet an agent"><span class="a-dot a"></span>📅 AI Rota Gen</button>' +
  '</div>';

  const cockpitHtml =
    '<div class="ck-cmd"><input id="ckAsk" placeholder="Ask JACK anything — &quot;why is CI red?&quot;, &quot;how were sales?&quot;… (Enter to send)" ' +
      'onkeydown="if(event.key===\'Enter\'){event.preventDefault();Agents.cmdSend();}">' +
      '<button onclick="Agents.cmdSend()">Ask</button>' +
      '<button onclick="Agents.jackListen()" title="Talk to Jack">🎤</button>' +
      '<button onclick="Agents.jackSweepNow()" title="Run an on-demand sweep">🌙</button>' +
      '<button onclick="Agents.jackToggleMute()" id="ckMute" title="Voice on/off">' + (_jackMuted() ? '🔇' : '🔊') + '</button>' +
    '</div>' +
    '<div class="a-strip">' +
      '<div class="a-stat a-overall clickable" title="See the advisor" onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'#advPanel\')"><div class="a-k">System health</div>' +
        '<div class="a-v"><span class="a-dot ' + ragMod(overall) + ' a-pulse"' + ragDotAttrs(overall) + '></span>' + overallLabel + '</div>' +
        '<div class="a-s">' + (stability.length + data.length + 1) + ' agents tracked · ' + plannedAgents.length + ' planned</div></div>' +
      '<div class="a-stat clickable" title="Open the Bug-Triage agent" onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'#card-bug-triage\')"><div class="a-k">Reports triaged</div><div class="a-v">' + ts.total + '</div><div class="a-s">all time (cached)</div></div>' +
      '<div class="a-stat clickable" title="Open the Bug-Triage agent" onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'#card-bug-triage\')"><div class="a-k">Fix vs clarify</div><div class="a-v">' + ts.fixes + ' / ' + ts.clarify + '</div><div class="a-s">proposed / need-info</div></div>' +
      '<div class="a-stat clickable" title="Open the Feedback list" onclick="if(window.DevConsole&&DevConsole.showView)DevConsole.showView(\'feedback\')"><div class="a-k">Open feedback</div><div class="a-v">' + (totalOpen == null ? '—' : totalOpen) + '</div><div class="a-s">' + (topTheme ? 'top: ' + esc(topTheme) : 'across screens') + '</div></div>' +
    '</div>';

  // Right column: the live system map + release truth + cron heartbeats +
  // the weekly-email pulse line. Release versions come from BOTH workers'
  // public /api/version — deployed truth, never a doc's claim.
  const rel = (releaseState && releaseState.release) || {};
  // v129.138 — an unreachable end shows WHY on hover, and a version we could
  // not read is never silently treated as a version that disagrees.
  const vTxt = (w) => w && w.ok
    ? esc(w.version)
    : '<span style="color:var(--red)" title="' + esc((w && (w.error || w.status)) ? String(w.error || ('HTTP ' + w.status)) : 'no response') + '">unreachable</span>';
  // comparable === false means we did not learn both versions. Saying
  // "differs — staging awaits promotion" from that is a claim with nothing
  // behind it; it read as a live release state for weeks while both ends were
  // simply unread. Unknown gets its own wording.
  const verdict = rel.comparable === false
    ? '<span style="color:var(--sub);font-weight:700">cannot compare — one end unread</span>'
    : rel.in_sync ? '<span style="color:var(--green);font-weight:700">in sync ✓</span>'
                  : '<span style="color:var(--amber);font-weight:700">differs — staging awaits promotion</span>';
  // v133.123: the prod Worker cannot fetch the staging Worker (Cloudflare refuses a Worker calling another
  // Worker on the same account by its public address), so staging read "unreachable" while it was fine. When
  // the Worker could not read it, this browser asks staging directly and fills the line in.
  const stagingUnread = !(rel.staging && rel.staging.ok);
  if (releaseState && stagingUnread) setTimeout(function () { fillStagingFromBrowser(rel.prod && rel.prod.ok ? rel.prod.version : null, 0); }, 0);
  const releaseLine = releaseState
    ? '<div style="font-size:11.5px;color:var(--sub);padding:2px 4px">🚀 Release: prod <b>' + vTxt(rel.prod) + '</b> · staging <b id="relStaging">' + vTxt(rel.staging) + '</b> <span id="relVerdict">' + verdict + '</span></div>'
    : '<div style="font-size:11.5px;color:var(--sub);padding:2px 4px">🚀 Release: unreachable</div>';
  const crons = (releaseState && releaseState.crons) || {};
  const hrs = crons.nightly ? crons.nightly.hours_since : null;
  // The 3am Dubai train stamps cron_last_complete LAST — >26h means it died.
  const nightlyLine = '⏰ 3am train: ' + (hrs == null ? '<span style="color:var(--amber)">no heartbeat</span>'
    : hrs > 26 ? '<b style="color:var(--red)">' + hrs + 'h ago — investigate</b>'
    : '<b style="color:var(--green)">' + hrs + 'h ago ✓</b>');
  const wl = crons.weekly_email;
  const weeklyLine = '📮 Weekly cron: ' + (wl && wl.ts
    ? (wl.outcome === 'sent' ? '<b style="color:var(--green)">sent ✓</b>' : wl.outcome === 'failed' ? '<b style="color:var(--red)">FAILED</b>' : 'skipped') +
      ' ' + esc(String(wl.ts).slice(5, 16).replace('T', ' '))
    : '<span style="color:var(--amber)">no record</span>');
  const cronLine = '<div style="font-size:11.5px;color:var(--sub);padding:2px 4px">' + nightlyLine + ' &nbsp;·&nbsp; ' + weeklyLine + '</div>';
  // v134.6: the detailed map is too wide for this column, so it gets its own full-width panel
  // below; the column keeps the release truth and heartbeats, headed by the map's tally.
  const mapTallyLine =
    '<div class="ck-h">🗺 The stack <span style="text-transform:none;letter-spacing:0;font-weight:500">· live map below</span></div>' +
    '<div class="clickable" style="font-size:11.5px;color:var(--sub);padding:2px 4px 6px;cursor:pointer" title="Open the system map" ' +
      'onclick="if(window.DevConsole&&DevConsole.drillTo)DevConsole.drillTo(\'#sysMap\')" id="ckMapTally">' + mapTallyHtml(mapTally) + '</div>';
  const rightColHtml =
    '<div class="ck-panel" style="padding:10px 12px">' + mapTallyLine +
      releaseLine + cronLine +
      '<div id="wemCockpit" style="font-size:11.5px;color:var(--sub);padding:2px 4px 0">📧 Weekly email: checking…</div>' +
    '</div>';
  const mapPanelHtml = '<div class="ck-panel ck-map">' + systemMapHtml + '</div>';

  const tickerHtml =
    '<div class="ck-tick"><span class="beat"></span><span class="live">LIVE</span>' +
      '<span id="ckTickText">JACK watching · checked just now</span></div>';

  // The vault: every deep panel stays in the DOM (real nodes — ids, handlers
  // and status writers intact) but hidden; openDrill() moves one into the
  // overlay and back. Nothing is duplicated, nothing stacks on the cockpit.
  const deepHtml =
    '<div id="deepPanels">' +
      '<div id="welcomePanel">' + welcomeHtml + '</div>' +
      '<div id="briefingRoomPanel">' + briefingRoomHtml + '</div>' +
      advisorHtml +
      devAgentHtml +
      jackDraftsHtml +
      roadmapHtml +
      '<div id="wemPanel">' + weeklyEmailHtml + '</div>' +
      '<div id="jdgPanel">' + jackDigestHtml + '</div>' +
      '<div class="a-grid">' + stability.join('') + '</div>' +
      '<div class="a-grid">' + data.join('') + '</div>' +
    '</div>';

  mount.innerHTML =
    cockpitHtml +
    '<div class="ck-mid">' + jackListHtml + rightColHtml + '</div>' +
    mapPanelHtml +
    chipsHtml +
    tickerHtml +
    deepHtml;
  if (mapView() === '3d') _mount3d();   // v136.64: the globe is the default; a refresh moves the same one into the new panel
  _aliveStart();

  weeklyEmailStatus();   // populate the Weekly Sales Email card's live status line
  feedHealthStatus();    // v136.65: colour the Cubes and Amanah nodes from their own push feeds
  jackDigestStatus();    // and the Jack Weekly Search Digest card's

  // ── JACK reads the brief ──
  // The boot greeting (jackGreetBoot, fired at the top of render) flows into
  // this headline when it finishes talking. If boot has already ended (fast
  // load, or speech unavailable), read it now; otherwise the boot utterance's
  // onend handler will. Once per load, mute-respecting.
  _jackHeadline = jackText;
  // Phase 1b: race the AI-narrated login briefing against the boot greeting —
  // if it returns before the deterministic line is spoken it becomes the
  // spoken line; either way it lands in the chat. Failures fall back to the
  // deterministic headline above (never stall, never fake).
  narrateLoginBriefing();
  if (_jackBootEnded) _maybeSpeakHeadline();
}

// ── Weekly Sales Email controls (beta) ───────────────────────────────────────
// All four hit the HO-gated /api/admin/weekly-email endpoints with the console's
// admin token. Status is read-only; preview sends to Matt; toggle flips the
// Wednesday cron; send-now emails the UK team immediately (confirmed first).
function _wemHeaders() { return { 'Authorization': 'Bearer ' + (_jackCtx && _jackCtx.token), 'Content-Type': 'application/json' }; }
// v129.58 — the EFFECTIVE audience as reported by the worker (env override or
// code defaults). The status card AND the send-now confirm both render this,
// so the UI can never claim a different list than the send actually uses.
let _wemRecipients = null;
export async function weeklyEmailStatus() {
  const el = typeof document !== 'undefined' && document.getElementById('wemStatus');
  if (!el || !_jackCtx) return;
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/weekly-email', { cache: 'no-store', headers: _wemHeaders() });
    const d = await res.json();
    // v134.6: no Resend key means no email can send at all: the map's Resend node says so.
    if (d && d.resend_configured === false) {
      _patchMapNode('resend', 'r', 'not connected', 'Resend: RESEND_API_KEY is not set on this Worker, so no email can send.');
    }
    // v125.83 — the last-attempt line. The cron writes a record on EVERY daily
    // pass (skips included), so a stale/missing record means the cron itself
    // isn't running — the exact blind spot the 2026-07-15 missing email hid in.
    let lastHtml = '';
    const la = d.last_attempt;
    if (la && la.ts) {
      const when = String(la.ts).slice(0, 16).replace('T', ' ') + ' UTC';
      const col = la.outcome === 'sent' ? 'var(--green)' : la.outcome === 'failed' ? 'var(--red)' : 'var(--muted)';
      const what = la.outcome === 'sent' ? 'sent ✓' + (la.via === 'manual' ? ' (manual)' : '')
        : la.outcome === 'failed' ? 'FAILED — ' + (la.detail || 'unknown')
          : 'skipped (' + (la.detail || '—') + ')';
      lastHtml = '<br>Last cron check: <b style="color:' + col + '">' + esc(what) + '</b> &nbsp;·&nbsp; ' + esc(when);
    } else {
      lastHtml = '<br>Last cron check: <b style="color:var(--amber)">no record yet</b> — if this stays empty past 07:00 Dubai, the cron is not running';
    }
    _wemRecipients = (d.recipients && Array.isArray(d.recipients.to)) ? d.recipients : null;
    const recHtml = _wemRecipients
      ? '<br>Live audience: <b>' + _wemRecipients.to.map(esc).join(', ') + '</b>' +
        (_wemRecipients.cc && _wemRecipients.cc.length ? ' &nbsp;·&nbsp; cc ' + _wemRecipients.cc.map(esc).join(', ') : '') +
        (_wemRecipients.env_override ? ' <span style="color:var(--amber)">(set by WEEKLY_EMAIL_TO env — overrides the code defaults)</span>' : '')
      : '';
    // v129.59 — the cockpit's compact pulse line mirrors the same status.
    const ck = document.getElementById('wemCockpit');
    if (ck) {
      const laShort = la && la.ts ? (la.outcome === 'sent' ? 'sent ✓ ' : la.outcome === 'failed' ? 'FAILED ' : 'skipped ') + String(la.ts).slice(5, 16).replace('T', ' ') : 'no cron record';
      ck.innerHTML = '📧 Weekly email: ' + (d.live_enabled ? '<b style="color:var(--green)">ON</b>' : '<b style="color:var(--amber)">OFF</b>') +
        ' · last: ' + esc(laShort) +
        (_wemRecipients ? ' · to ' + _wemRecipients.to.length + (_wemRecipients.env_override ? ' <span style="color:var(--amber)">(env)</span>' : '') : '');
    }
    el.innerHTML = 'Email service: <b style="color:' + (d.resend_configured ? 'var(--green)' : 'var(--red)') + '">' + (d.resend_configured ? 'connected ✓' : 'NOT connected ✗') + '</b>' +
      ' &nbsp;·&nbsp; Wednesday send: <b style="color:' + (d.live_enabled ? 'var(--green)' : 'var(--amber)') + '">' + (d.live_enabled ? 'ON' : 'OFF') + '</b>' +
      (d.from ? ' &nbsp;·&nbsp; from ' + esc(d.from) : '') + recHtml + lastHtml;
  } catch (e) { el.textContent = 'Could not load status.'; }
}
export async function weeklyEmailPreview(btn) {
  if (btn) { btn.disabled = true; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/weekly-email/test', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: '{}' });
    const d = await res.json().catch(() => ({}));
    if (typeof alert === 'function') alert(res.ok && d.ok ? 'Preview emailed to you — check your inbox (and Spam).' : 'Could not send preview: ' + (d.error || ('status ' + res.status)));
  } catch (e) { if (typeof alert === 'function') alert('Could not send preview: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; }
}
export async function weeklyEmailToggle(on, btn) {
  if (btn) { btn.disabled = true; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/weekly-email/toggle', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: JSON.stringify({ enabled: !!on }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.ok) { weeklyEmailStatus(); if (typeof alert === 'function') alert('Wednesday send is now ' + (on ? 'ON' : 'OFF') + '.'); }
    else if (typeof alert === 'function') alert('Could not change the setting: ' + (d.error || ('status ' + res.status)));
  } catch (e) { if (typeof alert === 'function') alert('Could not change the setting: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; }
}
export async function weeklyEmailSendNow(btn) {
  const _who = _wemRecipients && _wemRecipients.to && _wemRecipients.to.length
    ? _wemRecipients.to.join(', ') + (_wemRecipients.cc && _wemRecipients.cc.length ? ' (cc ' + _wemRecipients.cc.join(', ') + ')' : '')
    : 'the live recipient list shown on the card';
  if (!(await uiConfirm('Send the REAL weekly sales email now to: ' + _who + '?\n\nThis goes out immediately and cannot be undone.', { danger: true, confirmLabel: 'Send' }))) return;
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/weekly-email/send-now', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: '{}' });
    const d = await res.json().catch(() => ({}));
    if (typeof alert === 'function') alert(res.ok && d.ok ? 'Sent to the UK team ✓ (cc you).\n\nSubject: ' + (d.subject || '') : 'Send failed: ' + (d.error || ('status ' + res.status)));
  } catch (e) { if (typeof alert === 'function') alert('Send failed: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; btn.textContent = '🚀 Send to UK team NOW'; }
}

// ── Jack Weekly Search Digest controls ───────────────────────────────────────
// Mirrors the weekly-email handlers above, against the HO-gated
// /api/admin/jack-digest endpoints. Status is read-only; test goes to you
// alone; toggle flips the Monday cron; send-now emails the real audience
// immediately (confirmed first); recipients edits the audience, which is data
// in app_config rather than a deploy.
let _jdgRecipients = null;
export async function jackDigestStatus() {
  const el = typeof document !== 'undefined' && document.getElementById('jdgStatus');
  if (!el || !_jackCtx) return;
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-digest', { cache: 'no-store', headers: _wemHeaders() });
    const d = await res.json();
    _jdgRecipients = Array.isArray(d.recipients) ? d.recipients : [];
    // Same last-attempt honesty as the weekly email: the cron writes on EVERY
    // pass, so "no record" means the cron itself is not running.
    let lastHtml = '';
    const la = d.last_attempt;
    if (la && la.ts) {
      const when = String(la.ts).slice(0, 16).replace('T', ' ') + ' UTC';
      const col = la.outcome === 'sent' ? 'var(--green)' : la.outcome === 'failed' ? 'var(--red)' : 'var(--muted)';
      const what = la.outcome === 'sent' ? 'sent ✓' + (la.via === 'manual' ? ' (manual)' : '')
        : la.outcome === 'failed' ? 'FAILED — ' + (la.detail || 'unknown')
          : la.outcome === 'test-sent' ? 'test sent ✓'
            : 'skipped (' + (la.detail || '—') + ')';
      lastHtml = '<br>Last cron check: <b style="color:' + col + '">' + esc(what) + '</b> &nbsp;·&nbsp; ' + esc(when);
    } else {
      lastHtml = '<br>Last cron check: <b style="color:var(--amber)">no record yet</b> — if this stays empty past 07:00 Dubai on a Monday, the cron is not running';
    }
    const audience = _jdgRecipients.length
      ? '<br>Audience: <b>' + _jdgRecipients.map(esc).join(', ') + '</b>'
      : '<br>Audience: <b style="color:var(--amber)">nobody yet</b> — add addresses before turning Monday on';
    el.innerHTML = 'Email service: <b style="color:' + (d.resend_configured ? 'var(--green)' : 'var(--red)') + '">' + (d.resend_configured ? 'connected ✓' : 'NOT connected ✗') + '</b>' +
      ' &nbsp;·&nbsp; Jack: <b style="color:' + (d.jack_reachable ? 'var(--green)' : 'var(--red)') + '">' + (d.jack_reachable ? 'reachable ✓' : 'NO ROUTE ✗') + '</b>' +
      ' &nbsp;·&nbsp; Monday send: <b style="color:' + (d.live_enabled ? 'var(--green)' : 'var(--amber)') + '">' + (d.live_enabled ? 'ON' : 'OFF') + '</b>' +
      audience + lastHtml;
  } catch (e) { el.textContent = 'Could not load status.'; }
}
export async function jackDigestTest(btn) {
  if (btn) { btn.disabled = true; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-digest/test', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: '{}' });
    const d = await res.json().catch(() => ({}));
    if (typeof alert === 'function') alert(res.ok && d.ok ? 'Test digest emailed to you — check your inbox (and Spam).' : 'Could not send the test: ' + (d.error || ('status ' + res.status)));
    jackDigestStatus();
  } catch (e) { if (typeof alert === 'function') alert('Could not send the test: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; }
}
export async function jackDigestToggle(on, btn) {
  if (btn) { btn.disabled = true; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-digest/toggle', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: JSON.stringify({ enabled: !!on }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.ok) { jackDigestStatus(); if (typeof alert === 'function') alert('Monday digest is now ' + (on ? 'ON' : 'OFF') + '.'); }
    else if (typeof alert === 'function') alert('Could not change the setting: ' + (d.error || ('status ' + res.status)));
  } catch (e) { if (typeof alert === 'function') alert('Could not change the setting: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; }
}
// Edit the audience. A comma/newline separated list; the worker drops anything
// invalid and reports how many, so a typo is visible instead of silently gone.
export async function jackDigestRecipients(btn) {
  if (typeof prompt !== 'function') return;
  const current = (_jdgRecipients || []).join(', ');
  const next = prompt('Who should receive the Monday Jack digest?\n\nComma-separated email addresses (max 20).', current);
  if (next === null) return;
  const list = String(next).split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
  if (btn) { btn.disabled = true; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-digest/recipients', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: JSON.stringify({ recipients: list }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.ok) {
      jackDigestStatus();
      if (typeof alert === 'function') alert('Audience saved: ' + (d.recipients || []).length + ' address(es).' + (d.dropped ? '\n\n' + d.dropped + ' entr(y/ies) were dropped as invalid or duplicate.' : ''));
    } else if (typeof alert === 'function') alert('Could not save the audience: ' + (d.error || ('status ' + res.status)));
  } catch (e) { if (typeof alert === 'function') alert('Could not save the audience: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; }
}
export async function jackDigestSendNow(btn) {
  const who = (_jdgRecipients && _jdgRecipients.length) ? _jdgRecipients.join(', ') : null;
  if (!who) { if (typeof alert === 'function') alert('No audience set yet — use "Edit audience" first.'); return; }
  if (!(await uiConfirm('Send the REAL Jack weekly digest now to: ' + who + '?\n\nThis goes out immediately and cannot be undone.', { danger: true, confirmLabel: 'Send' }))) return;
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const res = await fetch(_jackCtx.workerUrl + '/api/admin/jack-digest/send-now', { cache: 'no-store', method: 'POST', headers: _wemHeaders(), body: '{}' });
    const d = await res.json().catch(() => ({}));
    if (typeof alert === 'function') alert(res.ok && d.ok ? 'Digest sent ✓ to ' + ((d.recipients || []).join(', ')) + '\n\n' + (d.questions || 0) + ' question(s) covered.' : 'Send failed: ' + (d.error || ('status ' + res.status)));
    jackDigestStatus();
  } catch (e) { if (typeof alert === 'function') alert('Send failed: ' + ((e && e.message) || e)); }
  if (btn) { btn.disabled = false; btn.textContent = '🚀 Send digest NOW'; }
}

// ── v129.59 drill overlay ─────────────────────────────────────────────────────
// Deep panels live hidden in #deepPanels; openDrill MOVES the real node into
// the overlay (ids, inline handlers and status writers all stay live — nothing
// is cloned), and closeDrill moves it back. One panel at a time.
export function openDrill(sel) {
  if (typeof document === 'undefined') return;
  const el = sel && document.querySelector(sel);
  const overlay = document.getElementById('drillOverlay');
  const body = document.getElementById('drillBody');
  if (!el || !overlay || !body) return;
  closeDrill();
  _drilledEl = el;
  body.appendChild(el);
  const d = el.querySelector && el.querySelector('details'); if (d) d.open = true;
  overlay.style.display = 'flex';
  overlay.onclick = (e) => { if (e.target === overlay) closeDrill(); };
}
export function closeDrill() {
  if (typeof document === 'undefined') return;
  const overlay = document.getElementById('drillOverlay');
  const deep = document.getElementById('deepPanels');
  if (_drilledEl && deep) deep.appendChild(_drilledEl);
  _drilledEl = null;
  if (overlay) { overlay.style.display = 'none'; overlay.onclick = null; }
}

// Command bar → JACK: jump to the chat view with the question already sent.
export function cmdSend() {
  if (typeof document === 'undefined') return;
  const inp = document.getElementById('ckAsk');
  const text = inp ? inp.value.trim() : '';
  if (!text) return;
  if (inp) inp.value = '';
  if (window.DevConsole && DevConsole.showView) DevConsole.showView('jack');
  jackAsk(text);
}

// ── v129.59 alive loop ────────────────────────────────────────────────────────
// The cockpit breathes: a ticker counts up from the last check, and every
// 5 minutes (page visible, no drill open, nothing being typed) the console
// quietly re-fetches everything; RAG changes are narrated in render(). The
// ticker updates every 10s — cheap DOM writes, no network.
function _aliveStart() {
  _lastCheckedMs = Date.now();
  if (_tickTimer) clearInterval(_tickTimer);
  if (_aliveTimer) clearInterval(_aliveTimer);
  if (typeof document === 'undefined') return;
  _tickTimer = setInterval(() => {
    const el = document.getElementById('ckTickText');
    if (!el) return;
    const s = Math.max(0, Math.round((Date.now() - _lastCheckedMs) / 1000));
    el.textContent = 'JACK watching · checked ' + (s < 5 ? 'just now' : s < 90 ? s + 's ago' : Math.round(s / 60) + 'm ago') +
      (_lastChangeText ? ' · last change: ' + _lastChangeText : '');
  }, 10000);
  _aliveTimer = setInterval(() => {
    if (!_lastRenderOpts) return;
    if (document.hidden) return;
    if (_drilledEl) return;                                   // don't yank a panel mid-read
    const inp = document.getElementById('ckAsk');
    if (inp && (inp.value || document.activeElement === inp)) return;
    render(Object.assign({}, _lastRenderOpts, { quiet: true }));
  }, 5 * 60 * 1000);
}

export function version() { return 'agents-console-v37'; }
